import { detectDevice, Device } from 'mediasoup-client';
import EventEmitter, { EventEmitter as EventEmitter$1 } from 'events';
import { io } from 'socket.io-client';
import adapterRaw from 'webrtc-adapter';
import rtcstats from 'rtcstats';
import { v4 as v4$1 } from 'uuid';
import SDPUtils from 'sdp';
import * as sdpTransform from 'sdp-transform';
import { Address6 } from 'ip-address';
import checkIp from 'check-ip';
import validate from 'uuid-validate';

const debugOn = process.env.NODE_ENV === "development" || new URLSearchParams(window.location.search).has("debug");
class Logger {
    constructor() {
        this._isEnabled = false;
        this._isEnabled = debugOn;
    }
    isEnabled() {
        return this._isEnabled;
    }
    enable() {
        this._isEnabled = true;
    }
    disable() {
        this._isEnabled = false;
    }
    info(...params) {
        if (!this._isEnabled) {
            return;
        }
        return console.info(...params);
    }
    warn(...params) {
        if (!this._isEnabled) {
            return;
        }
        return console.warn(...params);
    }
    error(...params) {
        if (!this._isEnabled) {
            return;
        }
        return console.error(...params);
    }
    withDebugLogger(myDebugger) {
        this._debugger = myDebugger;
        return this;
    }
    debug(...params) {
        if (!this._isEnabled || !this._debugger) {
            return;
        }
        const suppliedParams = [];
        params.forEach((param) => {
            if (typeof param === "function") {
                const suppliedParam = param();
                suppliedParams.push(suppliedParam);
            }
            else {
                suppliedParams.push(param);
            }
        });
        this._debugger.print(...suppliedParams);
    }
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

function captureCandidatePairInfoMetrics(cpMetrics, currentCptats, prevCptats, timeDiff, report) {
    const bytesReceivedDiff = currentCptats.bytesReceived - ((prevCptats === null || prevCptats === void 0 ? void 0 : prevCptats.bytesReceived) || 0);
    const bytesSentDiff = currentCptats.bytesSent - ((prevCptats === null || prevCptats === void 0 ? void 0 : prevCptats.bytesSent) || 0);
    cpMetrics.bitrateIn = (8000 * bytesReceivedDiff) / timeDiff;
    cpMetrics.bitrateOut = (8000 * bytesSentDiff) / timeDiff;
    cpMetrics.state = currentCptats.state;
    cpMetrics.roundTripTime = currentCptats.currentRoundTripTime;
    cpMetrics.requestsSent = currentCptats.requestsSent;
    cpMetrics.responsesReceived = currentCptats.responsesReceived;
    cpMetrics.requestsReceived = currentCptats.requestsReceived;
    cpMetrics.responsesSent = currentCptats.responsesSent;
    cpMetrics.availableOutgoingBitrate = currentCptats.availableOutgoingBitrate;
    cpMetrics.availableIncomingBitrate = currentCptats.availableIncomingBitrate;
    const remote = report.get(currentCptats.remoteCandidateId);
    const local = report.get(currentCptats.localCandidateId);
    cpMetrics.usingTurn = false;
    if (local) {
        if (/relay/i.test(local.candidateType || '')) {
            cpMetrics.usingTurn = true;
            cpMetrics.turnProtocol = local.relayProtocol;
        }
    }
    if (remote && local) {
        cpMetrics.localEp = `${local.protocol}:${local.address || local.ip}:${local.port} ${local.candidateType} (${local.networkType})`;
        cpMetrics.remoteEp = `${remote.protocol}:${remote.address || remote.ip}:${remote.port} ${remote.candidateType}`;
    }
}
function captureSsrcInfo(ssrcMetrics, currentSsrcStats, prevSsrcStats, timeDiff, report) {
    if (ssrcMetrics.codec)
        return;
    ssrcMetrics.kind = currentSsrcStats.kind || currentSsrcStats.mediaType;
    ssrcMetrics.ssrc = currentSsrcStats.ssrc;
    ssrcMetrics.direction = currentSsrcStats.type === "inbound-rtp" ? "in" : "out";
    const codecStats = currentSsrcStats.codecId && report.get(currentSsrcStats.codecId);
    ssrcMetrics.codec =
        codecStats &&
            [
                codecStats.mimeType,
                codecStats.channels && `${codecStats.channels}ch`,
                codecStats.clockRate,
                codecStats.payloadType,
                currentSsrcStats.encoderImplementation || currentSsrcStats.decoderImplementation,
                codecStats.sdpFmtpLine,
            ]
                .filter(Boolean)
                .join(" ");
    ssrcMetrics.mid = currentSsrcStats.mid;
    ssrcMetrics.rid = currentSsrcStats.rid;
}
function captureCommonSsrcMetrics(ssrcMetrics, currentSsrcStats, prevSsrcStats, timeDiff, report) {
    const nackCountDiff = (currentSsrcStats.nackCount || 0) - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.nackCount) || 0);
    ssrcMetrics.nackCount = (ssrcMetrics.nackCount || 0) + nackCountDiff;
    ssrcMetrics.nackRate = (1000 * nackCountDiff) / timeDiff;
    if (ssrcMetrics.direction === "in") {
        const packetCountDiff = currentSsrcStats.packetsReceived - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.packetsReceived) || 0);
        ssrcMetrics.packetCount = (ssrcMetrics.packetCount || 0) + packetCountDiff;
        ssrcMetrics.packetRate = (1000 * packetCountDiff) / timeDiff;
        const packetLossCountDiff = currentSsrcStats.packetsLost - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.packetsLost) || 0);
        ssrcMetrics.packetLossCount = (ssrcMetrics.packetLossCount || 0) + packetLossCountDiff;
        ssrcMetrics.packetLossRate = (1000 * packetLossCountDiff) / timeDiff;
        ssrcMetrics.lossRatio = (1000 * (packetLossCountDiff / (packetLossCountDiff + packetCountDiff))) / timeDiff;
        const byteCountDiff = currentSsrcStats.bytesReceived - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.bytesReceived) || 0);
        ssrcMetrics.byteCount = (ssrcMetrics.byteCount || 0) + byteCountDiff;
        let headerByteCountDiff = 0;
        if (currentSsrcStats.headerBytesReceived) {
            headerByteCountDiff = currentSsrcStats.headerBytesReceived - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.headerBytesReceived) || 0);
            ssrcMetrics.headerByteCount = (ssrcMetrics.headerByteCount || 0) + headerByteCountDiff;
        }
        const totalBytesDiff = byteCountDiff + headerByteCountDiff;
        ssrcMetrics.bitrate = (8000 * totalBytesDiff) / timeDiff;
        ssrcMetrics.mediaRatio = byteCountDiff / totalBytesDiff;
        if (currentSsrcStats.kind === "audio") {
            const fecBytesDiff = (currentSsrcStats.fecBytesReceived || 0) - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.fecBytesReceived) || 0);
            ssrcMetrics.fecRatio = fecBytesDiff / byteCountDiff;
        }
        const jitterBufferDelayDiff = (currentSsrcStats.jitterBufferDelay || 0) - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.jitterBufferDelay) || 0);
        const jitterBufferEmittedDiff = (currentSsrcStats.jitterBufferEmittedCount || 0) - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.jitterBufferEmittedCount) || 0);
        ssrcMetrics.jitter = jitterBufferEmittedDiff ? jitterBufferDelayDiff / jitterBufferEmittedDiff : 0;
    }
    else {
        const packetCountDiff = currentSsrcStats.packetsSent - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.packetsSent) || 0);
        ssrcMetrics.packetCount = (ssrcMetrics.packetCount || 0) + packetCountDiff;
        ssrcMetrics.packetRate = (1000 * packetCountDiff) / timeDiff;
        const byteCountDiff = currentSsrcStats.bytesSent - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.bytesSent) || 0);
        ssrcMetrics.byteCount = (ssrcMetrics.byteCount || 0) + byteCountDiff;
        let headerByteCountDiff = 0;
        if (currentSsrcStats.headerBytesSent) {
            headerByteCountDiff = currentSsrcStats.headerBytesSent - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.headerBytesSent) || 0);
            ssrcMetrics.headerByteCount = (ssrcMetrics.headerByteCount || 0) + headerByteCountDiff;
        }
        const totalBytesDiff = byteCountDiff + headerByteCountDiff;
        ssrcMetrics.bitrate = (8000 * totalBytesDiff) / timeDiff;
        ssrcMetrics.mediaRatio = byteCountDiff / totalBytesDiff;
        const sendDelayDiff = currentSsrcStats.totalPacketSendDelay - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.totalPacketSendDelay) || 0);
        ssrcMetrics.sendDelay = sendDelayDiff / packetCountDiff;
        const retransDiff = currentSsrcStats.retransmittedPacketsSent - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.retransmittedPacketsSent) || 0);
        ssrcMetrics.retransRatio = (1000 * (retransDiff / packetCountDiff)) / timeDiff;
        if (currentSsrcStats.remoteId) {
            const remoteReport = report.get(currentSsrcStats.remoteId);
            if (remoteReport) {
                ssrcMetrics.roundTripTime = remoteReport.roundTripTime || 0;
                ssrcMetrics.jitter = remoteReport.jitter || 0;
                ssrcMetrics.fractionLost = remoteReport.fractionLost || 0;
            }
        }
    }
}
function captureAudioSsrcMetrics(ssrcMetrics, currentSsrcStats, prevSsrcStats, timeDiff, report) {
    const packetsDiscardedDiff = currentSsrcStats.packetsDiscarded - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.packetsDiscarded) || 0);
    ssrcMetrics.packetsDiscarded = (ssrcMetrics.packetsDiscarded || 0) + packetsDiscardedDiff;
    if (ssrcMetrics.direction === "in") {
        const totalAudioEnergyDiff = currentSsrcStats.totalAudioEnergy - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.totalAudioEnergy) || 0);
        const totalSamplesDurationDiff = currentSsrcStats.totalSamplesDuration - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.totalSamplesDuration) || 0);
        ssrcMetrics.audioLevel = Math.sqrt(totalAudioEnergyDiff / totalSamplesDurationDiff);
        const samples = currentSsrcStats.totalSamplesReceived || 0;
        const concealmentEvents = currentSsrcStats.concealmentEvents || 0;
        const samplesDiff = samples - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.totalSamplesReceived) || 0);
        const concealedSamples = currentSsrcStats.concealedSamples || 0;
        const concealedDiff = concealedSamples - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.concealedSamples) || 0);
        const silentConcealedDiff = (currentSsrcStats.silentConcealedSamples || 0) - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.silentConcealedSamples) || 0);
        const insDiff = (currentSsrcStats.insertedSamplesForDeceleration || 0) -
            ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.insertedSamplesForDeceleration) || 0);
        const remDiff = (currentSsrcStats.removedSamplesForAcceleration || 0) - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.removedSamplesForAcceleration) || 0);
        ssrcMetrics.audioSamples = samples;
        ssrcMetrics.audioConcealmentEvents = concealmentEvents;
        ssrcMetrics.audioConcealedSamples = concealedSamples;
        ssrcMetrics.audioConcealment = concealedDiff ? concealedDiff / samplesDiff : 0;
        ssrcMetrics.audioSilentConcealment = silentConcealedDiff ? silentConcealedDiff / samplesDiff : 0;
        ssrcMetrics.audioAcceleration = remDiff ? remDiff / samplesDiff : 0;
        ssrcMetrics.audioDeceleration = insDiff ? insDiff / samplesDiff : 0;
    }
}
function captureVideoSsrcMetrics(ssrcMetrics, currentSsrcStats, prevSsrcStats, timeDiff, report) {
    ssrcMetrics.width = currentSsrcStats.frameWidth;
    ssrcMetrics.height = currentSsrcStats.frameHeight;
    ssrcMetrics.qualityLimitationReason = currentSsrcStats.qualityLimitationReason || "";
    const pliCountDiff = currentSsrcStats.pliCount - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.pliCount) || 0);
    ssrcMetrics.pliCount = (ssrcMetrics.pliCount || 0) + pliCountDiff;
    ssrcMetrics.pliRate = (1000 * pliCountDiff) / timeDiff;
    const firCountDiff = currentSsrcStats.firCount - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.firCount) || 0);
    ssrcMetrics.firCount = (ssrcMetrics.firCount || 0) + firCountDiff;
    ssrcMetrics.firRate = (1000 * firCountDiff) / timeDiff;
    if (ssrcMetrics.direction === "in") {
        const kfCountDiff = currentSsrcStats.keyFramesDecoded - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.keyFramesDecoded) || 0);
        ssrcMetrics.kfCount = (ssrcMetrics.kfCount || 0) + kfCountDiff;
        ssrcMetrics.kfRate = (1000 * kfCountDiff) / timeDiff;
        const frameCountDiff = currentSsrcStats.framesDecoded - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.framesDecoded) || 0);
        ssrcMetrics.frameCount = (ssrcMetrics.frameCount || 0) + frameCountDiff;
        const qpsumDiff = currentSsrcStats.qpSum - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.qpSum) || 0);
        ssrcMetrics.qpf = qpsumDiff / frameCountDiff;
        ssrcMetrics.fps = (frameCountDiff * 1000) / timeDiff;
    }
    else {
        const kfCountDiff = currentSsrcStats.keyFramesEncoded - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.keyFramesEncoded) || 0);
        ssrcMetrics.kfCount = (ssrcMetrics.kfCount || 0) + kfCountDiff;
        ssrcMetrics.kfRate = (1000 * kfCountDiff) / timeDiff;
        const frameCountDiff = currentSsrcStats.framesEncoded - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.framesEncoded) || 0);
        ssrcMetrics.frameCount = (ssrcMetrics.frameCount || 0) + frameCountDiff;
        const qpsumDiff = currentSsrcStats.qpSum - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.qpSum) || 0);
        ssrcMetrics.qpf = qpsumDiff / frameCountDiff;
        ssrcMetrics.fps = (frameCountDiff * 1000) / timeDiff;
        const encodeTimeDiff = currentSsrcStats.totalEncodeTime - ((prevSsrcStats === null || prevSsrcStats === void 0 ? void 0 : prevSsrcStats.totalEncodeTime) || 0);
        ssrcMetrics.encodeTime = encodeTimeDiff / frameCountDiff;
        if (currentSsrcStats.mediaSourceId) {
            const mediaSourceReport = report.get(currentSsrcStats.mediaSourceId);
            if (mediaSourceReport) {
                ssrcMetrics.sourceWidth = mediaSourceReport.width || 0;
                ssrcMetrics.sourceHeight = mediaSourceReport.height || 0;
                ssrcMetrics.sourceFps = mediaSourceReport.framesPerSecond || 0;
            }
        }
    }
}

let peerConnections = [];
let peerConnectionCounter = 0;
const peerConnectionData = new WeakMap();
const removePeerConnection = (pc) => {
    peerConnections = peerConnections.filter((old) => old !== pc);
};
if (window.RTCPeerConnection) {
    const OriginalRTCPeerConnection = window.RTCPeerConnection;
    function PatchedRTCPeerConnection(rtcConfig) {
        const pc = new OriginalRTCPeerConnection(rtcConfig);
        peerConnections.push(pc);
        peerConnectionData.set(pc, { index: peerConnectionCounter++ });
        const onConnectionStateChange = () => {
            if (pc.connectionState === "closed") {
                removePeerConnection(pc);
                pc.removeEventListener("connectionstatechange", onConnectionStateChange);
            }
        };
        pc.addEventListener("connectionstatechange", onConnectionStateChange);
        return pc;
    }
    PatchedRTCPeerConnection.prototype = OriginalRTCPeerConnection.prototype;
    window.RTCPeerConnection = PatchedRTCPeerConnection;
}
const getCurrentPeerConnections = () => peerConnections;
const getPeerConnectionIndex = (pc) => { var _a; return (_a = peerConnectionData.get(pc)) === null || _a === void 0 ? void 0 : _a.index; };
const setPeerConnectionsForTests = (pcs) => (peerConnections = pcs);

const pcDataByPc = new WeakMap();
const getPeerConnectionsWithStatsReports = () => Promise.all(getCurrentPeerConnections().map((pc) => __awaiter(void 0, void 0, void 0, function* () {
    let pcData = pcDataByPc.get(pc);
    if (!pcData) {
        pcData = { ssrcToTrackId: {} };
        pcDataByPc.set(pc, pcData);
    }
    try {
        const report = yield pc.getStats();
        let missingSsrcs = null;
        report.forEach((stats) => {
            if (stats.type === "inbound-rtp" || stats.type === "outbound-rtp") {
                if (!stats.trackIdentifier && !pcData.ssrcToTrackId[stats.ssrc]) {
                    if (stats.mediaSourceId) {
                        const mediaSourceStats = report.get(stats.mediaSourceId);
                        if (mediaSourceStats) {
                            if (mediaSourceStats.trackIdentifier) {
                                pcData.ssrcToTrackId[stats.ssrc] = mediaSourceStats.trackIdentifier;
                            }
                        }
                    }
                    if (!pcData.ssrcToTrackId[stats.ssrc] && stats.trackId) {
                        const deprecatedTrackStats = report.get(stats.trackId);
                        if (deprecatedTrackStats) {
                            if (deprecatedTrackStats.trackIdentifier) {
                                pcData.ssrcToTrackId[stats.ssrc] = deprecatedTrackStats.trackIdentifier;
                            }
                        }
                    }
                    if (!pcData.ssrcToTrackId[stats.ssrc]) {
                        if (!missingSsrcs)
                            missingSsrcs = [];
                        missingSsrcs.push(stats.ssrc);
                    }
                }
            }
        });
        if (missingSsrcs) {
            const sendersAndReceivers = [...pc.getSenders(), ...pc.getReceivers()];
            const reports = yield Promise.all(sendersAndReceivers.map((o) => o.getStats()));
            reports.forEach((tReport, index) => {
                tReport.forEach((stats) => {
                    if (stats.type === "inbound-rtp" || stats.type === "outbound-rtp") {
                        pcData.ssrcToTrackId[stats.ssrc] = sendersAndReceivers[index].track.id;
                    }
                });
            });
            missingSsrcs.forEach((ssrc) => {
                if (!pcData.ssrcToTrackId[ssrc]) {
                    pcData.ssrcToTrackId[ssrc] = "?" + ssrc;
                }
            });
        }
        return [pc, report, pcData];
    }
    catch (ex) {
        return [pc, [], pcData];
    }
})));

const getOrCreateSsrcMetricsContainer = (statsByView, time, pcIndex, clientId, trackId, ssrc) => {
    let viewStats = statsByView[clientId];
    if (!viewStats) {
        viewStats = { tracks: {}, startTime: time, updated: time };
        statsByView[clientId] = viewStats;
    }
    viewStats.updated = time;
    let trackStats = viewStats.tracks[trackId];
    if (!trackStats) {
        trackStats = { ssrcs: {}, startTime: time, updated: time };
        viewStats.tracks[trackId] = trackStats;
    }
    trackStats.updated = time;
    let ssrcStats = trackStats.ssrcs[ssrc];
    if (!ssrcStats) {
        ssrcStats = {
            startTime: time,
            updated: time,
            pcIndex,
        };
        trackStats.ssrcs[ssrc] = ssrcStats;
    }
    ssrcStats.updated = time;
    return ssrcStats;
};
const removeNonUpdatedStats = (statsByView, time) => {
    Object.entries(statsByView).forEach(([viewId, viewStats]) => {
        if (viewStats.updated < time) {
            delete statsByView[viewId];
        }
        else {
            Object.entries(viewStats.tracks).forEach(([trackId, trackStats]) => {
                if (trackStats.updated < time) {
                    delete viewStats.tracks[trackId];
                }
                else {
                    Object.entries(trackStats.ssrcs).forEach(([ssrc, ssrcStats]) => {
                        if (ssrcStats.updated < time) {
                            delete trackStats.ssrcs[ssrc];
                        }
                    });
                }
            });
        }
    });
};
function collectStats(state, { logger, interval }, immediate) {
    return __awaiter(this, void 0, void 0, function* () {
        const collectStatsBound = collectStats.bind(null, state, { interval, logger });
        try {
            const clients = state.getClients();
            const defaultClient = clients.find((c) => c.isLocalClient && !c.isPresentation) || { id: "unknown" };
            let defaultViewStats = state.statsByView[defaultClient.id];
            if (!defaultViewStats) {
                defaultViewStats = { tracks: {}, candidatePairs: {}, pressure: null };
                state.statsByView[defaultClient.id] = defaultViewStats;
            }
            defaultViewStats.pressure = state.lastPressureObserverRecord;
            const timeSinceLastUpdate = Date.now() - state.lastUpdateTime;
            if (timeSinceLastUpdate < 400) {
                if (immediate)
                    return state.statsByView;
                state.subscriptions.forEach((subscription) => { var _a; return (_a = subscription.onUpdatedStats) === null || _a === void 0 ? void 0 : _a.call(subscription, state.statsByView, clients); });
                state.nextTimeout = setTimeout(collectStatsBound, interval);
                return;
            }
            state.lastUpdateTime = Date.now();
            (yield getPeerConnectionsWithStatsReports()).forEach(([pc, report, pcData]) => {
                const pcIndex = getPeerConnectionIndex(pc);
                if (pc.connectionState === "closed") {
                    report = new Map();
                    removePeerConnection(pc);
                }
                pcData.previousSSRCs = pcData.currentSSRCs || {};
                pcData.currentSSRCs = {};
                report.forEach((currentRtcStats) => {
                    var _a, _b, _c;
                    if (currentRtcStats.type === "candidate-pair" && /inprogress|succeeded/.test(currentRtcStats.state)) {
                        const prevRtcStats = (_a = pcData._oldReport) === null || _a === void 0 ? void 0 : _a.get(currentRtcStats.id);
                        const timeDiff = prevRtcStats ? currentRtcStats.timestamp - prevRtcStats.timestamp : interval;
                        const cpId = pcIndex + ":" + currentRtcStats.id;
                        let cpMetrics = defaultViewStats.candidatePairs[cpId];
                        if (!cpMetrics) {
                            cpMetrics = { startTime: state.lastUpdateTime, id: cpId };
                            defaultViewStats.candidatePairs[cpId] = cpMetrics;
                        }
                        captureCandidatePairInfoMetrics(cpMetrics, currentRtcStats, prevRtcStats, timeDiff, report);
                        cpMetrics.lastRtcStatsTime = state.lastUpdateTime;
                    }
                    if (currentRtcStats.type === "inbound-rtp" || currentRtcStats.type === "outbound-rtp") {
                        const kind = currentRtcStats.mediaType || currentRtcStats.kind;
                        const ssrc = currentRtcStats.ssrc;
                        let trackId = currentRtcStats.trackIdentifier || pcData.ssrcToTrackId[ssrc];
                        let prevRtcStats = (_b = pcData._oldReport) === null || _b === void 0 ? void 0 : _b.get(currentRtcStats.id);
                        if (prevRtcStats && prevRtcStats.mediaSourceId !== currentRtcStats.mediaSourceId) {
                            const mediaSourceStats = report.get(currentRtcStats.mediaSourceId);
                            if (mediaSourceStats && mediaSourceStats.trackIdentifier) {
                                trackId = mediaSourceStats.trackIdentifier;
                                pcData.ssrcToTrackId[ssrc] = trackId;
                            }
                        }
                        const client = clients.find((c) => { var _a; return ((_a = c[kind].track) === null || _a === void 0 ? void 0 : _a.id) === trackId; }) || defaultClient;
                        if (!currentRtcStats.trackIdentifier &&
                            pcData.ssrcToTrackId[ssrc] &&
                            ((_c = client[kind].track) === null || _c === void 0 ? void 0 : _c.id) &&
                            client[kind].track.id !== pcData.ssrcToTrackId[ssrc]) {
                            trackId = client[kind].track.id;
                            pcData.ssrcToTrackId[ssrc] = client[kind].track.id;
                        }
                        pcData.currentSSRCs[ssrc] = client.id;
                        if (prevRtcStats) {
                            const newTransport = report.get(currentRtcStats.transportId);
                            const oldTransport = pcData._oldReport.get(prevRtcStats.transportId);
                            if (oldTransport &&
                                (newTransport === null || newTransport === void 0 ? void 0 : newTransport.selectedCandidatePairId) !== oldTransport.selectedCandidatePairId) {
                                prevRtcStats = null;
                            }
                            else if (currentRtcStats.bytesReceived < prevRtcStats.bytesReceived ||
                                currentRtcStats.bytesSent < prevRtcStats.bytesSent) {
                                prevRtcStats = null;
                            }
                        }
                        const timeDiff = prevRtcStats ? currentRtcStats.timestamp - prevRtcStats.timestamp : interval;
                        const ssrcMetrics = getOrCreateSsrcMetricsContainer(state.statsByView, state.lastUpdateTime, pcIndex, client.id, trackId, ssrc);
                        captureSsrcInfo(ssrcMetrics, currentRtcStats, prevRtcStats, timeDiff, report);
                        captureCommonSsrcMetrics(ssrcMetrics, currentRtcStats, prevRtcStats, timeDiff, report);
                        if (kind === "video") {
                            captureVideoSsrcMetrics(ssrcMetrics, currentRtcStats, prevRtcStats, timeDiff, report);
                        }
                        if (kind === "audio") {
                            captureAudioSsrcMetrics(ssrcMetrics, currentRtcStats, prevRtcStats, timeDiff, report);
                        }
                    }
                });
                pcData._oldReport = report;
                Object.keys(pcData.previousSSRCs)
                    .filter((ssrc) => !pcData.currentSSRCs[ssrc])
                    .forEach((ssrc) => {
                    const clientId = pcData.previousSSRCs[ssrc];
                    if (clientId) {
                        const clientView = state.statsByView[clientId];
                        if (clientView) {
                            Object.values(clientView.tracks).forEach((trackStats) => {
                                if (trackStats.ssrcs[ssrc]) {
                                    delete trackStats.ssrcs[ssrc];
                                }
                            });
                        }
                    }
                });
            });
            removeNonUpdatedStats(state.statsByView, state.lastUpdateTime);
            Object.entries(defaultViewStats.candidatePairs).forEach(([cpKey, cp]) => {
                const active = cp.lastRtcStatsTime === state.lastUpdateTime;
                cp.active = active;
                if (!active) {
                    cp.state = "old/inactive";
                    if (!cp.inactiveFromTime)
                        cp.inactiveFromTime = state.lastUpdateTime;
                    else {
                        if (state.lastUpdateTime - cp.inactiveFromTime > 4000) {
                            delete defaultViewStats.candidatePairs[cpKey];
                        }
                    }
                }
            });
            if (immediate) {
                return state.statsByView;
            }
            else {
                state.subscriptions.forEach((subscription) => { var _a; return (_a = subscription.onUpdatedStats) === null || _a === void 0 ? void 0 : _a.call(subscription, state.statsByView, clients); });
            }
        }
        catch (ex) {
            logger.warn(ex);
        }
        state.nextTimeout = setTimeout(collectStatsBound, interval);
    });
}

const REGISTERED_TRIALS = {};
function registerOriginTrials(trials, registeredTrials = REGISTERED_TRIALS, document = window.document) {
    trials.forEach(({ hostnamePattern, token }) => {
        const key = `${hostnamePattern}-${token}`;
        if (registeredTrials[key]) {
            return;
        }
        if (hostnamePattern.test(document.location.hostname)) {
            const otMeta = document.createElement("meta");
            otMeta.httpEquiv = "origin-trial";
            otMeta.content = token;
            document.head.append(otMeta);
            registeredTrials[key] = true;
        }
    });
}

const CPU_OBSERVER_OPTIONS = {
    sampleRate: 1,
    originTrials: [
        {
            hostnamePattern: /hereby\.dev/,
            token: "AkSNPHJw6EK08X0QU7kORnK9NABzRLAC7dqfXOwk5JwFAQG4Ey7WxLXxsnhieCgC1QHUdevE2EFICy7uBGDANwUAAABqeyJvcmlnaW4iOiJodHRwczovL2hlcmVieS5kZXY6NDQ0MyIsImZlYXR1cmUiOiJDb21wdXRlUHJlc3N1cmVfdjIiLCJleHBpcnkiOjE3MTY5NDA3OTksImlzU3ViZG9tYWluIjp0cnVlfQ==",
        },
        {
            hostnamePattern: /whereby\.com/,
            token: "Asc2wu8KpSx648i932NICteQDFcB05yl2QUUSHD7AQo8JGP2Fp6FF91TvYVJBsKGzLMH349rysPw5q9tqPC/PAUAAABqeyJvcmlnaW4iOiJodHRwczovL3doZXJlYnkuY29tOjQ0MyIsImZlYXR1cmUiOiJDb21wdXRlUHJlc3N1cmVfdjIiLCJleHBpcnkiOjE3MTY5NDA3OTksImlzU3ViZG9tYWluIjp0cnVlfQ==",
        },
    ],
};
function startCpuObserver(cb, { sampleRate, originTrials } = CPU_OBSERVER_OPTIONS, window = globalThis.window) {
    registerOriginTrials(originTrials);
    let pressureObserver;
    if ("PressureObserver" in window) {
        pressureObserver = new window.PressureObserver(cb, { sampleRate });
        pressureObserver.observe("cpu", { sampleInterval: sampleRate * 1000 });
        return {
            stop: () => {
                pressureObserver.unobserve("cpu");
            },
        };
    }
}

const STATE = {
    currentMonitor: null,
    getClients: () => [],
    lastUpdateTime: 0,
    statsByView: {},
    subscriptions: [],
};
const OPTIONS = {
    interval: 2000,
    logger: new Logger(),
};
const getStats = () => {
    return Object.assign({}, STATE.statsByView);
};
const getUpdatedStats = () => { var _a; return (_a = STATE.currentMonitor) === null || _a === void 0 ? void 0 : _a.getUpdatedStats(); };
const setClientProvider = (provider) => (STATE.getClients = provider);
function startStatsMonitor(state, { interval, logger }) {
    const collectStatsBound = collectStats.bind(null, state, { interval, logger });
    let cpuObserver;
    try {
        cpuObserver = startCpuObserver((records) => (state.lastPressureObserverRecord = records.pop()));
    }
    catch (ex) {
        logger.warn("Failed to observe CPU pressure", ex);
    }
    setTimeout(collectStatsBound, interval);
    return {
        getUpdatedStats: () => {
            return collectStatsBound(true);
        },
        stop: () => {
            clearTimeout(state.nextTimeout);
            if (cpuObserver)
                cpuObserver.stop();
        },
    };
}
function subscribeStats(subscription, options = OPTIONS, state = STATE) {
    state.subscriptions.push(subscription);
    if (!state.currentMonitor)
        state.currentMonitor = startStatsMonitor(state, options);
    return {
        stop() {
            var _a;
            state.subscriptions = state.subscriptions.filter((s) => s !== subscription);
            if (!state.subscriptions.length) {
                (_a = state.currentMonitor) === null || _a === void 0 ? void 0 : _a.stop();
                state.currentMonitor = null;
            }
        },
    };
}

class AssertionError extends Error {
    constructor(options) {
        super(options.message);
        this.name = "AssertionError";
        this.code = "ERR_ASSERTION";
        this.actual = options.actual;
        this.expected = options.expected;
        this.operator = options.operator;
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, options.stackStartFn);
        }
    }
}
function innerOk(fn, argLen, value, message) {
    if (!value) {
        let generatedMessage = false;
        if (argLen === 0) {
            generatedMessage = true;
            message = "No value argument passed to `assert.ok()`";
        }
        else if (message instanceof Error) {
            throw message;
        }
        const err = new AssertionError({
            actual: value,
            expected: true,
            message,
            operator: "==",
            stackStartFn: fn,
        });
        err.generatedMessage = generatedMessage;
        throw err;
    }
}
function innerFail(obj) {
    if (obj.message instanceof Error)
        throw obj.message;
    throw new AssertionError(obj);
}
function ok(...args) {
    innerOk(ok, args.length, ...args);
}
const assert = {
    fail: (message) => {
        innerFail({
            actual: "fail()",
            expected: "fail() should not be called",
            message,
            operator: "fail",
            stackStartFn: assert.fail,
        });
    },
    ok,
};
assert.notEqual = function notEqual(actual, expected, message) {
    if (arguments.length < 2) {
        throw new Error("'actual' and 'expected' arguments are required");
    }
    if (actual == expected) {
        innerFail({
            actual,
            expected,
            message,
            operator: "!=",
            stackStartFn: notEqual,
        });
    }
};
assert.ok = ok;

const createWorker = (fn) => {
    return new Worker(URL.createObjectURL(new Blob(["self.onmessage = ", fn.toString()], { type: "text/javascript" })));
};
const generateByteString = (count) => {
    if (count === 0) {
        return "";
    }
    const count2 = count / 2;
    let result = "F";
    while (result.length <= count2) {
        result += result;
    }
    return result + result.substring(0, count - result.length);
};

const getHandler = () => {
    let handlerName = detectDevice() || (/applecoremedia|applewebkit|safari/i.test(navigator.userAgent) ? "Safari12" : undefined);
    if (/iphone|ipad/i.test(navigator.userAgent))
        handlerName = "Safari12";
    return handlerName;
};

const word = "[a-fA-F\\d:]";
const boundry = (options) => options && options.includeBoundaries ? `(?:(?<=\\s|^)(?=${word})|(?<=${word})(?=\\s|$))` : "";
const v4 = "(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]\\d|\\d)(?:\\.(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]\\d|\\d)){3}";
const v6segment = "[a-fA-F\\d]{1,4}";
const v6 = `
(?:
(?:${v6segment}:){7}(?:${v6segment}|:)|                                    // 1:2:3:4:5:6:7::  1:2:3:4:5:6:7:8
(?:${v6segment}:){6}(?:${v4}|:${v6segment}|:)|                             // 1:2:3:4:5:6::    1:2:3:4:5:6::8   1:2:3:4:5:6::8  1:2:3:4:5:6::1.2.3.4
(?:${v6segment}:){5}(?::${v4}|(?::${v6segment}){1,2}|:)|                   // 1:2:3:4:5::      1:2:3:4:5::7:8   1:2:3:4:5::8    1:2:3:4:5::7:1.2.3.4
(?:${v6segment}:){4}(?:(?::${v6segment}){0,1}:${v4}|(?::${v6segment}){1,3}|:)| // 1:2:3:4::        1:2:3:4::6:7:8   1:2:3:4::8      1:2:3:4::6:7:1.2.3.4
(?:${v6segment}:){3}(?:(?::${v6segment}){0,2}:${v4}|(?::${v6segment}){1,4}|:)| // 1:2:3::          1:2:3::5:6:7:8   1:2:3::8        1:2:3::5:6:7:1.2.3.4
(?:${v6segment}:){2}(?:(?::${v6segment}){0,3}:${v4}|(?::${v6segment}){1,5}|:)| // 1:2::            1:2::4:5:6:7:8   1:2::8          1:2::4:5:6:7:1.2.3.4
(?:${v6segment}:){1}(?:(?::${v6segment}){0,4}:${v4}|(?::${v6segment}){1,6}|:)| // 1::              1::3:4:5:6:7:8   1::8            1::3:4:5:6:7:1.2.3.4
(?::(?:(?::${v6segment}){0,5}:${v4}|(?::${v6segment}){1,7}|:))             // ::2:3:4:5:6:7:8  ::2:3:4:5:6:7:8  ::8             ::1.2.3.4
)(?:%[0-9a-zA-Z]{1,})?                                             // %eth0            %1
`
    .replace(/\s*\/\/.*$/gm, "")
    .replace(/\n/g, "")
    .trim();
const v46Exact = new RegExp(`(?:^${v4}$)|(?:^${v6}$)`);
const v4exact = new RegExp(`^${v4}$`);
const v6exact = new RegExp(`^${v6}$`);
const ipRegex = (options) => options && options.exact
    ? v46Exact
    : new RegExp(`(?:${boundry(options)}${v4}${boundry(options)})|(?:${boundry(options)}${v6}${boundry(options)})`, "g");
ipRegex.v4 = (options) => options && options.exact ? v4exact : new RegExp(`${boundry(options)}${v4}${boundry(options)}`, "g");
ipRegex.v6 = (options) => options && options.exact ? v6exact : new RegExp(`${boundry(options)}${v6}${boundry(options)}`, "g");

const AUDIO_SETTINGS = {
    codecOptions: {
        opusDtx: true,
        opusFec: true,
    },
    encodings: [{ dtx: true }],
};
const VIDEO_SETTINGS_HD = {
    codecOptions: {
        videoGoogleStartBitrate: 500,
    },
    encodings: [
        { scaleResolutionDownBy: 4, maxBitrate: 150000 },
        { scaleResolutionDownBy: 2, maxBitrate: 500000 },
        { scaleResolutionDownBy: 1, maxBitrate: 1000000 },
    ],
};
const VIDEO_SETTINGS_SD = {
    codecOptions: {
        videoGoogleStartBitrate: 500,
    },
    encodings: [
        { scaleResolutionDownBy: 2, maxBitrate: 150000 },
        { scaleResolutionDownBy: 1, maxBitrate: 500000 },
    ],
};
const VIDEO_SETTINGS_VP9 = {
    codecOptions: {
        videoGoogleStartBitrate: 500,
    },
    encodings: [{ scalabilityMode: "L3T2_KEY" }],
};
const SCREEN_SHARE_SETTINGS = {
    encodings: [{}],
};
const SCREEN_SHARE_SIMULCAST_SETTINGS = {
    encodings: [
        { scaleResolutionDownBy: 2, dtx: true, maxBitrate: 500000 },
        { scaleResolutionDownBy: 1, dtx: true, maxBitrate: 1500000 },
    ],
};
const SCREEN_SHARE_SETTINGS_VP9 = {
    encodings: [{ dtx: true }],
};
const getMediaSettings = (kind, isScreenShare, features) => {
    const { lowDataModeEnabled, simulcastScreenshareOn, vp9On } = features;
    if (kind === "audio") {
        return AUDIO_SETTINGS;
    }
    if (isScreenShare) {
        if (vp9On)
            return SCREEN_SHARE_SETTINGS_VP9;
        if (simulcastScreenshareOn)
            return SCREEN_SHARE_SIMULCAST_SETTINGS;
        return SCREEN_SHARE_SETTINGS;
    }
    else {
        if (vp9On)
            return VIDEO_SETTINGS_VP9;
        if (lowDataModeEnabled)
            return VIDEO_SETTINGS_SD;
        return VIDEO_SETTINGS_HD;
    }
};
const modifyMediaCapabilities = (routerRtpCapabilities, features) => {
    const { vp9On, h264On } = features;
    if (vp9On) {
        const { preferredPayloadType } = routerRtpCapabilities.codecs.find((codec) => codec.mimeType.toLowerCase() === "video/vp9");
        const { preferredPayloadType: aptPreferredPayloadType } = routerRtpCapabilities.codecs.find((codec) => codec.mimeType.toLowerCase() === "video/rtx" && codec.parameters.apt === preferredPayloadType);
        routerRtpCapabilities.codecs = routerRtpCapabilities.codecs.filter((codec) => codec.kind === "audio" ||
            codec.preferredPayloadType === preferredPayloadType ||
            codec.preferredPayloadType === aptPreferredPayloadType);
    }
    else if (h264On) {
        const { preferredPayloadType } = routerRtpCapabilities.codecs.find((codec) => codec.mimeType.toLowerCase() === "video/h264");
        const { preferredPayloadType: aptPreferredPayloadType } = routerRtpCapabilities.codecs.find((codec) => codec.mimeType.toLowerCase() === "video/rtx" && codec.parameters.apt === preferredPayloadType);
        routerRtpCapabilities.codecs = routerRtpCapabilities.codecs.filter((codec) => codec.kind === "audio" ||
            codec.preferredPayloadType === preferredPayloadType ||
            codec.preferredPayloadType === aptPreferredPayloadType);
    }
};

const lowPixelCount = 320 * 180;
const lowBitratePerPixel = 150000 / lowPixelCount;
const highPixelCount = 1280 * 720;
const highBitratePerPixel = 1000000 / highPixelCount;
const bitrateChangePerPixel = (highBitratePerPixel - lowBitratePerPixel) / (highPixelCount - lowPixelCount);
function getOptimalBitrate(width, height, frameRate) {
    let targetPixelCount = width * height;
    if (targetPixelCount < lowPixelCount)
        targetPixelCount = lowPixelCount;
    if (targetPixelCount > highPixelCount)
        targetPixelCount = highPixelCount;
    let targetBitratePerPixel = lowBitratePerPixel;
    if (targetPixelCount > highPixelCount)
        targetBitratePerPixel = highBitratePerPixel;
    else if (targetPixelCount > lowPixelCount) {
        targetBitratePerPixel += (targetPixelCount - lowPixelCount) * bitrateChangePerPixel;
    }
    let targetBitrate = width * height * targetBitratePerPixel;
    if (frameRate <= 15)
        targetBitrate = targetBitrate * 0.7;
    else if (frameRate <= 24)
        targetBitrate = targetBitrate * 0.9;
    return targetBitrate;
}

const PROTOCOL_REQUESTS = {
    BLOCK_CLIENT: "block_client",
    CLAIM_ROOM: "claim_room",
    CLEAR_CHAT_HISTORY: "clear_chat_history",
    ENABLE_AUDIO: "enable_audio",
    ENABLE_VIDEO: "enable_video",
    END_STREAM: "end_stream",
    FETCH_MEDIASERVER_CONFIG: "fetch_mediaserver_config",
    HANDLE_KNOCK: "handle_knock",
    IDENTIFY_DEVICE: "identify_device",
    INVITE_CLIENT_AS_MEMBER: "invite_client_as_member",
    JOIN_ROOM: "join_room",
    KICK_CLIENT: "kick_client",
    KNOCK_ROOM: "knock_room",
    LEAVE_ROOM: "leave_room",
    SEND_CLIENT_METADATA: "send_client_metadata",
    SET_LOCK: "set_lock",
    SHARE_MEDIA: "share_media",
    START_NEW_STREAM: "start_new_stream",
    START_SCREENSHARE: "start_screenshare",
    STOP_SCREENSHARE: "stop_screenshare",
    START_URL_EMBED: "start_url_embed",
    STOP_URL_EMBED: "stop_url_embed",
    START_RECORDING: "start_recording",
    STOP_RECORDING: "stop_recording",
};
const PROTOCOL_RESPONSES = {
    AUDIO_ENABLED: "audio_enabled",
    BACKGROUND_IMAGE_CHANGED: "background_image_changed",
    BLOCK_ADDED: "block_added",
    BLOCK_REMOVED: "block_removed",
    CHAT_HISTORY_CLEARED: "chat_history_cleared",
    CLIENT_BLOCKED: "client_blocked",
    CLIENT_INVITED_AS_MEMBER: "client_invited_as_member",
    CLIENT_KICKED: "client_kicked",
    CLIENT_LEFT: "client_left",
    CLIENT_METADATA_RECEIVED: "client_metadata_received",
    CLIENT_READY: "client_ready",
    CLIENT_ROLE_CHANGED: "client_role_changed",
    CLIENT_USER_ID_CHANGED: "client_user_id_changed",
    CONTACTS_UPDATED: "contacts_updated",
    DEVICE_IDENTIFIED: "device_identified",
    ROOM_ROLES_UPDATED: "room_roles_updated",
    KNOCK_HANDLED: "knock_handled",
    KNOCK_PAGE_BACKGROUND_CHANGED: "knock_page_background_changed",
    KNOCKER_LEFT: "knocker_left",
    MEDIASERVER_CONFIG: "mediaserver_config",
    MEDIA_SHARED: "media_shared",
    MEMBER_INVITE: "member_invite",
    NEW_CLIENT: "new_client",
    NEW_STREAM_STARTED: "new_stream_started",
    SCREENSHARE_STARTED: "screenshare_started",
    SCREENSHARE_STOPPED: "screenshare_stopped",
    OWNER_NOTIFIED: "owner_notified",
    OWNERS_CHANGED: "owners_changed",
    PLAY_CLIENT_STICKER: "play_client_sticker",
    ROOM_INTEGRATION_ENABLED: "room_integration_enabled",
    ROOM_INTEGRATION_DISABLED: "room_integration_disabled",
    ROOM_JOINED: "room_joined",
    ROOM_KNOCKED: "room_knocked",
    ROOM_LEFT: "room_left",
    ROOM_LOCKED: "room_locked",
    ROOM_PERMISSIONS_CHANGED: "room_permissions_changed",
    ROOM_LOGO_CHANGED: "room_logo_changed",
    ROOM_TYPE_CHANGED: "room_type_changed",
    ROOM_MODE_CHANGED: "room_mode_changed",
    SOCKET_USER_ID_CHANGED: "socket_user_id_changed",
    STICKERS_UNLOCKED: "stickers_unlocked",
    STREAM_ENDED: "stream_ended",
    URL_EMBED_STARTED: "url_embed_started",
    URL_EMBED_STOPPED: "url_embed_stopped",
    RECORDING_STARTED: "recording_started",
    RECORDING_STOPPED: "recording_stopped",
    USER_NOTIFIED: "user_notified",
    VIDEO_ENABLED: "video_enabled",
    CLIENT_UNABLE_TO_JOIN: "client_unable_to_join",
    LIVE_TRANSCRIPTION_STARTED: "live_transcription_started",
    LIVE_TRANSCRIPTION_STOPPED: "live_transcription_stopped",
};
const PROTOCOL_ERRORS = {
    CANNOT_INVITE_YOURSELF: "cannot_invite_yourself",
    CLIENT_MISSING_DEVICE_ID: "client_missing_device_id",
    FORBIDDEN: "forbidden",
    INTERNAL_SERVER_ERROR: "internal_server_error",
    INVALID_AVATAR: "invalid_avatar",
    INVALID_PARAMETERS: "invalid_parameters",
    INVALID_ROOM_NAME: "invalid_room_name",
    MISSING_PARAMETERS: "missing_parameters",
    MISSING_ROOM_NAME: "missing_room_name",
    NOT_AN_OWNER: "not_an_owner",
    NOT_IN_A_ROOM: "not_in_a_room",
    ROOM_ALREADY_CLAIMED: "room_already_claimed",
    ROOM_EMAIL_MISSING: "room_email_missing",
    ROOM_FULL: "room_full",
    ROOM_UNCLAIMED: "room_unclaimed",
    CLIENT_BLOCKED: "client_blocked",
    ROOM_LOCKED: "room_locked",
    TOO_LONG_TEXT: "too_long_text",
    VIDEO_STICKER_DOES_NOT_EXIST: "video_sticker_does_not_exist",
    VIDEO_STICKER_FORMAT_ERROR: "video_sticker_format_error",
    UNSUPPORTED_VIDEO_ENCODING: "unsupported_video_encoding",
};
const RELAY_MESSAGES = {
    CHAT_MESSAGE: "chat_message",
    CHAT_READ_STATE: "chat_read_state",
    CHAT_STATE: "chat_state",
    ICE_CANDIDATE: "ice_candidate",
    ICE_END_OF_CANDIDATES: "ice_endofcandidates",
    READY_TO_RECEIVE_OFFER: "ready_to_receive_offer",
    REMOTE_CLIENT_MEDIA_REQUEST: "remote_client_media_request",
    SDP_ANSWER: "sdp_answer",
    SDP_OFFER: "sdp_offer",
    VIDEO_STICKER: "video_sticker",
};
const KNOCK_MESSAGES = {
    actions: {
        ACCEPT: "accept",
        HOLD: "hold",
        REJECT: "reject",
    },
    resolutions: {
        ACCEPTED: "accepted",
        ON_HOLD: "on_hold",
        REJECTED: "rejected",
    },
};
const PROTOCOL_EVENTS = {
    PENDING_CLIENT_LEFT: "pending_client_left",
    MEDIA_QUALITY_CHANGED: "media_quality_changed",
};

const logger$a = new Logger();
class ReconnectManager extends EventEmitter {
    constructor(socket) {
        super();
        this.reconnectThresholdInMs = 0;
        this._socket = socket;
        this._clients = {};
        this._signalDisconnectTime = undefined;
        this.rtcManager = undefined;
        this.metrics = {
            roomJoinedLate: 0,
            pendingClientCanceled: 0,
            evaluationFailed: 0,
            roomJoined: 0,
        };
        socket.on("disconnect", () => {
            this._signalDisconnectTime = Date.now();
        });
        socket.on(PROTOCOL_RESPONSES.ROOM_JOINED, (payload) => this._onRoomJoined(payload));
        socket.on(PROTOCOL_RESPONSES.NEW_CLIENT, (payload) => this._onNewClient(payload));
        socket.on(PROTOCOL_RESPONSES.CLIENT_LEFT, (payload) => this._onClientLeft(payload));
        socket.on(PROTOCOL_EVENTS.PENDING_CLIENT_LEFT, (payload) => this._onPendingClientLeft(payload));
        socket.on(PROTOCOL_RESPONSES.AUDIO_ENABLED, (payload) => this._onAudioEnabled(payload));
        socket.on(PROTOCOL_RESPONSES.VIDEO_ENABLED, (payload) => this._onVideoEnabled(payload));
        socket.on(PROTOCOL_RESPONSES.SCREENSHARE_STARTED, (payload) => this._onScreenshareChanged(payload, true));
        socket.on(PROTOCOL_RESPONSES.SCREENSHARE_STOPPED, (payload) => this._onScreenshareChanged(payload, false));
    }
    _onRoomJoined(payload) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.reconnectThresholdInMs = (payload.disconnectTimeout || 0) * 0.8;
            if (!((_a = payload.room) === null || _a === void 0 ? void 0 : _a.clients)) {
                this.emit(PROTOCOL_RESPONSES.ROOM_JOINED, payload);
                return;
            }
            if (!payload.selfId) {
                this.emit(PROTOCOL_RESPONSES.ROOM_JOINED, payload);
                return;
            }
            const myDeviceId = (_b = payload.room.clients.find((c) => payload.selfId === c.id)) === null || _b === void 0 ? void 0 : _b.deviceId;
            if (!myDeviceId) {
                this.emit(PROTOCOL_RESPONSES.ROOM_JOINED, payload);
                return;
            }
            if (!this._signalDisconnectTime) {
                this._resetClientState(payload);
                payload.room.clients = payload.room.clients.filter((c) => !(c.deviceId === myDeviceId && c.isPendingToLeave));
                this.emit(PROTOCOL_RESPONSES.ROOM_JOINED, payload);
                return;
            }
            const RECONNECT_THRESHOLD = payload.disconnectTimeout * 0.8;
            const timeSinceDisconnect = Date.now() - this._signalDisconnectTime;
            if (timeSinceDisconnect > RECONNECT_THRESHOLD) {
                this._resetClientState(payload);
                this.emit(PROTOCOL_RESPONSES.ROOM_JOINED, payload);
                this.metrics.roomJoinedLate++;
                return;
            }
            payload.room.clients = payload.room.clients.filter((c) => !(c.deviceId === myDeviceId && c.isPendingToLeave));
            const allStats = yield getUpdatedStats();
            payload.room.clients.forEach((client) => {
                var _a;
                try {
                    if (client.id === payload.selfId)
                        return;
                    if (!this._clients[client.id]) {
                        this._addClientToState(client);
                        return;
                    }
                    if (!((_a = this.rtcManager) === null || _a === void 0 ? void 0 : _a.hasClient(client.id))) {
                        return;
                    }
                    if (this._hasClientStateChanged({
                        clientId: client.id,
                        webcam: client.isVideoEnabled,
                        mic: client.isAudioEnabled,
                        screenShare: client.streams.length > 1,
                    })) {
                        return;
                    }
                    if (this._wasClientSendingMedia(client.id)) {
                        if (!this._isClientMediaActive(allStats, client.id)) {
                            return;
                        }
                    }
                    client.mergeWithOldClientState = true;
                }
                catch (error) {
                    logger$a.error("Failed to evaluate if we should merge client state %o", error);
                    this.metrics.evaluationFailed++;
                }
            });
            payload.room.clients.forEach((c) => {
                if (c.isPendingToLeave) {
                    this._onPendingClientLeft({ clientId: c.id });
                }
            });
            this.metrics.roomJoined++;
            this.emit(PROTOCOL_RESPONSES.ROOM_JOINED, payload);
        });
    }
    _onClientLeft(payload) {
        var _a;
        const { clientId } = payload;
        const client = this._clients[clientId];
        if (client) {
            clearTimeout(client.timeout);
            delete this._clients[clientId];
        }
        (_a = this.rtcManager) === null || _a === void 0 ? void 0 : _a.disconnect(clientId, null, payload.eventClaim);
        this.emit(PROTOCOL_RESPONSES.CLIENT_LEFT, payload);
    }
    _onPendingClientLeft(payload) {
        const { clientId } = payload;
        const client = this._clients[clientId];
        if (!client) {
            logger$a.warn(`client ${clientId} not found`);
            return;
        }
        if (client.isPendingToLeave) {
            return;
        }
        client.isPendingToLeave = true;
        if (this._wasClientSendingMedia(clientId)) {
            client.checkActiveMediaAttempts = 0;
            this._abortIfNotActive(payload);
        }
    }
    _onNewClient(payload) {
        const { client: { id: clientId, deviceId }, } = payload;
        const client = this._clients[clientId];
        if (client && client.isPendingToLeave) {
            clearTimeout(client.timeoutHandler);
            client.isPendingToLeave = false;
            this.metrics.pendingClientCanceled++;
            return;
        }
        this._getPendingClientsByDeviceId(deviceId).forEach((client) => {
            clearTimeout(client.timeoutHandler);
            client.isPendingToLeave = undefined;
            this.emit(PROTOCOL_RESPONSES.CLIENT_LEFT, { clientId: client.clientId });
        });
        this._addClientToState(payload.client);
        this.emit(PROTOCOL_RESPONSES.NEW_CLIENT, payload);
    }
    _abortIfNotActive(payload) {
        return __awaiter(this, void 0, void 0, function* () {
            const { clientId } = payload;
            let client = this._clients[clientId];
            if (!(client === null || client === void 0 ? void 0 : client.isPendingToLeave))
                return;
            client.checkActiveMediaAttempts++;
            if (client.checkActiveMediaAttempts > 3) {
                return;
            }
            const stillActive = yield this._checkIsActive(clientId);
            if (stillActive) {
                client.timeoutHandler = setTimeout(() => this._abortIfNotActive(payload), 500);
                return;
            }
            client = this._clients[clientId];
            if (client === null || client === void 0 ? void 0 : client.isPendingToLeave) {
                clearTimeout(client.timeoutHandler);
                delete this._clients[clientId];
                this.emit(PROTOCOL_RESPONSES.CLIENT_LEFT, payload);
            }
        });
    }
    _checkIsActive(clientId) {
        return __awaiter(this, void 0, void 0, function* () {
            const allStats = yield getUpdatedStats();
            return this._isClientMediaActive(allStats, clientId);
        });
    }
    _isClientMediaActive(stats, clientId) {
        const clientStats = stats === null || stats === void 0 ? void 0 : stats[clientId];
        let isActive = false;
        if (clientStats) {
            Object.entries(clientStats.tracks).forEach(([trackId, trackStats]) => {
                if (trackId !== "probator")
                    Object.values(trackStats.ssrcs).forEach((ssrcStats) => {
                        if ((ssrcStats.bitrate || 0) > 0)
                            isActive = true;
                    });
            });
        }
        return isActive;
    }
    _onAudioEnabled(payload) {
        const { clientId, isAudioEnabled } = payload;
        this._clients[clientId] = Object.assign(Object.assign({}, (this._clients[clientId] || {})), { isAudioEnabled });
    }
    _onVideoEnabled(payload) {
        const { clientId, isVideoEnabled } = payload;
        this._clients[clientId] = Object.assign(Object.assign({}, (this._clients[clientId] || {})), { isVideoEnabled });
    }
    _onScreenshareChanged(payload, action) {
        const { clientId } = payload;
        this._clients[clientId] = Object.assign(Object.assign({}, (this._clients[clientId] || {})), { isScreenshareEnabled: action });
    }
    _hasClientStateChanged({ clientId, webcam, mic, screenShare, }) {
        const state = this._clients[clientId];
        if (!state) {
            throw new Error(`Client ${clientId} not found in ReconnectManager state`);
        }
        if (webcam !== state.isVideoEnabled) {
            return true;
        }
        if (mic !== state.isAudioEnabled) {
            return true;
        }
        if (screenShare !== state.isScreenshareEnabled) {
            return true;
        }
        return false;
    }
    _addClientToState(newClient) {
        this._clients[newClient.id] = Object.assign(Object.assign({}, (this._clients[newClient.id] || {})), { isAudioEnabled: newClient.isAudioEnabled, isVideoEnabled: newClient.isVideoEnabled, isScreenshareEnabled: newClient.streams.length > 1, deviceId: newClient.deviceId, isPendingToLeave: newClient.isPendingToLeave, clientId: newClient.id });
    }
    _wasClientSendingMedia(clientId) {
        const client = this._clients[clientId];
        if (!client) {
            throw new Error(`Client ${clientId} not found in ReconnectManager state`);
        }
        return client.isAudioEnabled || client.isVideoEnabled || client.isScreenshareEnabled;
    }
    _getPendingClientsByDeviceId(deviceId) {
        return Object.values(this._clients).filter((clientState) => {
            return clientState.deviceId === deviceId && clientState.isPendingToLeave;
        });
    }
    _resetClientState(payload) {
        this._clients = {};
        payload.room.clients.forEach((client) => {
            if (client.id === payload.selfId) {
                return;
            }
            else {
                this._addClientToState(client);
            }
        });
    }
}

var _a$7;
const adapter$6 = (_a$7 = adapterRaw.default) !== null && _a$7 !== void 0 ? _a$7 : adapterRaw;
const DEFAULT_SOCKET_PATH = "/protocol/socket.io/v4";
const NOOP_KEEPALIVE_INTERVAL = 2000;
class ServerSocket {
    constructor(hostName, optionsOverrides, glitchFree = false) {
        this._wasConnectedUsingWebsocket = false;
        this._reconnectManager = null;
        this._socket = io(hostName, Object.assign({ path: DEFAULT_SOCKET_PATH, randomizationFactor: 0.5, reconnectionDelay: 250, reconnectionDelayMax: 5000, timeout: 5000, transports: ["websocket"], withCredentials: true }, optionsOverrides));
        this._socket.io.on("reconnect", () => {
            this._socket.sendBuffer = [];
        });
        this._socket.io.on("reconnect_attempt", () => {
            if (this._wasConnectedUsingWebsocket) {
                this._socket.io.opts.transports = ["websocket"];
                if (adapter$6.browserDetails.browser !== "safari")
                    delete this._wasConnectedUsingWebsocket;
            }
            else {
                this._socket.io.opts.transports = ["websocket", "polling"];
            }
        });
        if (glitchFree)
            this._reconnectManager = new ReconnectManager(this._socket);
        this._socket.on("connect", () => {
            const transport = this.getTransport();
            if (transport === "websocket") {
                this._wasConnectedUsingWebsocket = true;
                if (!this.noopKeepaliveInterval)
                    this.noopKeepaliveInterval = setInterval(() => {
                        try {
                            if (this._socket.connected) {
                                this._socket.io.engine.sendPacket("noop");
                            }
                        }
                        catch (ex) { }
                    }, NOOP_KEEPALIVE_INTERVAL);
            }
        });
        this._socket.on("disconnect", () => {
            this.disconnectTimestamp = Date.now();
            if (this.noopKeepaliveInterval) {
                clearInterval(this.noopKeepaliveInterval);
                this.noopKeepaliveInterval = null;
            }
        });
    }
    setRtcManager(rtcManager) {
        if (this._reconnectManager) {
            this._reconnectManager.rtcManager = rtcManager;
        }
    }
    connect() {
        if (this.isConnected() || this.isConnecting()) {
            return;
        }
        this._socket.open();
    }
    disconnect() {
        this._socket.disconnect();
    }
    disconnectOnConnect() {
        this._socket.once("connect", () => {
            this._socket.disconnect();
        });
    }
    emit(eventName, ...args) {
        this._socket.emit.apply(this._socket, arguments);
    }
    emitIfConnected(eventName, data) {
        if (!this.isConnected()) {
            return;
        }
        this.emit(eventName, data);
    }
    getTransport() {
        return (this._socket &&
            this._socket.io &&
            this._socket.io.engine &&
            this._socket.io.engine.transport &&
            this._socket.io.engine.transport.name);
    }
    getManager() {
        return this._socket.io;
    }
    isConnecting() {
        return this._socket && this._socket.connecting;
    }
    isConnected() {
        return this._socket && this._socket.connected;
    }
    on(eventName, handler) {
        const relayableEvents = [
            PROTOCOL_RESPONSES.ROOM_JOINED,
            PROTOCOL_RESPONSES.CLIENT_LEFT,
            PROTOCOL_RESPONSES.NEW_CLIENT,
        ];
        if (this._reconnectManager && relayableEvents.includes(eventName)) {
            return this._interceptEvent(eventName, handler);
        }
        this._socket.on(eventName, handler);
        return () => {
            this._socket.off(eventName, handler);
        };
    }
    once(eventName, handler) {
        this._socket.once(eventName, handler);
    }
    off(eventName, handler) {
        this._socket.off(eventName, handler);
    }
    _interceptEvent(eventName, handler) {
        if (this._reconnectManager) {
            this._reconnectManager.on(eventName, handler);
        }
        return () => {
            if (this._reconnectManager)
                this._reconnectManager.removeListener(eventName, handler);
        };
    }
    getGlitchFreeMetrics() {
        var _a;
        return (_a = this._reconnectManager) === null || _a === void 0 ? void 0 : _a.metrics;
    }
    getReconnectThreshold() {
        var _a;
        return (_a = this._reconnectManager) === null || _a === void 0 ? void 0 : _a.reconnectThresholdInMs;
    }
}

const maybeTurnOnly = (transportConfig, features) => {
    if (!features.useOnlyTURN) {
        return;
    }
    transportConfig.iceTransportPolicy = "relay";
    const filter = {
        onlyudp: /^turn:.*transport=udp$/,
        onlytcp: /^turn:.*transport=tcp$/,
        onlytls: /^turns:.*transport=tcp$/,
    }[features.useOnlyTURN];
    if (filter) {
        transportConfig.iceServers = transportConfig.iceServers.filter((entry) => entry.url && entry.url.match(filter));
    }
};

const defaultSubdomainPattern = /^(?:([^.]+)[.])?((:?[^.]+[.]){1,}[^.]+)$/;
const localstackPattern = /^(?:([^.]+)-)?(ip-[^.]*[.](?:hereby[.]dev|rfc1918[.]disappear[.]at)(?::\d+|))$/;
const localhostPattern = /^(?:([^.]+)[.])?(localhost:?\d*)/;
const serverPattern = /^(?:([^.]+)[.])?(server:?\d*)/;
const ipv4Pattern = /^(?:([^.]+)[.])?((\d+[.]){3}:?\d*)$/;
const subdomainPatterns = [
    { pattern: serverPattern, separator: "." },
    { pattern: localhostPattern, separator: "." },
    { pattern: ipv4Pattern, separator: "." },
    { pattern: localstackPattern, separator: "-" },
    { pattern: defaultSubdomainPattern, separator: "." },
];
function fromLocation({ host = "whereby.com", protocol = "https:" } = {}) {
    let subdomain = "";
    let domain = host;
    let subdomainSeparator = ".";
    for (const { separator, pattern } of subdomainPatterns) {
        const match = pattern.exec(host);
        if (match) {
            subdomain = match[1] || "";
            domain = match[2];
            subdomainSeparator = separator;
            break;
        }
    }
    const organizationDomain = !subdomain ? domain : `${subdomain}${subdomainSeparator}${domain}`;
    return {
        domain,
        domainWithSeparator: `${subdomainSeparator}${domain}`,
        organizationDomain,
        organization: `${protocol}//${organizationDomain}`,
        service: `${protocol}//${domain}`,
        subdomain,
    };
}
fromLocation(window && window.location);

const logger$9 = new Logger();
const debugLogger = {
    print: (...args) => console.debug(args[0], ...args.slice(1)),
};
logger$9.withDebugLogger(debugLogger);
class PacketLossAnalyser {
    constructor() {
        this.BEGIN_PACKET_LOSS_PERIOD_THRESHOLD = 0.04;
        this.END_PACKET_LOSS_PERIOD_THRESHOLD = 0.005;
        this.INTERVAL_DIFF_THRESHOLD_MS = 4000;
        this.STALE_MEASUREMENT_TIMEOUT_MS = 10000;
        this.MINIMUM_INTERVAL_MS = 30000;
        this.ssrcsHistory = new Map();
        this.staleMeasurementTimeouts = new Map();
    }
    addPacketLossMeasurement(id, packetLoss, timestamp) {
        this.handleStaleMeasurements(id);
        const beginNewPacketLossPeriod = packetLoss > this.BEGIN_PACKET_LOSS_PERIOD_THRESHOLD;
        let history = this.ssrcsHistory.get(id);
        if (!history) {
            history = {
                id,
                hasActivePacketLoss: beginNewPacketLossPeriod,
                currPeriod: beginNewPacketLossPeriod ? { begin: timestamp } : undefined,
                hasPeriodicPacketLoss: false,
            };
            this.ssrcsHistory.set(id, history);
            return;
        }
        if (history.hasActivePacketLoss) {
            if (packetLoss < this.END_PACKET_LOSS_PERIOD_THRESHOLD) {
                this.endPacketLossPeriod(history, timestamp);
                if (history.prevIntervalInMs && history.prevIntervalInMs < this.MINIMUM_INTERVAL_MS) {
                    this.ssrcsHistory.delete(id);
                }
            }
            return;
        }
        if (beginNewPacketLossPeriod) {
            history.hasActivePacketLoss = true;
            history.currPeriod = {
                begin: timestamp,
            };
        }
    }
    hasPeriodicPacketLoss(id, timestamp) {
        const history = this.ssrcsHistory.get(id);
        if (history && this.prevIntervalExceeded(history, timestamp)) {
            this.ssrcsHistory.delete(history.id);
            return false;
        }
        return (history === null || history === void 0 ? void 0 : history.hasPeriodicPacketLoss) || false;
    }
    prevIntervalExceeded(history, timestamp) {
        if (history.prevPeriod && history.prevIntervalInMs) {
            const intervalLimitTimestamp = this.calculatePeriodCenterTimestamp(history.prevPeriod) +
                history.prevIntervalInMs +
                this.INTERVAL_DIFF_THRESHOLD_MS;
            return timestamp > intervalLimitTimestamp;
        }
        return false;
    }
    handleStaleMeasurements(id) {
        const staleMeasurementTimeout = this.staleMeasurementTimeouts.get(id);
        if (staleMeasurementTimeout) {
            clearTimeout(staleMeasurementTimeout);
        }
        this.staleMeasurementTimeouts.set(id, setTimeout(() => {
            logger$9.debug("handleStaleMeasurements() [measurements invalid for ssrc: %s]", id);
            this.ssrcsHistory.delete(id);
        }, this.STALE_MEASUREMENT_TIMEOUT_MS));
    }
    endPacketLossPeriod(history, timestamp) {
        logger$9.debug("endPacketLossPeriod() [ssrcId: %s, timestamp: %s]", history.id, timestamp);
        if (!history.currPeriod)
            throw new Error("No packet loss period for " + history.id);
        if (!history.prevPeriod) {
            history.prevPeriod = Object.assign(Object.assign({}, history.currPeriod), { end: timestamp });
        }
        else {
            history.currPeriod.end = timestamp;
            this.addNewPacketLossInterval(history);
        }
        delete history.currPeriod;
        history.hasActivePacketLoss = false;
    }
    addNewPacketLossInterval(history) {
        logger$9.debug("addNewPacketLossInterval() [ssrcId: %s, prevIntervalInMs: %s]", history.id, history.prevIntervalInMs);
        if (!history.currPeriod || !history.prevPeriod)
            throw new Error("missing period timestamps");
        const prevPeriodCenter = this.calculatePeriodCenterTimestamp(history.prevPeriod);
        const currPeriodCenter = this.calculatePeriodCenterTimestamp(history.currPeriod);
        const currIntervalInMs = currPeriodCenter - prevPeriodCenter;
        if (history.prevIntervalInMs) {
            const intervalDiffInMs = Math.abs(history.prevIntervalInMs - currIntervalInMs);
            history.hasPeriodicPacketLoss = intervalDiffInMs < this.INTERVAL_DIFF_THRESHOLD_MS;
            logger$9.debug("addNewPacketLossInterval() [hasPeriodicPacketLoss: %s, intervalDiffInMs: %s]", intervalDiffInMs < this.INTERVAL_DIFF_THRESHOLD_MS, intervalDiffInMs);
        }
        history.prevIntervalInMs = currIntervalInMs;
        history.prevPeriod = history.currPeriod;
    }
    calculatePeriodCenterTimestamp(period) {
        if (!period.end)
            throw new Error("Missing period end timestamp");
        return period.begin + (period.end - period.begin) / 2;
    }
}

const packetLossAnalyser = new PacketLossAnalyser();
const periodicPacketLossDetector = {
    id: "periodic-packet-loss",
    enabled: ({ client, hasLiveTrack, ssrc0 }) => {
        return client.isLocalClient && hasLiveTrack && ssrc0.direction === "out" && ssrc0.bitrate;
    },
    check: ({ ssrc0 }) => {
        packetLossAnalyser.addPacketLossMeasurement(ssrc0.ssrc, ssrc0.fractionLost || 0, Date.now());
        return packetLossAnalyser.hasPeriodicPacketLoss(ssrc0.ssrc, Date.now());
    },
};
const badNetworkIssueDetector = {
    id: "bad-network",
    enabled: ({ hasLiveTrack, ssrcs }) => hasLiveTrack && ssrcs.length > 0,
    check: ({ client, clients, kind, ssrcs }) => {
        const hasPositiveBitrate = ssrcs.some((ssrc) => ssrc.bitrate > 0);
        if (!hasPositiveBitrate && client.isLocalClient && kind === "video") {
            const remoteClients = clients.filter((c) => !c.isLocalClient);
            if (remoteClients.length > 0) {
                return !remoteClients.every((c) => c.isAudioOnlyModeEnabled);
            }
        }
        const ssrc0 = ssrcs[0];
        if (!ssrc0) {
            return false;
        }
        return (ssrc0.bitrate === 0 ||
            ssrc0.lossRatio > 0.03 ||
            (ssrc0.fractionLost || 0) > 0.03 ||
            (!client.isPresentation && kind === "video" && ssrc0.bitrate < 30000) ||
            (ssrc0.direction === "in" && ssrc0.pliRate > 2));
    },
};
const dryTrackIssueDetector = {
    id: "dry-track",
    enabled: ({ hasLiveTrack, ssrcs }) => !!(hasLiveTrack && ssrcs),
    check: ({ client, clients, ssrcs }) => {
        let hasPositiveBitrate = false;
        ssrcs.forEach((ssrc) => {
            hasPositiveBitrate = hasPositiveBitrate || ssrc.bitrate > 0;
        });
        if (!hasPositiveBitrate && client.isLocalClient) {
            const remoteClients = clients.filter((c) => !c.isLocalClient);
            if (remoteClients.length > 0) {
                return !remoteClients.every((c) => c.isAudioOnlyModeEnabled);
            }
        }
        return !hasPositiveBitrate;
    },
};
const noTrackIssueDetector = {
    id: "no-track",
    enabled: () => true,
    check: ({ client, clients, kind, track }) => {
        if (!track && !client.isLocalClient && kind === "video") {
            const localClient = clients.find((c) => c.isLocalClient);
            if (localClient) {
                return !localClient.isAudioOnlyModeEnabled;
            }
        }
        return !track;
    },
};
const noTrackStatsIssueDetector = {
    id: "no-track-stats",
    enabled: ({ hasLiveTrack }) => hasLiveTrack,
    check: ({ client, clients, kind, ssrcs }) => {
        if (ssrcs.length === 0 && !client.isLocalClient && kind === "video") {
            const localClient = clients.find((c) => c.isLocalClient);
            if (localClient) {
                return !localClient.isAudioOnlyModeEnabled;
            }
        }
        return ssrcs.length === 0;
    },
};
const issueDetectors = [
    {
        id: "desync",
        enabled: ({ client, track, stats }) => {
            return (!client.isLocalClient &&
                (track === null || track === void 0 ? void 0 : track.kind) === "audio" &&
                typeof (stats === null || stats === void 0 ? void 0 : stats.tracks) === "object" &&
                Object.keys(stats.tracks).length > 1);
        },
        check: ({ stats: { tracks } }) => {
            const jitter = {
                audio: 0,
                video: 0,
            };
            Object.values(tracks)
                .flatMap((t) => Object.values(t.ssrcs))
                .forEach((ssrc) => {
                if (ssrc.kind === "audio" && ssrc.jitter > jitter.audio)
                    jitter.audio = ssrc.jitter;
                if (ssrc.kind === "video" && ssrc.jitter > jitter.video)
                    jitter.video = ssrc.jitter;
            });
            const diff = Math.abs(jitter.audio * 1000 - jitter.video * 1000);
            return diff > 500;
        },
    },
    noTrackIssueDetector,
    {
        id: "ended-track",
        enabled: ({ track }) => !!track,
        check: ({ track }) => (track === null || track === void 0 ? void 0 : track.readyState) === "ended",
    },
    noTrackStatsIssueDetector,
    dryTrackIssueDetector,
    {
        id: "low-layer0-bitrate",
        enabled: ({ hasLiveTrack, ssrc0, kind, client }) => hasLiveTrack && kind === "video" && ssrc0 && ssrc0.height && !client.isPresentation,
        check: ({ ssrc0 }) => ssrc0.height < 200 && ssrc0.bitrate < 30000,
    },
    {
        id: "quality-limitation-bw",
        enabled: ({ hasLiveTrack, stats, client, kind }) => hasLiveTrack && client.isLocalClient && kind === "video" && stats,
        check: ({ stats }) => !!Object.values(stats.tracks).find((track) => Object.values(track.ssrcs).find((ssrc) => ssrc.qualityLimitationReason === "bandwidth")),
    },
    {
        id: "quality-limitation-cpu",
        enabled: ({ hasLiveTrack, stats, client, kind }) => hasLiveTrack && client.isLocalClient && kind === "video" && stats,
        check: ({ stats }) => !!Object.values(stats.tracks).find((track) => Object.values(track.ssrcs).find((ssrc) => ssrc.qualityLimitationReason === "cpu")),
    },
    {
        id: "high-plirate",
        enabled: ({ hasLiveTrack, ssrc0 }) => hasLiveTrack && ssrc0 && ssrc0.height,
        check: ({ ssrc0 }) => ssrc0.pliRate > 2,
    },
    {
        id: "extreme-plirate",
        enabled: ({ hasLiveTrack, ssrc0 }) => hasLiveTrack && ssrc0 && ssrc0.height,
        check: ({ ssrc0 }) => ssrc0.pliRate > 5,
    },
    {
        id: "high-packetloss",
        enabled: ({ hasLiveTrack, ssrc0 }) => hasLiveTrack && ssrc0 && ssrc0.direction === "in",
        check: ({ ssrc0 }) => ssrc0.lossRatio > 0.02,
    },
    {
        id: "extreme-packetloss",
        enabled: ({ hasLiveTrack, ssrc0 }) => hasLiveTrack && ssrc0 && ssrc0.direction === "in",
        check: ({ ssrc0 }) => ssrc0.lossRatio > 0.1,
    },
    {
        id: "high-packetloss",
        enabled: ({ hasLiveTrack, ssrc0 }) => hasLiveTrack && ssrc0 && ssrc0.direction === "out",
        check: ({ ssrc0 }) => (ssrc0.fractionLost || 0) > 0.02,
    },
    {
        id: "extreme-packetloss",
        enabled: ({ hasLiveTrack, ssrc0 }) => hasLiveTrack && ssrc0 && ssrc0.direction === "out",
        check: ({ ssrc0 }) => (ssrc0.fractionLost || 0) > 0.1,
    },
    {
        id: "fps-below-20",
        enabled: ({ hasLiveTrack, ssrc0, kind, client }) => hasLiveTrack && ssrc0 && ssrc0.height && kind === "video" && !client.isPresentation,
        check: ({ ssrc0 }) => ssrc0.height > 180 && ssrc0.fps < 20,
    },
    {
        id: "fps-below-10",
        enabled: ({ hasLiveTrack, ssrc0, kind, client }) => hasLiveTrack && ssrc0 && ssrc0.height && kind === "video" && !client.isPresentation,
        check: ({ ssrc0 }) => ssrc0.fps < 10,
    },
    badNetworkIssueDetector,
    periodicPacketLossDetector,
    {
        id: "cpu-pressure-serious",
        global: true,
        enabled: ({ stats }) => { var _a; return ((_a = stats === null || stats === void 0 ? void 0 : stats.pressure) === null || _a === void 0 ? void 0 : _a.source) === "cpu"; },
        check: ({ stats }) => stats.pressure.state === "serious",
    },
    {
        id: "cpu-pressure-critical",
        global: true,
        enabled: ({ stats }) => { var _a; return ((_a = stats === null || stats === void 0 ? void 0 : stats.pressure) === null || _a === void 0 ? void 0 : _a.source) === "cpu"; },
        check: ({ stats }) => stats.pressure.state === "critical",
    },
    {
        id: "concealed",
        enabled: ({ hasLiveTrack, ssrc0, kind }) => hasLiveTrack && ssrc0 && kind === "audio",
        check: ({ ssrc0 }) => ssrc0.bitrate && ssrc0.direction === "in" && ssrc0.audioLevel >= 0.001 && ssrc0.audioConcealment >= 0.1,
    },
    {
        id: "decelerated",
        enabled: ({ hasLiveTrack, ssrc0, kind }) => hasLiveTrack && ssrc0 && kind === "audio",
        check: ({ ssrc0 }) => ssrc0.bitrate && ssrc0.direction === "in" && ssrc0.audioLevel >= 0.001 && ssrc0.audioDeceleration >= 0.1,
    },
    {
        id: "accelerated",
        enabled: ({ hasLiveTrack, ssrc0, kind }) => hasLiveTrack && ssrc0 && kind === "audio",
        check: ({ ssrc0 }) => ssrc0.bitrate && ssrc0.direction === "in" && ssrc0.audioLevel >= 0.001 && ssrc0.audioAcceleration >= 0.1,
    },
];

let subscriptions = [];
let stopStats = null;
const metrics = [
    {
        id: "bitrate",
        enabled: ({ hasLiveTrack, track, ssrc0 }) => hasLiveTrack && track && ssrc0,
        value: ({ trackStats }) => Object.values(trackStats.ssrcs).reduce((sum, ssrc) => sum + ssrc.bitrate, 0),
    },
    {
        id: "pixelrate",
        enabled: ({ hasLiveTrack, track, ssrc0, kind }) => hasLiveTrack && kind === "video" && track && ssrc0 && ssrc0.height,
        value: ({ trackStats }) => Object.values(trackStats.ssrcs).reduce((sum, ssrc) => sum + (ssrc.fps || 0) * (ssrc.width || 0) * (ssrc.height || 0), 0),
    },
    {
        id: "height",
        enabled: ({ hasLiveTrack, track, ssrc0, kind }) => hasLiveTrack && kind === "video" && track && ssrc0 && ssrc0.height,
        value: ({ trackStats }) => Object.values(trackStats.ssrcs).reduce((max, ssrc) => Math.max(max, ssrc.fps > 0 ? ssrc.height : 0), 0),
    },
    {
        id: "sourceHeight",
        enabled: ({ hasLiveTrack, track, ssrc0, kind }) => hasLiveTrack && kind === "video" && track && ssrc0 && ssrc0.sourceHeight && ssrc0.direction === "out",
        value: ({ ssrc0 }) => ssrc0.sourceHeight,
    },
    {
        id: "packetloss",
        enabled: ({ hasLiveTrack, ssrc0 }) => hasLiveTrack && ssrc0 && ssrc0.bitrate,
        value: ({ ssrc0 }) => (ssrc0.direction === "in" ? ssrc0.lossRatio : ssrc0.fractionLost) || 0,
    },
    {
        id: "jitter",
        enabled: ({ hasLiveTrack, ssrc0 }) => hasLiveTrack && ssrc0 && ssrc0.bitrate && ssrc0.direction === "in",
        value: ({ ssrc0 }) => ssrc0.jitter,
    },
    {
        id: "mos",
        enabled: ({ hasLiveTrack, ssrc0 }) => hasLiveTrack && ssrc0 && ssrc0.bitrate && ssrc0.direction === "out",
        value: ({ ssrc0 }) => {
            const averageLatency = ssrc0.roundTripTime || 0;
            const jitter = ssrc0.jitter || 0;
            const packetLoss = (ssrc0.fractionLost || 0) * 100;
            const effectiveLatency = averageLatency + jitter * 2 + 10;
            let r = effectiveLatency < 160 ? 93.2 - effectiveLatency / 40 : 93.2 - (effectiveLatency - 120) / 10;
            r = r - packetLoss * 2.5;
            const mos = 1 + 0.035 * r + 0.000007 * r * (r - 60) * (100 - r);
            return mos;
        },
    },
    {
        id: "rendered",
        value: () => 1,
    },
    {
        id: "active",
        value: ({ hasLiveTrack, track, ssrc0 }) => (hasLiveTrack && track && ssrc0 ? 1 : 0),
    },
    {
        id: "cpu-pressure",
        global: true,
        enabled: ({ stats }) => { var _a; return ((_a = stats === null || stats === void 0 ? void 0 : stats.pressure) === null || _a === void 0 ? void 0 : _a.source) === "cpu"; },
        value: ({ stats }) => ({ nominal: 0.25, fair: 0.5, serious: 0.75, critical: 1 })[stats.pressure.state] || 0,
    },
    {
        id: "turn-usage",
        global: true,
        enabled: ({ stats }) => !!Object.values(stats.candidatePairs).length,
        value: ({ stats }) => Object.values(stats.candidatePairs).some((cp) => cp.usingTurn),
    },
    {
        id: "turn-tls-usage",
        global: true,
        enabled: ({ stats }) => !!Object.values(stats.candidatePairs).length,
        value: ({ stats }) => Object.values(stats.candidatePairs).some((cp) => cp.turnProtocol === "tls"),
    },
    {
        id: "concealment",
        enabled: ({ hasLiveTrack, ssrc0, kind }) => hasLiveTrack &&
            ssrc0 &&
            ssrc0.bitrate &&
            ssrc0.direction === "in" &&
            kind === "audio" &&
            ssrc0.audioLevel >= 0.001,
        value: ({ ssrc0 }) => ssrc0.audioConcealment,
    },
    {
        id: "deceleration",
        enabled: ({ hasLiveTrack, ssrc0, kind }) => hasLiveTrack &&
            ssrc0 &&
            ssrc0.bitrate &&
            ssrc0.direction === "in" &&
            kind === "audio" &&
            ssrc0.audioLevel >= 0.001,
        value: ({ ssrc0 }) => ssrc0.audioDeceleration,
    },
    {
        id: "acceleration",
        enabled: ({ hasLiveTrack, ssrc0, kind }) => hasLiveTrack &&
            ssrc0 &&
            ssrc0.bitrate &&
            ssrc0.direction === "in" &&
            kind === "audio" &&
            ssrc0.audioLevel >= 0.001,
        value: ({ ssrc0 }) => ssrc0.audioAcceleration,
    },
    {
        id: "qpf",
        enabled: ({ hasLiveTrack, track, ssrc0, kind }) => hasLiveTrack && kind === "video" && track && ssrc0 && ssrc0.height,
        value: ({ trackStats }) => Object.values(trackStats.ssrcs).reduce((sum, ssrc) => sum + (ssrc.qpf || 0), 0),
    },
];
let aggregatedMetrics;
let aggregatedIssues;
let issuesAndMetricsByView;
const initIssuesAndMetricsByView = () => {
    aggregatedMetrics = {};
    aggregatedIssues = {};
    issuesAndMetricsByView = {
        aggregated: {
            issues: aggregatedIssues,
            metrics: aggregatedMetrics,
        },
    };
};
initIssuesAndMetricsByView();
const getIssuesAndMetrics = () => {
    return Object.assign({}, issuesAndMetricsByView);
};
function onUpdatedStats(statsByView, clients) {
    Object.values(aggregatedMetrics).forEach((metricData) => {
        metricData.curTicks = 0;
        metricData.curSum = 0;
        metricData.curMax = 0;
        metricData.curMin = 0;
        metricData.curAvg = 0;
    });
    Object.values(aggregatedIssues).forEach((issueData) => {
        issueData.curTicks = 0;
        issueData.curRegistered = 0;
        issueData.active = false;
    });
    if (!clients.find((client) => !client.isLocalClient))
        return;
    clients.forEach((client) => {
        const stats = statsByView[client.id];
        ["video", "audio", "global"].forEach((kind) => {
            var _a, _b;
            if (!(kind === "global" && !client.isPresentation) && !((_a = client[kind]) === null || _a === void 0 ? void 0 : _a.enabled))
                return;
            if (kind === "global" && !client.isLocalClient)
                return;
            let issuesAndMetrics = issuesAndMetricsByView[client.id];
            if (!issuesAndMetrics) {
                issuesAndMetrics = { issues: {}, metrics: {} };
                issuesAndMetricsByView[client.id] = issuesAndMetrics;
            }
            const track = (_b = client[kind]) === null || _b === void 0 ? void 0 : _b.track;
            const hasLiveTrack = !!track && track.readyState !== "ended";
            const trackStats = track && stats && stats.tracks[track.id];
            const ssrcs = trackStats
                ? Object.values(trackStats.ssrcs).sort((a, b) => (a.height || Number.MAX_SAFE_INTEGER) - (b.height || Number.MAX_SAFE_INTEGER))
                : [];
            const ssrc0 = ssrcs[0];
            const checkData = {
                client,
                clients,
                kind,
                track,
                trackStats,
                stats,
                hasLiveTrack,
                ssrc0,
                ssrcs,
                issues: issuesAndMetrics.issues,
                metrics: issuesAndMetrics.metrics,
            };
            const qualifierString = kind === "global"
                ? "global"
                : `${client.isLocalClient ? "loc" : "rem"}-${client.isPresentation ? "pres" : "cam"}-${kind}`;
            metrics.forEach((metric) => {
                if (metric.global && kind !== "global")
                    return;
                if (!metric.global && kind === "global")
                    return;
                const enabled = metric.enabled ? metric.enabled(checkData) : true;
                if (enabled) {
                    const metricKey = `${qualifierString}-${metric.id}`;
                    let metricData = issuesAndMetrics.metrics[metricKey];
                    const value = metric.value(checkData);
                    if (!metricData) {
                        metricData = {
                            ticks: 0,
                            sum: 0,
                            avg: 0,
                            min: value,
                            max: value,
                        };
                        issuesAndMetrics.metrics[metricKey] = metricData;
                    }
                    metricData.ticks++;
                    metricData.sum += value;
                    metricData.min = Math.min(metricData.min, value);
                    metricData.max = Math.max(metricData.max, value);
                    metricData.avg = metricData.sum / metricData.ticks;
                    let aggregatedMetricData = aggregatedMetrics[metricKey];
                    if (!aggregatedMetricData) {
                        aggregatedMetricData = {
                            ticks: 0,
                            sum: 0,
                            min: value,
                            max: value,
                            avg: 0,
                            curTicks: 0,
                            curMin: 0,
                            curMax: 0,
                            curAvg: 0,
                            curSum: 0,
                            totTicks: 0,
                            totMin: 0,
                            totMax: 0,
                            totAvg: 0,
                            totSum: 0,
                        };
                        aggregatedMetrics[metricKey] = aggregatedMetricData;
                    }
                    aggregatedMetricData.ticks++;
                    aggregatedMetricData.sum += value;
                    aggregatedMetricData.min = Math.min(aggregatedMetricData.min, value);
                    aggregatedMetricData.max = Math.max(aggregatedMetricData.max, value);
                    aggregatedMetricData.avg = aggregatedMetricData.sum / aggregatedMetricData.ticks;
                    if (aggregatedMetricData.curTicks === 0) {
                        aggregatedMetricData.curMax = value;
                        aggregatedMetricData.curMin = value;
                        aggregatedMetricData.curSum = value;
                    }
                    else {
                        aggregatedMetricData.curMax = Math.max(aggregatedMetricData.curMax, value);
                        aggregatedMetricData.curMin = Math.min(aggregatedMetricData.curMin, value);
                        aggregatedMetricData.curSum += value;
                    }
                    aggregatedMetricData.curTicks++;
                }
            });
            issueDetectors.forEach((issueDetector) => {
                if (issueDetector.global && kind !== "global")
                    return;
                if (!issueDetector.global && kind === "global")
                    return;
                const issueKey = `${qualifierString}-${issueDetector.id}`;
                const enabled = issueDetector.enabled(checkData);
                let issueData = issuesAndMetrics.issues[issueKey];
                let aggregatedIssueData = aggregatedIssues[issueKey];
                if (enabled) {
                    if (!issueData) {
                        issueData = {
                            active: false,
                            ticks: 0,
                            registered: 0,
                            initial: 0,
                            periods: 0,
                            current: 0,
                            longest: 0,
                        };
                        issuesAndMetrics.issues[issueKey] = issueData;
                    }
                    issueData.ticks++;
                    if (!aggregatedIssueData) {
                        aggregatedIssueData = {
                            active: false,
                            ticks: 0,
                            registered: 0,
                            initial: 0,
                            curTicks: 0,
                            curRegistered: 0,
                        };
                        aggregatedIssues[issueKey] = aggregatedIssueData;
                    }
                    aggregatedIssueData.ticks++;
                    aggregatedIssueData.curTicks++;
                    const issueDetected = issueDetector.check(checkData);
                    if (issueDetected) {
                        issueData.registered++;
                        issueData.current++;
                        if (!issueData.active) {
                            issueData.active = true;
                            issueData.periods++;
                        }
                        if (issueData.ticks === issueData.initial + 1) {
                            issueData.initial++;
                            aggregatedIssueData.initial++;
                        }
                        if (issueData.current > issueData.longest)
                            issueData.longest = issueData.current;
                        aggregatedIssueData.active = true;
                        aggregatedIssueData.registered++;
                        aggregatedIssueData.curRegistered++;
                    }
                    else {
                        issueData.active = false;
                        issueData.current = 0;
                    }
                }
                else {
                    if (issueData) {
                        issueData.active = false;
                        issueData.current = 0;
                    }
                }
            });
        });
    });
    Object.values(aggregatedMetrics).forEach((aggregatedMetricData) => {
        if (aggregatedMetricData.curTicks) {
            aggregatedMetricData.curAvg = aggregatedMetricData.curSum / aggregatedMetricData.curTicks;
            if (aggregatedMetricData.totTicks === 0) {
                aggregatedMetricData.totMin = aggregatedMetricData.curSum;
                aggregatedMetricData.totMax = aggregatedMetricData.curSum;
            }
            aggregatedMetricData.totTicks++;
            aggregatedMetricData.totSum += aggregatedMetricData.curSum;
            aggregatedMetricData.totMin = Math.min(aggregatedMetricData.totMin, aggregatedMetricData.curSum);
            aggregatedMetricData.totMax = Math.max(aggregatedMetricData.totMax, aggregatedMetricData.curSum);
            aggregatedMetricData.totAvg = aggregatedMetricData.totSum / aggregatedMetricData.totTicks;
        }
    });
    Object.values(aggregatedIssues).forEach((aggregateIssueData) => {
        if (aggregateIssueData.curTicks) ;
    });
    subscriptions.forEach((subscription) => { var _a; return (_a = subscription.onUpdatedIssues) === null || _a === void 0 ? void 0 : _a.call(subscription, issuesAndMetricsByView, statsByView, clients); });
}
function subscribeIssues(subscription) {
    subscriptions.push(subscription);
    if (!stopStats)
        stopStats = subscribeStats({ onUpdatedStats }).stop;
    return {
        stop() {
            subscriptions = subscriptions.filter((s) => s !== subscription);
            if (!subscriptions.length) {
                stopStats === null || stopStats === void 0 ? void 0 : stopStats();
                stopStats = null;
                initIssuesAndMetricsByView();
            }
        },
    };
}

const WINDOW_SIZE = 10;
const SET_TIMEOUT_INTERVAL = 500;
const RIC_TIMEOUT = 500;
const REPORTING_INTERVAL = 1000;
const buckets = [
    { name: "all", test: ({ avc }) => avc >= 1 },
    { name: "1", test: ({ avc }) => avc === 1 },
    { name: "2to4", test: ({ avc }) => avc >= 2 && avc <= 4 },
    { name: "5plus", test: ({ avc }) => avc >= 5 },
];
function startPerformanceMonitor({ onMetricsUpdated, onTerminated, isHidden, }) {
    const decodedFpsSamplesByTrack = {};
    let currentMetrics = {};
    const aggregatedMetrics = {};
    let wasHidden = false;
    const reportingInterval = setInterval(() => {
        if (wasHidden) {
            wasHidden = isHidden();
            return;
        }
        buckets
            .filter((bucket) => bucket.test(currentMetrics))
            .forEach((bucket) => {
            const bucketData = (aggregatedMetrics[bucket.name] = aggregatedMetrics[bucket.name] || {});
            Object.entries(currentMetrics).forEach(([metric, value]) => {
                if (!bucketData[metric])
                    bucketData[metric] = {
                        min: value,
                        max: value,
                        avg: value,
                        sum: value,
                        cur: value,
                        count: 1,
                    };
                else {
                    const old = bucketData[metric];
                    bucketData[metric] = {
                        min: Math.min(old.min, value),
                        max: Math.max(old.max, value),
                        avg: (old.sum + value) / (old.count + 1),
                        sum: old.sum + value,
                        cur: value,
                        count: old.count + 1,
                    };
                }
            });
        });
        onMetricsUpdated === null || onMetricsUpdated === void 0 ? void 0 : onMetricsUpdated(aggregatedMetrics);
    }, REPORTING_INTERVAL);
    let stHandle = 0;
    let ricHandle = 0;
    const measureScheduling = () => {
        const before = performance.now();
        stHandle = setTimeout(() => {
            const delayed = Math.max(0, performance.now() - (before + SET_TIMEOUT_INTERVAL));
            currentMetrics = Object.assign(Object.assign({}, currentMetrics), { stLag: delayed });
            if (window.requestIdleCallback) {
                const beforeRic = performance.now();
                ricHandle = requestIdleCallback((idleDeadline) => {
                    if (idleDeadline.didTimeout) {
                        currentMetrics = Object.assign(Object.assign({}, currentMetrics), { ricLag: RIC_TIMEOUT });
                    }
                    else {
                        currentMetrics = Object.assign(Object.assign({}, currentMetrics), { ricLag: performance.now() - beforeRic });
                    }
                }, { timeout: RIC_TIMEOUT });
            }
            measureScheduling();
        }, SET_TIMEOUT_INTERVAL);
    };
    measureScheduling();
    return {
        registerDecodedFps: (fpsData) => {
            if (wasHidden)
                return;
            const volatilityScores = fpsData.map(({ trackId, fps }) => {
                const samples = (decodedFpsSamplesByTrack[trackId] =
                    decodedFpsSamplesByTrack[trackId] || []);
                samples.push(fps) > WINDOW_SIZE && samples.shift();
                const mean = samples.reduce((acc, val) => acc + val, 0) / samples.length;
                const volatility = samples.reduce((acc, val) => acc + Math.abs(val - mean), 0) /
                    (samples.length * mean);
                return volatility;
            });
            if (volatilityScores.length) {
                const maxVolatility = Math.max(...volatilityScores);
                currentMetrics = Object.assign(Object.assign({}, currentMetrics), { rrVol: maxVolatility, avc: volatilityScores.length });
            }
        },
        terminate: () => {
            clearInterval(reportingInterval);
            clearTimeout(stHandle);
            if (ricHandle)
                cancelIdleCallback(ricHandle);
            onTerminated === null || onTerminated === void 0 ? void 0 : onTerminated();
        },
        updateHidden: () => {
            if (isHidden())
                wasHidden = true;
        },
    };
}

class SfuV2Parser {
    static parse(raw) {
        let object;
        try {
            object = JSON.parse(raw);
        }
        catch (error) {
            return;
        }
        if (typeof object !== "object" || Array.isArray(object)) {
            return;
        }
        if (object.request) {
            return SfuV2Parser._handleRequest(object);
        }
        else if (object.response) {
            return SfuV2Parser._handleResponse(object);
        }
        else if (object.message) {
            return SfuV2Parser._handleMessage(object);
        }
        else {
            return;
        }
    }
    static _handleRequest(rawMessage) {
        const message = {};
        message.request = true;
        if (typeof rawMessage.method !== "string") {
            return;
        }
        if (typeof rawMessage.id !== "number") {
            return;
        }
        message.id = rawMessage.id;
        message.method = rawMessage.method;
        message.data = rawMessage.data || {};
        return message;
    }
    static _handleResponse(rawMessage) {
        const message = {};
        message.response = true;
        if (typeof rawMessage.id !== "number") {
            return;
        }
        message.id = rawMessage.id;
        if (rawMessage.ok) {
            message.ok = true;
            message.data = rawMessage.data || {};
        }
        else {
            message.ok = false;
            message.errorCode = rawMessage.errorCode;
            message.errorReason = rawMessage.errorReason;
        }
        return message;
    }
    static _handleMessage(rawMessage) {
        const message = {};
        message.message = true;
        if (typeof rawMessage.method !== "string") {
            return;
        }
        message.method = rawMessage.method;
        message.data = rawMessage.data || {};
        return message;
    }
    static createRequest(method, data) {
        return {
            request: true,
            id: Math.round(Math.random() * 10000000),
            method,
            data: data || {},
        };
    }
    static createSuccessResponse(request, data) {
        return {
            response: true,
            id: request.id,
            ok: true,
            data: data || {},
        };
    }
    static createErrorResponse(request, errorCode, errorReason) {
        return {
            response: true,
            id: request.id,
            errorCode,
            errorReason,
        };
    }
    static createMessage(method, data) {
        return {
            message: true,
            method,
            data: data || {},
        };
    }
}

const logger$8 = new Logger();
class VegaConnection extends EventEmitter$1 {
    constructor(wsUrl, protocol = "whereby-sfu#v4") {
        super();
        this.socket = null;
        this.wsUrl = wsUrl;
        this.protocol = protocol;
        this.sents = new Map();
        this._setupSocket();
    }
    _setupSocket() {
        this.socket = new WebSocket(this.wsUrl, this.protocol);
        this.socket.onopen = this._onOpen.bind(this);
        this.socket.onmessage = this._onMessage.bind(this);
        this.socket.onclose = this._onClose.bind(this);
        this.socket.onerror = this._onError.bind(this);
    }
    _tearDown() {
        if (this.socket === null)
            return;
        this.socket.onopen = null;
        this.socket.onmessage = null;
        this.socket.onclose = null;
        this.socket.onerror = null;
        this.socket = null;
        this.sents.forEach((sent) => sent.close());
        this.emit("close");
    }
    close() {
        var _a;
        (_a = this.socket) === null || _a === void 0 ? void 0 : _a.close();
    }
    _onOpen() {
        logger$8.info("Connected");
        this.emit("open");
    }
    _onMessage(event) {
        const socketMessage = SfuV2Parser.parse(event.data);
        logger$8.info("Received message", socketMessage);
        if (socketMessage === null || socketMessage === void 0 ? void 0 : socketMessage.response) {
            this._handleResponse(socketMessage);
        }
        else if (socketMessage === null || socketMessage === void 0 ? void 0 : socketMessage.message) {
            this.emit("message", socketMessage);
        }
    }
    _onClose() {
        logger$8.info("Disconnected");
        this._tearDown();
    }
    _onError(error) {
        logger$8.info("Error", error);
    }
    _handleResponse(socketMessage) {
        const sent = this.sents.get(socketMessage.id);
        if (socketMessage.ok) {
            sent.resolve(socketMessage.data);
        }
        else {
            const error = new Error(socketMessage.errorReason);
            sent.reject(error);
        }
    }
    send(message) {
        try {
            if (this.socket) {
                this.socket.send(JSON.stringify(message));
            }
        }
        catch (error) { }
    }
    message(method, data = {}) {
        const message = SfuV2Parser.createMessage(method, data);
        this.send(message);
    }
    request(method, data = {}, timeout = 1500 * (15 + 0.1 * this.sents.size)) {
        const request = SfuV2Parser.createRequest(method, data);
        this.send(request);
        return new Promise((pResolve, pReject) => {
            const sent = {
                id: request.id,
                method: request.method,
                resolve: (data2) => {
                    if (!this.sents.delete(request.id))
                        return;
                    clearTimeout(sent.timer);
                    pResolve(data2);
                },
                reject: (error) => {
                    if (!this.sents.delete(request.id))
                        return;
                    clearTimeout(sent.timer);
                    pReject(error);
                },
                timer: setTimeout(() => {
                    if (!this.sents.delete(request.id))
                        return;
                    pReject(new Error("request timeout"));
                }, timeout),
                close: () => {
                    clearTimeout(sent.timer);
                    pReject(new Error("transport closed"));
                },
            };
            this.sents.set(request.id, sent);
        });
    }
}

const logger$7 = new Logger();
class BandwidthTester extends EventEmitter {
    constructor({ features } = {}) {
        super();
        this.closed = false;
        this._features = features || {};
        this._vegaConnection = null;
        this._mediasoupDevice = new Device({ handlerName: getHandler() });
        this._routerRtpCapabilities = null;
        this._sendTransport = null;
        this._receiveTransport = null;
        this._producer = null;
        this._consumers = new Map();
        this._startTime = null;
        this._connectTime = null;
        this._mediaEstablishedTime = null;
        this._runTime = null;
        this._endTime = null;
        this._timeout = null;
        this._canvas = null;
        this._drawInterval = null;
        this._resultTimeout = null;
    }
    start(runTime = 15) {
        if (this.closed) {
            return;
        }
        if (runTime < 10) {
            this.emit("result", {
                error: true,
                details: {
                    timeout: true,
                },
            });
            return this.close();
        }
        this._runTime = runTime;
        this._startTime = Date.now();
        const host = this._features.sfuServerOverrideHost || "any.sfu.whereby.com";
        const wsUrl = `wss://${host}`;
        this._vegaConnection = new VegaConnection(wsUrl, "whereby-sfu#bw-test-v1");
        this._vegaConnection.on("open", () => this._start());
        this._vegaConnection.on("close", () => this.close());
        this._vegaConnection.on("message", (message) => this._onMessage(message));
        this._startTimeout();
    }
    close() {
        logger$7.info("close()");
        this.closed = true;
        this._clearTimeouts();
        clearInterval(this._drawInterval);
        this._drawInterval = null;
        if (this._sendTransport) {
            this._sendTransport.removeAllListeners();
            this._sendTransport.close();
        }
        this._sendTransport = null;
        if (this._receiveTransport) {
            this._receiveTransport.removeAllListeners();
            this._receiveTransport.close();
        }
        this._receiveTransport = null;
        this._mediasoupDevice = null;
        if (this._vegaConnection) {
            this._vegaConnection.removeAllListeners();
            this._vegaConnection.close();
        }
        this._vegaConnection = null;
        this.emit("close");
    }
    _start() {
        return __awaiter(this, void 0, void 0, function* () {
            logger$7.info("_start()");
            this._connectTime = Date.now() - this._startTime;
            if (this._connectTime > 5000) {
                this.emit("result", {
                    error: true,
                    details: {
                        timeout: true,
                    },
                });
                return this.close();
            }
            this._clearTimeouts();
            try {
                const { routerRtpCapabilities } = yield this._vegaConnection.request("getCapabilities");
                if (!this._routerRtpCapabilities) {
                    modifyMediaCapabilities(routerRtpCapabilities, this._features);
                    this._routerRtpCapabilities = routerRtpCapabilities;
                    yield this._mediasoupDevice.load({ routerRtpCapabilities });
                }
                this._vegaConnection.message("setCapabilities", {
                    rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
                });
                yield Promise.all([this._createTransport(true), this._createTransport(false)]);
                yield this._createProducer();
                this._mediaEstablishedTime = Date.now() - this._startTime;
                if (this._mediaEstablishedTime > 5000) {
                    this.emit("result", {
                        error: true,
                        details: {
                            timeout: true,
                        },
                    });
                    return this.close();
                }
                this._resultTimeout = setTimeout(() => {
                    this._reportResults();
                }, this._runTime * 1000 - this._mediaEstablishedTime);
            }
            catch (error) {
                logger$7.error("_start() [error:%o]", error);
                this.emit("result", {
                    error: true,
                    details: {
                        timeout: true,
                    },
                });
                this.close();
            }
        });
    }
    _createTransport(send) {
        return __awaiter(this, void 0, void 0, function* () {
            const creator = send ? "createSendTransport" : "createRecvTransport";
            const transportOptions = yield this._vegaConnection.request("createTransport", {
                producing: send,
                consuming: !send,
                enableTcp: true,
                enableUdp: true,
                preferUdp: true,
            });
            transportOptions.iceServers = [{ urls: "stun:any.turn.whereby.com" }];
            const transport = this._mediasoupDevice[creator](transportOptions);
            transport.on("connect", ({ dtlsParameters }, callback) => {
                this._vegaConnection.message("connectTransport", {
                    transportId: transport.id,
                    dtlsParameters,
                });
                callback();
            });
            if (send) {
                transport.on("produce", ({ kind, rtpParameters, appData }, callback, errback) => __awaiter(this, void 0, void 0, function* () {
                    try {
                        const { paused } = appData;
                        const { id } = yield this._vegaConnection.request("produce", {
                            transportId: transport.id,
                            kind,
                            rtpParameters,
                            paused,
                            appData,
                        });
                        callback({ id });
                    }
                    catch (error) {
                        errback(error);
                    }
                }));
                this._sendTransport = transport;
            }
            else {
                this._receiveTransport = transport;
            }
        });
    }
    _getTestTrack() {
        this._canvas = document.createElement("canvas");
        this._canvas.width = 1280;
        this._canvas.height = 720;
        document.body.appendChild(this._canvas);
        this._canvas.style.position = "absolute";
        this._canvas.style.right = "0";
        this._canvas.style.bottom = "0";
        this._canvas.style.width = "1px";
        this._canvas.style.height = "1px";
        this._canvas.style.opacity = "0";
        const context = this._canvas.getContext("2d");
        const ball = {
            x: 150,
            y: 150,
        };
        const velocity = 50;
        const startingAngle = 70;
        const rad = 100;
        let moveX = Math.cos((Math.PI / 180) * startingAngle) * velocity;
        let moveY = Math.sin((Math.PI / 180) * startingAngle) * velocity;
        const draw = () => {
            if (this.closed) {
                return;
            }
            const now = Date.now();
            context.fillStyle = `rgb(
                ${Math.floor(255 - 0.35 * ball.y)},
                ${Math.floor(255 - 0.18 * ball.x)},
                0)`;
            context.fillRect(0, 0, this._canvas.width, this._canvas.height);
            context.fillStyle = "white";
            context.font = "30px Arial";
            context.fillText("Whereby", 10, 50);
            context.font = "20px Arial";
            context.fillText("Test", 10, 80);
            context.font = "20px Arial";
            context.fillText(`Time: ${now - this._startTime}ms`, 10, 110);
            context.font = "20px Arial";
            if (this._connectTime) {
                context.fillText(`Connect: ${this._connectTime}ms`, 10, 140);
            }
            if (this._mediaEstablishedTime) {
                context.fillText(`Media: ${this._mediaEstablishedTime}ms`, 10, 170);
            }
            if (ball.x > this._canvas.width - rad || ball.x < rad)
                moveX = -moveX;
            if (ball.y > this._canvas.height - rad || ball.y < rad)
                moveY = -moveY;
            ball.x += moveX;
            ball.y += moveY;
            context.beginPath();
            context.fillStyle = `rgb(
                ${Math.floor(255 - 0.18 * ball.x)},
                ${Math.floor(255 - 0.35 * ball.y)},
                0)`;
            context.arc(ball.x, ball.y, rad, 0, Math.PI * 2);
            context.fill();
            context.closePath();
        };
        this._drawInterval = setInterval(draw, 33);
        const fakeStream = this._canvas.captureStream(30);
        const [fakeVideoTrack] = fakeStream.getVideoTracks();
        return fakeVideoTrack;
    }
    _createProducer() {
        return __awaiter(this, void 0, void 0, function* () {
            const track = this._getTestTrack();
            const producer = yield this._sendTransport.produce(Object.assign(Object.assign({ track }, getMediaSettings("video", false, this._features)), { appData: {
                    paused: false,
                } }));
            this._producer = producer;
            producer.observer.once("close", () => {
                logger$7.info('producer "close" event');
                this._producer = null;
            });
        });
    }
    _onMessage(message) {
        return __awaiter(this, void 0, void 0, function* () {
            const { method, data } = message;
            return Promise.resolve()
                .then(() => {
                switch (method) {
                    case "consumerReady":
                        return this._onConsumerReady(data);
                    case "consumerClosed":
                        return this._onConsumerClosed(data);
                    default:
                        logger$7.info(`unknown message method "${method}"`);
                        return;
                }
            })
                .catch((error) => {
                logger$7.error('"message" failed [error:%o]', error);
            });
        });
    }
    _onConsumerReady(options) {
        return __awaiter(this, void 0, void 0, function* () {
            const consumer = yield this._receiveTransport.consume(options);
            consumer.once("close", () => {
                this._consumers.delete(consumer.id);
            });
            this._consumers.set(consumer.id, consumer);
            this._vegaConnection.message("resumeConsumers", {
                consumerIds: [consumer.id],
            });
        });
    }
    _onConsumerClosed({ consumerId }) {
        logger$7.info("_onConsumerClosed()");
        const consumer = this._consumers.get(consumerId);
        if (consumer) {
            consumer.close();
        }
    }
    _reportResults() {
        return __awaiter(this, void 0, void 0, function* () {
            const [localSendStats, localRecvStats] = yield Promise.all([
                this._sendTransport.getStats(),
                this._receiveTransport.getStats(),
            ]);
            const { recvStats: [remoteRecvStats = null] = [] } = yield this._vegaConnection.request("getTransportStats");
            const { availableOutgoingBitrate = 5000000 } = remoteRecvStats || {};
            let outboundPackets = 0;
            let remotePacketsLost = 0;
            localSendStats.forEach((localSendStat) => {
                if (localSendStat.type === "outbound-rtp" && typeof localSendStat.packetsSent === "number") {
                    outboundPackets += localSendStat.packetsSent;
                }
                else if (localSendStat.type === "remote-inbound-rtp" && typeof localSendStat.packetsLost === "number") {
                    remotePacketsLost += localSendStat.packetsLost;
                }
            });
            let inboundPackets = 0;
            let packetsLost = 0;
            localRecvStats.forEach((localRecvStat) => {
                if (localRecvStat.type === "inbound-rtp" &&
                    typeof localRecvStat.packetsReceived === "number" &&
                    typeof localRecvStat.packetsLost === "number") {
                    inboundPackets += localRecvStat.packetsReceived;
                    packetsLost += localRecvStat.packetsLost;
                }
            });
            const recvAvailableBitrate = availableOutgoingBitrate / 1000000.0;
            const lowRecvAvailableBitrate = recvAvailableBitrate < 1.5;
            const sendLoss = remotePacketsLost / outboundPackets;
            const recvLoss = packetsLost / inboundPackets;
            const highSendLoss = sendLoss > 0.03;
            const highRecvLoss = recvLoss > 0.03;
            const testTime = Date.now() - this._startTime;
            const warning = lowRecvAvailableBitrate || highRecvLoss || highSendLoss;
            const success = !warning;
            this.emit("result", {
                warning,
                success,
                details: {
                    testTime,
                    recvAvailableBitrate,
                    lowRecvAvailableBitrate,
                    highSendLoss,
                    highRecvLoss,
                    sendLoss,
                    recvLoss,
                },
            });
            this.close();
        });
    }
    _startTimeout(seconds = 5) {
        this._timeout = setTimeout(() => {
            this.emit("result", {
                error: true,
                details: {
                    timeout: true,
                },
            });
            this.close();
        }, seconds * 1000);
    }
    _clearTimeouts() {
        clearTimeout(this._timeout);
        this._timeout = null;
        clearTimeout(this._reportTimeout);
        this._reportTimeout = null;
    }
}

var _a$6;
const adapter$5 = (_a$6 = adapterRaw.default) !== null && _a$6 !== void 0 ? _a$6 : adapterRaw;
function detectMicrophoneNotWorking(pc) {
    if (adapter$5.browserDetails.browser !== "chrome" ||
        adapter$5.browserDetails.browser < 58 ||
        pc.signalingState === "closed") {
        return Promise.resolve(false);
    }
    const sendingAudio = pc.getSenders().some((sender) => sender.track && sender.track.kind === "audio");
    const receivingAudio = pc.getReceivers().some((receiver) => receiver.track && receiver.track.kind === "audio");
    return pc.getStats(null).then((result) => {
        let microphoneFailed = false;
        result.forEach((report) => {
            if (report.type === "outbound-rtp" &&
                (report.kind === "audio" || report.mediaType === "audio") &&
                sendingAudio) {
                if (report.bytesSent === 0) {
                    microphoneFailed = "outbound";
                }
            }
            else if (report.type === "inbound-rtp" &&
                (report.kind === "audio" || report.mediaType === "audio") &&
                receivingAudio) {
                if (report.bytesReceived === 0) {
                    microphoneFailed = "inbound";
                }
            }
        });
        return microphoneFailed;
    });
}

const MAXIMUM_TURN_BANDWIDTH = 1280;
const MAXIMUM_TURN_BANDWIDTH_UNLIMITED = -1;
const MEDIA_JITTER_BUFFER_TARGET = 400;

var _a$5;
const adapter$4 = (_a$5 = adapterRaw.default) !== null && _a$5 !== void 0 ? _a$5 : adapterRaw;
const isSafari = adapter$4.browserDetails.browser === "safari";
const parseResolution = (res) => res.split(/[^\d]/g).map((n) => parseInt(n, 10));
function getMediaConstraints({ disableAEC, disableAGC, hd, lax, lowDataMode, preferredDeviceIds, resolution, simulcast, widescreen, }) {
    var _a, _b;
    let HIGH_HEIGHT = 480;
    let LOW_HEIGHT = 240;
    if (hd) {
        HIGH_HEIGHT = lax || isSafari ? 720 : { min: 360, ideal: 720 };
    }
    if (simulcast) {
        if (hd === false) {
            HIGH_HEIGHT = 360;
            LOW_HEIGHT = 270;
        }
        else {
            LOW_HEIGHT = 360;
        }
    }
    const constraints = {
        audio: Object.assign({}, (preferredDeviceIds.audioId && { deviceId: preferredDeviceIds.audioId })),
        video: Object.assign(Object.assign({}, (preferredDeviceIds.videoId ? { deviceId: preferredDeviceIds.videoId } : { facingMode: "user" })), { height: lowDataMode ? LOW_HEIGHT : HIGH_HEIGHT, frameRate: lowDataMode && !simulcast ? 15 : 24 }),
    };
    if (lax) {
        if (!((_a = constraints.audio) === null || _a === void 0 ? void 0 : _a.deviceId))
            constraints.audio = true;
        (_b = constraints.video) === null || _b === void 0 ? true : delete _b.facingMode;
        return constraints;
    }
    if (resolution) {
        const [w, h, fps] = parseResolution(resolution);
        if (w)
            constraints.video.width = { exact: w };
        if (h)
            constraints.video.height = { exact: h };
        if (fps)
            constraints.video.frameRate = { exact: fps };
        delete constraints.video.facingMode;
    }
    else {
        constraints.video.aspectRatio = widescreen ? 16 / 9 : 4 / 3;
    }
    if (disableAGC)
        constraints.audio.autoGainControl = false;
    if (disableAEC)
        constraints.audio.echoCancellation = false;
    return constraints;
}
function getConstraints({ devices, videoId, audioId, options, type = "ideal" }) {
    const audioDevices = devices.filter((d) => d.kind === "audioinput");
    const videoDevices = devices.filter((d) => d.kind === "videoinput");
    const useDefaultAudio = !audioId || !audioDevices.some((d) => d.deviceId === audioId);
    const useDefaultVideo = !videoId || !videoDevices.some((d) => d.deviceId === videoId);
    const constraints = getMediaConstraints(Object.assign({ preferredDeviceIds: {
            audioId: useDefaultAudio ? null : { [type]: audioId },
            videoId: useDefaultVideo ? null : { [type]: videoId },
        } }, options));
    if (audioId === false || !audioDevices.length) {
        delete constraints.audio;
    }
    if (videoId === false || !videoDevices.length) {
        delete constraints.video;
    }
    return constraints;
}

const logger$6 = new Logger();
const isMobile = /mobi/i.test(navigator.userAgent);
class NoDevicesError extends Error {
    constructor(...args) {
        super(...args);
        this.name = "nodevices";
    }
}
function removeDuplicates(devices) {
    return devices.filter((device, i, self) => i === self.findIndex((d) => d.deviceId === device.deviceId && d.kind === device.kind));
}
function enumerate() {
    return __awaiter(this, void 0, void 0, function* () {
        const devices = yield navigator.mediaDevices.enumerateDevices();
        const filteredDevices = removeDuplicates(devices);
        return filteredDevices;
    });
}
const idFieldsByKind = {
    audioinput: "audioId",
    videoinput: "videoId",
    audiooutput: "speakerId",
};
function buildDeviceList({ busyDeviceIds, devices, kind }) {
    const deviceList = devices &&
        devices.length &&
        devices
            .filter((d) => d.kind === kind)
            .map((d) => ({
            [idFieldsByKind[kind]]: d.deviceId,
            label: `${busyDeviceIds.includes(d.deviceId) ? "(busy) " : ""}${d.label || d.deviceId.slice(0, 5)}`,
            busy: busyDeviceIds.includes(d.deviceId),
        }));
    return deviceList && deviceList.length !== 0
        ? deviceList
        : [{ [idFieldsByKind[kind]]: "", label: "Default" }];
}
function getUserMedia(constraints) {
    if (!constraints.audio && !constraints.video) {
        return Promise.reject(new NoDevicesError("No provided devices"));
    }
    return navigator.mediaDevices.getUserMedia(constraints).catch((error) => {
        const message = `${error}, ${JSON.stringify(constraints, null, 2)}`;
        logger$6.error(`getUserMedia ${message}`);
        throw error;
    });
}
function getSettingsFromTrack(kind, track, devices, lastUsedId) {
    var _a, _b, _c;
    let settings = { deviceId: null };
    if (!track) {
        if (lastUsedId && devices) {
            settings.deviceId = (_a = devices.find((d) => d.deviceId === lastUsedId && d.kind === kind)) === null || _a === void 0 ? void 0 : _a.deviceId;
        }
        return settings;
    }
    settings.label = track.label;
    if (track.getSettings) {
        settings = Object.assign(Object.assign({}, settings), track.getSettings());
    }
    if (settings.deviceId)
        return settings;
    if (track.getCapabilities) {
        settings.deviceId = track.getCapabilities().deviceId;
    }
    if (settings.deviceId)
        return settings;
    if (track.label && devices) {
        settings.deviceId = (_b = devices.find((d) => track.label === d.label && d.kind === kind)) === null || _b === void 0 ? void 0 : _b.deviceId;
    }
    if (settings.deviceId)
        return settings;
    settings.deviceId = (_c = track.getConstraints()) === null || _c === void 0 ? void 0 : _c.deviceId;
    settings.broken = 1;
    return settings;
}
function getDeviceData({ audioTrack, videoTrack, devices, stoppedVideoTrack, lastAudioId, lastVideoId, }) {
    const usable = (d) => ((d === null || d === void 0 ? void 0 : d.readyState) === "live" ? d : null);
    videoTrack = usable(videoTrack) || stoppedVideoTrack;
    audioTrack = usable(audioTrack);
    const video = getSettingsFromTrack("videoinput", videoTrack, devices, lastVideoId);
    const audio = getSettingsFromTrack("audioinput", audioTrack, devices, lastAudioId);
    return { video, audio };
}
function stopStreamTracks(stream, only) {
    if (!only || only === "audio")
        stream.getAudioTracks().forEach((t) => t.stop());
    if (!only || only === "video")
        stream.getVideoTracks().forEach((t) => t.stop());
}
function replaceTracksInStream(stream, newStream, only) {
    const replacedTracks = [];
    if (!only || only === "audio") {
        replacedTracks.push(...stream.getAudioTracks());
        newStream.getAudioTracks().forEach((track) => {
            track.replacement = true;
            stream.addTrack(track);
        });
    }
    if (!only || only === "video") {
        replacedTracks.push(...stream.getVideoTracks());
        newStream.getVideoTracks().forEach((track) => {
            track.replacement = true;
            stream.addTrack(track);
        });
    }
    replacedTracks.forEach((track) => {
        track.replaced = true;
        stream.removeTrack(track);
    });
    return replacedTracks;
}
function getTrack({ kind, deviceId, type, fallback = true, primerTrack, }) {
    return __awaiter(this, void 0, void 0, function* () {
        const devId = (deviceId) => (type === "exact" ? { deviceId: { exact: deviceId } } : { deviceId });
        const constraints = {
            [kind]: deviceId ? devId(deviceId) : kind === "video" ? { facingMode: "user" } : true,
        };
        let stream;
        try {
            stream = yield getUserMedia(constraints);
        }
        catch (e) {
            if (!fallback) {
                e.details = { constraints, constraint: e.constraint };
                throw e;
            }
            if (primerTrack) {
                return primerTrack;
            }
            stream = yield getUserMedia({ [kind]: true });
        }
        return stream.getTracks()[0];
    });
}
function constrainTrack(track, constraints) {
    return __awaiter(this, void 0, void 0, function* () {
        while (constraints.length) {
            try {
                yield track.applyConstraints(Object.assign({}, ...constraints));
                break;
            }
            catch (e) {
                const c = constraints.pop();
                logger$6.warn(`unable to apply ${JSON.stringify(c)}`, e);
            }
        }
    });
}
function getStream2(constraintOpt, additionalOpts = {}) {
    return __awaiter(this, void 0, void 0, function* () {
        const { audioId, videoId, devices, type, options } = constraintOpt;
        const { replaceStream, fallback } = additionalOpts;
        const hasGrantedPermissions = !!devices.find((d) => d.label !== "");
        let audioPrimerTrack;
        let videoPrimerTrack;
        if (!hasGrantedPermissions) {
            try {
                const primerStream = yield getUserMedia({
                    audio: constraintOpt.audioId !== false,
                    video: constraintOpt.videoId !== false,
                });
                audioPrimerTrack = primerStream.getAudioTracks()[0];
                videoPrimerTrack = primerStream.getVideoTracks()[0];
            }
            catch (err) {
                if (err.name === "NotAllowedError") {
                    throw err;
                }
            }
        }
        const getAudio = () => __awaiter(this, void 0, void 0, function* () {
            if (audioId === false)
                return false;
            if (replaceStream)
                stopStreamTracks(replaceStream, "audio");
            const audioTrack = yield getTrack({
                deviceId: audioId,
                type,
                kind: "audio",
                fallback,
                primerTrack: audioPrimerTrack,
            });
            if (audioPrimerTrack && audioTrack !== audioPrimerTrack) {
                audioPrimerTrack.stop();
            }
            const { disableAEC, disableAGC } = options;
            const changes = [];
            if (disableAGC) {
                changes.push({ autoGainControl: false });
            }
            if (disableAEC) {
                changes.push({ echoCancellation: false });
            }
            yield constrainTrack(audioTrack, changes);
            return audioTrack;
        });
        const getVideo = () => __awaiter(this, void 0, void 0, function* () {
            if (videoId === false)
                return false;
            if (replaceStream)
                stopStreamTracks(replaceStream, "video");
            const videoTrack = yield getTrack({
                deviceId: videoId,
                type,
                kind: "video",
                fallback,
                primerTrack: videoPrimerTrack,
            });
            if (videoPrimerTrack && videoTrack !== videoPrimerTrack) {
                videoPrimerTrack.stop();
            }
            const { lowDataMode, simulcast, usingAspectRatio16x9 } = options;
            const changes = [];
            if (lowDataMode) {
                changes.push({ frameRate: simulcast ? 30 : 15 });
            }
            if (usingAspectRatio16x9) {
                changes.push({ aspectRatio: 16 / 9 });
            }
            if (lowDataMode) {
                changes.push({ width: { max: simulcast ? 640 : 320 } });
            }
            else {
                changes.push({ width: { max: 1280 } });
            }
            yield constrainTrack(videoTrack, changes);
            return videoTrack;
        });
        const audioPromise = getAudio();
        const videoPromise = getVideo();
        let audioTrack;
        let videoTrack;
        let error;
        try {
            audioTrack = yield audioPromise;
        }
        catch (e) {
            error = e;
            if (type === "exact")
                throw e;
        }
        try {
            videoTrack = yield videoPromise;
        }
        catch (e) {
            error = e;
            if (type === "exact")
                throw e;
        }
        const newStream = new MediaStream([audioTrack, videoTrack].filter(Boolean));
        let replacedTracks;
        if (replaceStream) {
            const only = (audioId === false && "video") || (videoId === false && "audio") || "audio";
            replacedTracks = replaceTracksInStream(replaceStream, newStream, only);
        }
        return { stream: replaceStream || newStream, error, replacedTracks };
    });
}
function getStream(constraintOpt, { replaceStream, fallback = true } = {}) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        let error;
        let newConstraints;
        let retryConstraintOpt;
        let stream = null;
        const only = (constraintOpt.audioId === false && "video") || (constraintOpt.videoId === false && "audio");
        const stopTracks = isMobile || only !== "video";
        const constraints = getConstraints(constraintOpt);
        const addDetails = (err, orgErr) => {
            err.details = Object.assign({ constraints, constraint: err.constraint || (orgErr === null || orgErr === void 0 ? void 0 : orgErr.constraint), newConstraints,
                fallback,
                stopTracks }, (err !== error && { error: String(error) }));
            return err;
        };
        if (stopTracks && replaceStream)
            stopStreamTracks(replaceStream, only);
        try {
            stream = yield getUserMedia(constraints);
        }
        catch (e) {
            error = e;
            if (!fallback) {
                throw addDetails(e);
            }
            if (e.name === "OverconstrainedError") {
                const laxConstraints = {
                    deviceId: { videoId: null, audioId: null },
                    width: { lax: true },
                    height: { lax: true },
                    "": { audioId: null, videoId: null, lax: true },
                };
                retryConstraintOpt = laxConstraints[e.constraint || ""];
            }
            else if (e.name === "NotFoundError") {
                if (constraints.audio && constraints.video) {
                    try {
                        stream = yield getUserMedia(getConstraints(Object.assign(Object.assign({}, constraintOpt), { audioId: false })));
                    }
                    catch (e2) {
                        if (e2.name !== "NotFoundError") {
                            throw addDetails(e2, e);
                        }
                    }
                    try {
                        if (!stream)
                            stream = yield getUserMedia(getConstraints(Object.assign(Object.assign({}, constraintOpt), { videoId: false })));
                    }
                    catch (e2) {
                        throw addDetails(e2, e);
                    }
                }
            }
            else if (e.name === "NotAllowedError" || e.name === "NotReadableError" || e.name === "AbortError") {
                if (replaceStream && !stopTracks) {
                    stopStreamTracks(replaceStream, only);
                    retryConstraintOpt = constraintOpt;
                }
                else if (e.name !== "NotAllowedError") {
                    try {
                        stream = yield getUserMedia(getConstraints(Object.assign(Object.assign({}, constraintOpt), { options: Object.assign(Object.assign({}, constraintOpt.options), { lax: true }) })));
                    }
                    catch (e2) {
                        logger$6.warn(`Tried getting stream again with laxer constraints, but failed: ${"" + e2}`);
                    }
                    const errMsg = ("" + e).toLowerCase();
                    const problemWith = { audio: "audioId", video: "videoId" }[((_a = /(video|audio)/.exec(errMsg)) === null || _a === void 0 ? void 0 : _a[0]) || only || ""];
                    if (!stream && problemWith) {
                        try {
                            stream = yield getUserMedia(getConstraints(Object.assign(Object.assign({}, constraintOpt), { [problemWith]: null })));
                        }
                        catch (e2) {
                            logger$6.warn(`Re-tried ${problemWith} with no constraints, but failed: ${"" + e2}`);
                        }
                    }
                    if (!stream) {
                        const tryOnly = problemWith ? [problemWith] : ["videoId", "audioId"];
                        for (const kind of tryOnly) {
                            try {
                                stream = yield getUserMedia(getConstraints(Object.assign(Object.assign({}, constraintOpt), { [kind]: false })));
                            }
                            catch (e2) {
                                logger$6.warn(`Re-tried without ${kind}, but failed: ${"" + e2}`);
                            }
                            if (stream)
                                break;
                        }
                    }
                }
            }
        }
        if (retryConstraintOpt) {
            const onlyConstraints = only ? { audio: { videoId: false }, video: { audioId: false } }[only] : {};
            newConstraints = getConstraints(Object.assign(Object.assign(Object.assign(Object.assign({}, constraintOpt), retryConstraintOpt), { options: Object.assign(Object.assign({}, constraintOpt.options), { lax: retryConstraintOpt.lax }) }), onlyConstraints));
            try {
                stream = yield getUserMedia(newConstraints);
            }
            catch (e) {
                throw addDetails(e, error);
            }
        }
        if (!stream) {
            throw addDetails(error);
        }
        let replacedTracks;
        if (replaceStream) {
            if (!stopTracks)
                stopStreamTracks(replaceStream, only);
            replacedTracks = replaceTracksInStream(replaceStream, stream, only);
            stream = replaceStream;
        }
        return { error: error && addDetails(error), stream, replacedTracks };
    });
}
function hasGetDisplayMedia() {
    if (global.navigator.userAgent.includes("Android")) {
        return false;
    }
    return !!(global.navigator.mediaDevices || {}).getDisplayMedia;
}
const defaultDisplayMediaConstraints = {
    video: {
        width: { max: window.screen.width },
        height: { max: window.screen.height },
    },
};
function getDisplayMedia(constraints = defaultDisplayMediaConstraints, contentHint = "detail") {
    return navigator.mediaDevices.getDisplayMedia(constraints).then((stream) => {
        stream.getVideoTracks().forEach((t) => {
            if ("contentHint" in t) {
                t.contentHint = contentHint;
            }
        });
        return stream;
    });
}
function compareLocalDevices(before, after) {
    const [beforeByKind, afterByKind] = [before, after].map((list) => list
        .filter((device) => device.kind && device.deviceId)
        .reduce((result, device) => (Object.assign(Object.assign({}, result), { [device.kind]: Object.assign(Object.assign({}, result[device.kind]), { [device.deviceId]: device }) })), {}));
    const changesByKind = {};
    before.forEach((device) => {
        if (!device.kind || !device.deviceId)
            return;
        if (!changesByKind[device.kind])
            changesByKind[device.kind] = { added: {}, removed: {}, changed: {} };
        if (!afterByKind[device.kind] || !afterByKind[device.kind][device.deviceId]) {
            changesByKind[device.kind].removed[device.deviceId] = device;
        }
    });
    after.forEach((device) => {
        if (!device.kind || !device.deviceId)
            return;
        if (!changesByKind[device.kind])
            changesByKind[device.kind] = { added: {}, removed: {}, changed: {} };
        if (!beforeByKind[device.kind] || !beforeByKind[device.kind][device.deviceId]) {
            changesByKind[device.kind].added[device.deviceId] = device;
        }
        else if (beforeByKind[device.kind][device.deviceId].label &&
            beforeByKind[device.kind][device.deviceId].label !== device.label) {
            changesByKind[device.kind].changed[device.deviceId] = device;
        }
    });
    return changesByKind;
}
function getUpdatedDevices({ oldDevices, newDevices, currentAudioId, currentVideoId, currentSpeakerId, }) {
    const changesByKind = compareLocalDevices(oldDevices, newDevices);
    const changedDevices = {};
    const addedDevices = {};
    [
        ["audioinput", currentAudioId],
        ["videoinput", currentVideoId],
        ["audiooutput", currentSpeakerId],
    ].forEach(([kind, currentDeviceId]) => {
        kind = kind || "";
        const changes = changesByKind[kind];
        if (!changes) {
            return;
        }
        if (currentDeviceId) {
            if (changes.removed[currentDeviceId]) {
                changedDevices[kind] = { deviceId: null };
                if (kind === "audiooutput") {
                    const fallbackSpeakerDevice = newDevices.find((d) => d.kind === "audiooutput");
                    changedDevices[kind] = { deviceId: fallbackSpeakerDevice === null || fallbackSpeakerDevice === void 0 ? void 0 : fallbackSpeakerDevice.deviceId };
                }
            }
            if (changes.changed[currentDeviceId]) {
                changedDevices[kind] = { deviceId: currentDeviceId };
            }
        }
        if (Object.keys(changes.added).length) {
            const [deviceAdded] = Object.keys(changes.added).slice(0, 1);
            const add = changes.added[deviceAdded];
            addedDevices[kind] = { deviceId: add.deviceId, label: add.label, kind: add.kind };
        }
    });
    return { addedDevices, changedDevices };
}

var _a$4;
(_a$4 = adapterRaw.default) !== null && _a$4 !== void 0 ? _a$4 : adapterRaw;
const RTCSTATS_PROTOCOL_VERSION = "1.0";
const GETSTATS_BUFFER_SIZE = 20;
const clientInfo = {
    id: v4$1(),
    connectionNumber: 0,
};
const noop = () => { };
let resetDelta = noop;
function rtcStatsConnection(wsURL, logger = console) {
    const buffer = [];
    let ws;
    let organizationId;
    let clientId;
    let displayName;
    let userRole;
    let roomSessionId;
    let connectionShouldBeOpen;
    let connectionAttempt = 0;
    let hasPassedOnRoomSessionId = false;
    let getStatsBufferUsed = 0;
    let deviceId;
    let roomProduct;
    let roomMode;
    let sfuServer;
    let featureFlags;
    const connection = {
        connected: false,
        trace: (...args) => {
            args.push(Date.now());
            if (args[0] === "customEvent" && args[2].type === "roomSessionId") {
                const oldRoomSessionIdValue = roomSessionId && roomSessionId[2].value.roomSessionId;
                const newRoomSessionIdValue = args[2].value.roomSessionId;
                roomSessionId = args;
                if (hasPassedOnRoomSessionId &&
                    newRoomSessionIdValue &&
                    newRoomSessionIdValue !== oldRoomSessionIdValue) {
                    ws === null || ws === void 0 ? void 0 : ws.close();
                }
                if (newRoomSessionIdValue)
                    hasPassedOnRoomSessionId = true;
            }
            else if (args[0] === "customEvent" && args[2].type === "clientId") {
                clientId = args;
            }
            else if (args[0] === "customEvent" && args[2].type === "organizationId") {
                organizationId = args;
            }
            else if (args[0] === "customEvent" && args[2].type === "displayName") {
                displayName = args;
            }
            else if (args[0] === "customEvent" && args[2].type === "userRole") {
                userRole = args;
            }
            else if (args[0] === "customEvent" && args[2].type === "deviceId") {
                deviceId = args;
            }
            else if (args[0] === "customEvent" && args[2].type === "roomProduct") {
                roomProduct = args;
            }
            else if (args[0] === "customEvent" && args[2].type === "roomMode") {
                roomMode = args;
            }
            else if (args[0] === "customEvent" && args[2].type === "sfuServer") {
                sfuServer = args;
            }
            else if (args[0] === "customEvent" && args[2].type === "featureFlags") {
                featureFlags = args;
            }
            if ((ws === null || ws === void 0 ? void 0 : ws.readyState) === WebSocket.OPEN) {
                connectionAttempt = 0;
                ws.send(JSON.stringify(args));
            }
            else if (args[0] === "getstats") {
                if (getStatsBufferUsed < GETSTATS_BUFFER_SIZE) {
                    getStatsBufferUsed++;
                    buffer.push(args);
                }
            }
            else if (args[0] === "customEvent" && args[2].type === "insightsStats") ;
            else {
                buffer.push(args);
            }
            if ((ws === null || ws === void 0 ? void 0 : ws.readyState) === WebSocket.CLOSED && connectionShouldBeOpen) {
                setTimeout(() => {
                    if (ws.readyState === WebSocket.CLOSED && connectionShouldBeOpen) {
                        connection.connect();
                    }
                }, 1000 * connectionAttempt);
            }
        },
        close: () => {
            connectionShouldBeOpen = false;
            ws === null || ws === void 0 ? void 0 : ws.close();
        },
        connect: () => {
            connectionShouldBeOpen = true;
            connectionAttempt += 1;
            ws === null || ws === void 0 ? void 0 : ws.close();
            connection.connected = true;
            ws = new WebSocket(wsURL + window.location.pathname, RTCSTATS_PROTOCOL_VERSION);
            ws.onerror = (e) => {
                connection.connected = false;
                logger.warn(`[RTCSTATS] WebSocket error`, e);
            };
            ws.onclose = (e) => {
                connection.connected = false;
                logger.info(`[RTCSTATS] Closed ${e.code}`);
                resetDelta();
            };
            ws.onopen = () => {
                clientInfo.connectionNumber++;
                ws.send(JSON.stringify(["clientInfo", null, clientInfo]));
                if (organizationId) {
                    ws.send(JSON.stringify(organizationId));
                }
                if (clientId) {
                    ws.send(JSON.stringify(clientId));
                }
                if (roomSessionId) {
                    ws.send(JSON.stringify(roomSessionId));
                }
                if (displayName) {
                    ws.send(JSON.stringify(displayName));
                }
                if (userRole) {
                    ws.send(JSON.stringify(userRole));
                }
                if (deviceId) {
                    ws.send(JSON.stringify(deviceId));
                }
                if (roomMode) {
                    ws.send(JSON.stringify(roomMode));
                }
                if (roomProduct) {
                    ws.send(JSON.stringify(roomProduct));
                }
                if (sfuServer) {
                    ws.send(JSON.stringify(sfuServer));
                }
                if (featureFlags) {
                    ws.send(JSON.stringify(featureFlags));
                }
                while (buffer.length) {
                    ws.send(JSON.stringify(buffer.shift()));
                }
                getStatsBufferUsed = 0;
            };
        },
    };
    return connection;
}
const server = rtcStatsConnection(process.env.RTCSTATS_URL || "wss://rtcstats.srv.whereby.com");
const stats = rtcstats(server.trace, 10000, [""]);
resetDelta = (stats === null || stats === void 0 ? void 0 : stats.resetDelta) || noop;
const rtcStats = {
    sendEvent: (type, value) => {
        server.trace("customEvent", null, {
            type,
            value,
        });
    },
    sendAudioMuted: (muted) => {
        rtcStats.sendEvent("audio_muted", { muted });
    },
    sendVideoMuted: (muted) => {
        rtcStats.sendEvent("video_muted", { muted });
    },
    server,
};

var _a$3;
const adapter$3 = (_a$3 = adapterRaw.default) !== null && _a$3 !== void 0 ? _a$3 : adapterRaw;
const logger$5 = new Logger();
const browserName$2 = adapter$3.browserDetails.browser;
const browserVersion$1 = adapter$3.browserDetails.version;
function setCodecPreferenceSDP(sdp, vp9On, redOn) {
    var _a, _b;
    try {
        const sdpObject = sdpTransform.parse(sdp);
        if (Array.isArray(sdpObject === null || sdpObject === void 0 ? void 0 : sdpObject.media)) {
            const mediaAudio = sdpObject.media.find((m) => m.type === "audio");
            if (Array.isArray(mediaAudio === null || mediaAudio === void 0 ? void 0 : mediaAudio.rtp)) {
                const rtp = mediaAudio.rtp;
                for (let i = 0; i < rtp.length; i++) {
                    if (redOn && rtp[i].codec === "red") {
                        const payloads = (_a = mediaAudio.payloads) === null || _a === void 0 ? void 0 : _a.split(" ");
                        const pt = payloads === null || payloads === void 0 ? void 0 : payloads.indexOf("" + rtp[i].payload);
                        if (pt && pt !== -1 && pt >= 0) {
                            payloads === null || payloads === void 0 ? void 0 : payloads.unshift(payloads.splice(pt, 1)[0]);
                            mediaAudio.payloads = payloads === null || payloads === void 0 ? void 0 : payloads.join(" ");
                        }
                    }
                }
            }
            const mediaVideo = sdpObject.media.find((m) => m.type === "video");
            if (Array.isArray(mediaVideo === null || mediaVideo === void 0 ? void 0 : mediaVideo.rtp)) {
                const rtp = mediaVideo.rtp;
                for (let i = 0; i < rtp.length; i++) {
                    if (vp9On && rtp[i].codec === "VP9") {
                        const payloads = (_b = mediaVideo.payloads) === null || _b === void 0 ? void 0 : _b.split(" ");
                        const pt = payloads === null || payloads === void 0 ? void 0 : payloads.indexOf("" + rtp[i].payload);
                        if (pt && pt !== -1 && pt >= 0) {
                            payloads === null || payloads === void 0 ? void 0 : payloads.unshift(payloads.splice(pt, 1)[0]);
                            mediaVideo.payloads = payloads === null || payloads === void 0 ? void 0 : payloads.join(" ");
                        }
                    }
                }
            }
        }
        const newSdp = sdpTransform.write(sdpObject);
        return newSdp;
    }
    catch (error) {
        logger$5.error("setCodecPreferenceSDP error:", error);
    }
}
function cleanSdp(sdp) {
    try {
        const sdpObject = sdpTransform.parse(sdp);
        sdpObject.media.forEach(mediaObject => {
            const usedPayloads = {};
            if (mediaObject.payloads)
                mediaObject.payloads = ("" + mediaObject.payloads).split(' ').filter(p => !usedPayloads[p] && (usedPayloads[p] = true)).join(' ');
            const usedRtps = {};
            mediaObject.rtp = mediaObject.rtp.filter(p => !p.payload || (usedPayloads[p.payload] && !usedRtps[p.payload] && (usedRtps[p.payload] = true)));
            const usedFmtps = {};
            if (mediaObject.fmtp)
                mediaObject.fmtp = mediaObject.fmtp.filter(p => !p.payload || (usedPayloads[p.payload] && !usedFmtps[p.payload] && (usedFmtps[p.payload] = true)));
            const usedRtcpFb = {};
            if (mediaObject.rtcpFb)
                mediaObject.rtcpFb = mediaObject.rtcpFb.filter(p => !p.payload || (usedPayloads[p.payload] && !usedRtcpFb[p.payload + p.type] && (usedRtcpFb[p.payload + p.type] = true)));
        });
        return sdpTransform.write(sdpObject);
    }
    catch (_) { }
    return sdp;
}
function maybeRejectNoH264(sdp) {
    if (browserName$2 !== "safari") {
        return sdp;
    }
    const sections = SDPUtils.splitSections(sdp);
    for (let i = 1; i < sections.length; i++) {
        if (SDPUtils.getKind(sections[i]) !== "video") {
            continue;
        }
        const codecs = SDPUtils.matchPrefix(sections[i], "a=rtpmap:")
            .map((line) => {
            return SDPUtils.parseRtpMap(line);
        })
            .map((codec) => {
            return codec.name.toUpperCase();
        });
        if (codecs.indexOf("H264") === -1 &&
            sections[i][8] === "9") {
            sections[i] = sections[i].replace("m=video 9 ", "m=video 0 ");
        }
    }
    return sections.join("");
}
function deprioritizeH264(sdp) {
    return SDPUtils.splitSections(sdp)
        .map((section) => {
        if (SDPUtils.getKind(section) !== "video")
            return section;
        const h264payloadTypes = SDPUtils.matchPrefix(section, "a=rtpmap:")
            .map((line) => SDPUtils.parseRtpMap(line))
            .filter((codec) => /h264/i.test(codec.name))
            .map((codec) => "" + codec.payloadType);
        if (!h264payloadTypes.length)
            return section;
        const mline = SDPUtils.matchPrefix(section, "m=video")[0];
        const mlinePayloadsSectionExec = /(\s\d+)+$/i.exec(mline);
        const mlinePayloadsSection = mlinePayloadsSectionExec ? mlinePayloadsSectionExec[0] : "";
        const mlinePayloadsNonH264 = mlinePayloadsSection
            .split(" ")
            .filter((payloadType) => payloadType && !h264payloadTypes.includes(payloadType));
        const reorderedPayloads = [...mlinePayloadsNonH264, ...h264payloadTypes].join(" ");
        const newmline = mline.replace(mlinePayloadsSection, " " + reorderedPayloads);
        return section.replace(mline, newmline);
    })
        .join("");
}
function replaceSSRCs(currentDescription, newDescription) {
    let ssrcs = currentDescription.match(/a=ssrc-group:FID (\d+) (\d+)\r\n/);
    let newssrcs = newDescription.match(/a=ssrc-group:FID (\d+) (\d+)\r\n/);
    if (!ssrcs) {
        ssrcs = currentDescription.match(/a=ssrc:(\d+) cname:(.*)\r\n/g)[1].match(/a=ssrc:(\d+)/);
        newssrcs = newDescription.match(/a=ssrc:(\d+) cname:(.*)\r\n/g)[1].match(/a=ssrc:(\d+)/);
    }
    for (let i = 1; i < ssrcs.length; i++) {
        newDescription = newDescription.replace(new RegExp(newssrcs[i], "g"), ssrcs[i]);
    }
    return newDescription;
}
function filterMidExtension(sdp) {
    if (browserName$2 !== "safari" &&
        (browserName$2 !== "firefox" || (browserVersion$1 && browserVersion$1 >= 63) || browserVersion$1 === 60)) {
        return sdp;
    }
    return (SDPUtils.splitLines(sdp.trim())
        .filter((line) => {
        if (!line.startsWith("a=extmap:")) {
            return true;
        }
        const extmap = SDPUtils.parseExtmap(line);
        return extmap.uri !== "urn:ietf:params:rtp-hdrext:sdes:mid";
    })
        .join("\r\n") + "\r\n");
}
function filterMsidSemantic(sdp) {
    if (browserName$2 !== "firefox") {
        return sdp;
    }
    return (SDPUtils.splitLines(sdp.trim())
        .map((line) => (line.startsWith("a=msid-semantic:") ? "a=msid-semantic: WMS *" : line))
        .join("\r\n") + "\r\n");
}
function changeMediaDirection(sdp, active) {
    const sections = SDPUtils.splitSections(sdp);
    return (sections.shift() +
        sections
            .map((section) => {
            const currentDirection = SDPUtils.getDirection(section, SDPUtils.getKind(section));
            return section.replace("a=" + currentDirection, "a=" + (active ? "recvonly" : "inactive"));
        })
            .join(""));
}
function addExtMap(sdp, extmapUri, modifyAudio = false, modifyVideo = false) {
    var _a, _b;
    try {
        const sdpObj = sdpTransform.parse(sdp);
        if (((_a = sdpObj === null || sdpObj === void 0 ? void 0 : sdpObj.ext) === null || _a === void 0 ? void 0 : _a.length) && sdpObj.ext.length > 0) {
            return sdp;
        }
        if ((sdpObj === null || sdpObj === void 0 ? void 0 : sdpObj.media.length) < 1)
            return sdp;
        const allHeaderExtensions = sdpObj === null || sdpObj === void 0 ? void 0 : sdpObj.media.flatMap((section) => section.ext || []);
        const extmapId = ((_b = allHeaderExtensions.find((ext) => ext.uri === extmapUri)) === null || _b === void 0 ? void 0 : _b.value) ||
            [...new Set([0, 15, ...allHeaderExtensions.map((ext) => ext.value)])]
                .sort((a, b) => a - b)
                .find((n, i, arr) => n + 1 !== arr[i + 1]) + 1;
        sdpObj.media.forEach((mediaSection) => {
            var _a;
            if ((modifyAudio && mediaSection.type === "audio") || (modifyVideo && mediaSection.type === "video")) {
                if (!((_a = mediaSection.ext) === null || _a === void 0 ? void 0 : _a.find((e) => e.uri === extmapUri))) {
                    if (Array.isArray(mediaSection.ext)) {
                        mediaSection["ext"].push({ value: extmapId, uri: extmapUri });
                    }
                    else {
                        mediaSection["ext"] = [{ value: extmapId, uri: extmapUri }];
                    }
                }
            }
        });
        return sdpTransform.write(sdpObj);
    }
    catch (error) {
        console.error("Error during addAbsCaptureTimeExtMap: ", error);
    }
    return sdp;
}
function addAbsCaptureTimeExtMap(sdp) {
    const absCaptureTimeUri = "http://www.webrtc.org/experiments/rtp-hdrext/abs-capture-time";
    return addExtMap(sdp, absCaptureTimeUri, true, true);
}

function isRelayed(pc) {
    return pc.getStats(null).then((result) => {
        let localCandidateType;
        let remoteCandidateType;
        result.forEach((report) => {
            if (report.type === "transport" && report.selectedCandidatePairId) {
                const transport = result.get(report.selectedCandidatePairId);
                if (!transport) {
                    return;
                }
                localCandidateType = result.get(transport.localCandidateId).candidateType;
                remoteCandidateType = result.get(transport.remoteCandidateId).candidateType;
                return;
            }
            if (report.type === "candidate-pair" && report.selected) {
                localCandidateType = result.get(report.localCandidateId).candidateType;
                remoteCandidateType = result.get(report.remoteCandidateId).candidateType;
            }
        });
        return (localCandidateType === "relay" ||
            localCandidateType === "relayed" ||
            remoteCandidateType === "relay" ||
            remoteCandidateType === "relayed");
    });
}

function setVideoBandwidthUsingSetParameters(pc, bandwidth, logger = console) {
    const sender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
    if (!sender) {
        return Promise.resolve();
    }
    const parameters = sender.getParameters();
    if (parameters.encodings && parameters.encodings.length === 0) {
        return Promise.resolve();
    }
    if (!parameters.encodings) {
        parameters.encodings = [{}];
    }
    if (bandwidth === 0) {
        delete parameters.encodings[0].maxBitrate;
    }
    else {
        parameters.encodings[0].maxBitrate = bandwidth * 1000;
    }
    return sender.setParameters(parameters).catch((err) => {
        logger.error("setParameters err: ", err);
    });
}

var _a$2;
const adapter$2 = (_a$2 = adapterRaw.default) !== null && _a$2 !== void 0 ? _a$2 : adapterRaw;
const logger$4 = new Logger();
class Session {
    constructor({ peerConnectionId, bandwidth, maximumTurnBandwidth, deprioritizeH264Encoding, }) {
        this.peerConnectionId = peerConnectionId;
        this.relayCandidateSeen = false;
        this.serverReflexiveCandidateSeen = false;
        this.publicHostCandidateSeen = false;
        this.ipv6HostCandidateSeen = false;
        this.ipv6HostCandidateTeredoSeen = false;
        this.ipv6HostCandidate6to4Seen = false;
        this.mdnsHostCandidateSeen = false;
        this.pc = null;
        this.wasEverConnected = false;
        this.connectionStatus = null;
        this.stats = {
            totalSent: 0,
            totalRecv: 0,
        };
        this.bandwidth = bandwidth || 0;
        this.maximumTurnBandwidth = maximumTurnBandwidth;
        this.pending = [];
        this.isOperationPending = false;
        this.streamIds = [];
        this.streams = [];
        this.earlyIceCandidates = [];
        this.afterConnected = new Promise((resolve) => {
            this.registerConnected = resolve;
        });
        this.offerOptions = { offerToReceiveAudio: true, offerToReceiveVideo: true };
        this._deprioritizeH264Encoding = deprioritizeH264Encoding;
    }
    setAndGetPeerConnection({ clientId, constraints, peerConnectionConfig, shouldAddLocalVideo, }) {
        this.peerConnectionConfig = peerConnectionConfig;
        this.shouldAddLocalVideo = shouldAddLocalVideo;
        this.clientId = clientId;
        this.pc = new RTCPeerConnection(peerConnectionConfig);
        this.signalingState = this.pc.signalingState;
        this.pc.addEventListener("signalingstatechange", () => {
            if (this.signalingState === this.pc.signalingState) {
                return;
            }
            this.signalingState = this.pc.signalingState;
            if (this.pc.signalingState === "stable") {
                this.isOperationPending = false;
                const action = this.pending.shift();
                if (action) {
                    action.apply();
                }
            }
        });
        return this.pc;
    }
    addStream(stream) {
        this.streamIds.push(stream.id);
        this.streams.push(stream);
        if (RTCPeerConnection.prototype.addTrack) {
            stream.getAudioTracks().forEach((track) => {
                this.pc.addTrack(track, stream);
            });
            stream.getVideoTracks().forEach((track) => {
                this.pc.addTrack(track, stream);
            });
        }
        else {
            this.pc.addStream(stream);
        }
    }
    addTrack(track, stream) {
        if (!stream) {
            stream = this.streams[0];
        }
        stream === null || stream === void 0 ? void 0 : stream.addTrack(track);
        this.pc.addTrack(track, stream);
    }
    removeTrack(track) {
        const stream = this.streams[0];
        stream.removeTrack(track);
        const sender = this.pc.getSenders().find((sender) => sender.track === track);
        if (sender) {
            this.pc.removeTrack(sender);
        }
    }
    removeStream(stream) {
        for (let i = 0; i < this.streamIds.length; i++) {
            if (this.streamIds[i] === stream.id) {
                this.streamIds.splice(i, 1);
                this.streams.splice(i, 1);
            }
        }
        if (this.pc) {
            if (this.pc.removeTrack) {
                stream.getTracks().forEach((track) => {
                    const sender = this.pc.getSenders().find((sender) => sender.track === track);
                    if (sender) {
                        this.pc.removeTrack(sender);
                    }
                });
            }
            else if (this.pc.removeStream) {
                this.pc.removeStream(stream);
            }
        }
    }
    _setRemoteDescription(desc) {
        if (this._deprioritizeH264Encoding)
            desc.sdp = deprioritizeH264(desc.sdp);
        this.srdComplete = this.pc.setRemoteDescription(desc);
        return this.srdComplete.then(() => {
            this.earlyIceCandidates.forEach((candidate) => this.pc.addIceCandidate(candidate));
            this.earlyIceCandidates = [];
        });
    }
    handleOffer(message) {
        if (!this.canModifyPeerConnection()) {
            return new Promise((resolve) => {
                this.pending.push(() => this.handleOffer(message).then(resolve));
            });
        }
        this.isOperationPending = true;
        let sdp = message.sdp;
        sdp = filterMidExtension(sdp);
        sdp = filterMsidSemantic(sdp);
        const desc = { type: message.type, sdp };
        let answerToSignal;
        return this._setRemoteDescription(desc)
            .then(() => {
            return this.pc.createAnswer();
        })
            .then((answer) => {
            answerToSignal = answer;
            return this.pc.setLocalDescription(answer);
        })
            .then(() => {
            return setVideoBandwidthUsingSetParameters(this.pc, this.bandwidth);
        })
            .then(() => {
            return answerToSignal;
        });
    }
    handleAnswer(message) {
        const sdp = filterMsidSemantic(message.sdp);
        const desc = { type: message.type, sdp };
        return this._setRemoteDescription(desc).then(() => {
            return setVideoBandwidthUsingSetParameters(this.pc, this.bandwidth);
        }, (e) => {
            logger$4.warn("Could not set remote description from remote answer: ", e);
        });
    }
    addIceCandidate(candidate) {
        if (!this.srdComplete) {
            this.earlyIceCandidates.push(candidate);
            return;
        }
        this.srdComplete.then(() => {
            if (this.pc.signalingState === "closed") {
                return;
            }
            if (adapter$2.browserDetails.browser === "safari" && candidate && candidate.candidate === "") {
                return;
            }
            this.pc.addIceCandidate(candidate).catch((e) => {
                logger$4.warn("Failed to add ICE candidate ('%s'): %s", candidate ? candidate.candidate : null, e);
            });
        });
    }
    canModifyPeerConnection() {
        return this.pc.signalingState === "stable" && !this.isOperationPending;
    }
    close() {
        const pc = this.pc;
        if (!pc) {
            return;
        }
        pc.oniceconnectionstatechange = null;
        pc.onicecandidate = null;
        pc.ontrack = null;
        try {
            pc.close();
        }
        catch (e) {
            logger$4.warn("failures during close of session", e);
        }
    }
    hasConnectedPeerConnection() {
        return this.pc && this.pc.connectionState === "connected";
    }
    replaceTrack(oldTrack, newTrack) {
        const pc = this.pc;
        if (!pc)
            return false;
        const senders = pc.getSenders();
        if (!oldTrack) {
            oldTrack = (senders.find((s) => s.track && s.track.kind === newTrack.kind) || {}).track;
        }
        if (window.RTCRtpSender && window.RTCRtpSender.prototype.replaceTrack) {
            if (oldTrack) {
                const process = () => {
                    for (let i = 0; i < senders.length; i++) {
                        const sender = senders[i];
                        const track = sender.track;
                        if ((track === null || track === void 0 ? void 0 : track.id) === newTrack.id) {
                            return Promise.resolve(newTrack);
                        }
                        if ((track === null || track === void 0 ? void 0 : track.id) === oldTrack.id) {
                            return senders[i].replaceTrack(newTrack);
                        }
                    }
                    return null;
                };
                let result = process();
                if (result) {
                    return result;
                }
                let resolve = null;
                let reject = null;
                result = new Promise((_resolve, _reject) => {
                    resolve = _resolve;
                    reject = _reject;
                });
                let retried = 0;
                let timer = setInterval(() => __awaiter(this, void 0, void 0, function* () {
                    const trackReplacedPromise = process();
                    if (!trackReplacedPromise) {
                        if (3 < ++retried) {
                            clearInterval(timer);
                            timer = null;
                            reject("No sender track to replace");
                        }
                        return;
                    }
                    clearInterval(timer);
                    timer = null;
                    const trackReplaced = yield trackReplacedPromise;
                    resolve(trackReplaced);
                }), 1000);
                return result;
            }
            const stream = this.streams.find((s) => s.getTracks().find((t) => t.id === newTrack.id)) || this.streams[0];
            if (!stream) {
                return Promise.reject(new Error("replaceTrack: No stream?"));
            }
            return pc.addTrack(newTrack, stream);
        }
        if (!this.canModifyPeerConnection()) {
            this.pending.push(() => {
                this.replaceTrack(oldTrack, newTrack);
            });
            return;
        }
        this.isOperationPending = true;
        const onn = pc.onnegotiationneeded;
        pc.onnegotiationneeded = null;
        this.removeTrack(oldTrack);
        this.addTrack(newTrack);
        setTimeout(() => {
            pc.onnegotiationneeded = onn;
        }, 0);
        if (pc.localDescription.type === "offer") {
            return pc
                .createOffer()
                .then((offer) => {
                offer.sdp = replaceSSRCs(pc.localDescription.sdp, offer.sdp);
                return pc.setLocalDescription(offer);
            })
                .then(() => {
                return this._setRemoteDescription(pc.remoteDescription);
            });
        }
        else {
            return this._setRemoteDescription(pc.remoteDescription)
                .then(() => {
                return pc.createAnswer();
            })
                .then((answer) => {
                answer.sdp = replaceSSRCs(pc.localDescription.sdp, answer.sdp);
                return pc.setLocalDescription(answer);
            });
        }
    }
    maybeRestrictRelayBandwidth() {
        if (this.maximumTurnBandwidth === MAXIMUM_TURN_BANDWIDTH_UNLIMITED) {
            return;
        }
        if (!this.pc.getStats) {
            return;
        }
        isRelayed(this.pc).then((isRelayed) => {
            if (isRelayed && this.bandwidth === 0) {
                this.changeBandwidth(this.maximumTurnBandwidth);
            }
        });
    }
    changeBandwidth(bandwidth) {
        if (bandwidth === this.bandwidth) {
            return;
        }
        if (!this.canModifyPeerConnection()) {
            this.pending.push(() => this.changeBandwidth(bandwidth));
            return;
        }
        this.bandwidth = bandwidth;
        if (!this.pc.localDescription || this.pc.localDescription.type === "") {
            return;
        }
        setVideoBandwidthUsingSetParameters(this.pc, this.bandwidth);
    }
    setAudioOnly(enable, excludedTrackIds = []) {
        this.pc
            .getTransceivers()
            .filter((videoTransceiver) => {
            var _a, _b, _c, _d, _e, _f;
            return (videoTransceiver === null || videoTransceiver === void 0 ? void 0 : videoTransceiver.direction) !== "recvonly" &&
                ((_b = (_a = videoTransceiver === null || videoTransceiver === void 0 ? void 0 : videoTransceiver.receiver) === null || _a === void 0 ? void 0 : _a.track) === null || _b === void 0 ? void 0 : _b.kind) === "video" &&
                !excludedTrackIds.includes((_d = (_c = videoTransceiver === null || videoTransceiver === void 0 ? void 0 : videoTransceiver.receiver) === null || _c === void 0 ? void 0 : _c.track) === null || _d === void 0 ? void 0 : _d.id) &&
                !excludedTrackIds.includes((_f = (_e = videoTransceiver === null || videoTransceiver === void 0 ? void 0 : videoTransceiver.sender) === null || _e === void 0 ? void 0 : _e.track) === null || _f === void 0 ? void 0 : _f.id);
        })
            .forEach((videoTransceiver) => {
            videoTransceiver.direction = enable ? "sendonly" : "sendrecv";
        });
    }
}

const EVENTS = {
    CLIENT_CONNECTION_STATUS_CHANGED: "client_connection_status_changed",
    STREAM_ADDED: "stream_added",
    RTC_MANAGER_CREATED: "rtc_manager_created",
    RTC_MANAGER_DESTROYED: "rtc_manager_destroyed",
    LOCAL_STREAM_TRACK_ADDED: "local_stream_track_added",
    LOCAL_STREAM_TRACK_REMOVED: "local_stream_track_removed",
    REMOTE_STREAM_TRACK_ADDED: "remote_stream_track_added",
    REMOTE_STREAM_TRACK_REMOVED: "remote_stream_track_removed",
};
const TYPES = {
    CONNECTING: "connecting",
    CONNECTION_FAILED: "connection_failed",
    CONNECTION_SUCCESSFUL: "connection_successful",
    CONNECTION_DISCONNECTED: "connection_disconnected",
};

const CAMERA_STREAM_ID$1 = "0";
const STREAM_TYPES = {
    CAMERA: "camera",
    SCREEN_SHARE: "screen_share",
};
class RtcStream {
    constructor(id, type) {
        this.id = "" + id;
        this.type = type;
        this.isEnabled = true;
        this.hasSupportForAutoSuperSize = false;
        this.isAudioEnabled = true;
        this.isVideoEnabled = true;
        this.status = TYPES.CONNECTING;
        this.stream = null;
        this.streamId = null;
    }
    setup(stream) {
        this.stream = stream;
        this.streamId = stream.id;
        this.setVideoEnabled(this.isVideoEnabled && stream.getVideoTracks().length > 0);
        this.setAudioEnabled(this.isAudioEnabled && stream.getAudioTracks().length > 0);
        return this;
    }
    setStatus(status) {
        this.status = status;
        return this;
    }
    setVideoEnabled(isEnabled) {
        this.isVideoEnabled = isEnabled;
        if (!this.stream) {
            return;
        }
        this.stream.getVideoTracks().forEach((track) => {
            track.enabled = isEnabled;
        });
    }
    setAudioEnabled(isEnabled) {
        this.isAudioEnabled = isEnabled;
        if (!this.stream) {
            return;
        }
        this.stream.getAudioTracks().forEach((track) => {
            track.enabled = isEnabled;
        });
    }
    static getCameraId() {
        return CAMERA_STREAM_ID$1;
    }
    static getTypeFromId(id) {
        const streamId = "" + id;
        return streamId === CAMERA_STREAM_ID$1 ? STREAM_TYPES.CAMERA : STREAM_TYPES.SCREEN_SHARE;
    }
}

var rtcManagerEvents = {
    CAMERA_NOT_WORKING: "camera_not_working",
    CONNECTION_BLOCKED_BY_NETWORK: "connection_blocked_by_network",
    ICE_IPV6_SEEN: "ice_ipv6_seen",
    ICE_MDNS_SEEN: "ice_mdns_seen",
    ICE_NO_PUBLIC_IP_GATHERED: "ice_no_public_ip_gathered",
    ICE_NO_PUBLIC_IP_GATHERED_3SEC: "ice_no_public_ip_gathered_3sec",
    ICE_RESTART: "ice_restart",
    MICROPHONE_NOT_WORKING: "microphone_not_working",
    MICROPHONE_STOPPED_WORKING: "microphone_stopped_working",
    CAMERA_STOPPED_WORKING: "camera_stopped_working",
    NEW_PC: "new_pc",
    SFU_CONNECTION_OPEN: "sfu_connection_open",
    SFU_CONNECTION_CLOSED: "sfu_connection_closed",
    COLOCATION_SPEAKER: "colocation_speaker",
    DOMINANT_SPEAKER: "dominant_speaker",
    PC_SLD_FAILURE: "pc_sld_failure",
    PC_ON_ANSWER_FAILURE: "pc_on_answer_failure",
    PC_ON_OFFER_FAILURE: "pc_on_offer_failure",
};

var _a$1, _b;
const adapter$1 = (_a$1 = adapterRaw.default) !== null && _a$1 !== void 0 ? _a$1 : adapterRaw;
const logger$3 = new Logger();
const ICE_PUBLIC_IP_GATHERING_TIMEOUT = 3 * 1000;
const CAMERA_STREAM_ID = RtcStream.getCameraId();
const browserName$1 = adapter$1.browserDetails.browser;
const browserVersion = adapter$1.browserDetails.version;
if (browserName$1 === "firefox" && adapter$1.browserShim && "shimGetDisplayMedia" in adapter$1.browserShim) {
    (_b = adapter$1.browserShim) === null || _b === void 0 ? void 0 : _b.shimGetDisplayMedia(window, "screen");
}
let unloading$1 = false;
if (browserName$1 === "chrome") {
    window.document.addEventListener("beforeunload", () => {
        unloading$1 = true;
    });
}
class P2pRtcManager {
    constructor({ selfId, room, emitter, serverSocket, webrtcProvider, features, }) {
        const { name, session, iceServers, sfuServer, mediaserverConfigTtlSeconds } = room;
        this._selfId = selfId;
        this._roomName = name;
        this._roomSessionId = session && session.id;
        this.peerConnections = {};
        this.localStreams = {};
        this.enabledLocalStreamIds = [];
        this._screenshareVideoTrackIds = [];
        this._socketListenerDeregisterFunctions = [];
        this._localStreamDeregisterFunction = null;
        this._emitter = emitter;
        this._serverSocket = serverSocket;
        this._webrtcProvider = webrtcProvider;
        this._features = features || {};
        this._isAudioOnlyMode = false;
        this.offerOptions = { offerToReceiveAudio: true, offerToReceiveVideo: true };
        this._pendingActionsForConnectedPeerConnections = [];
        this._audioTrackOnEnded = () => {
            rtcStats.sendEvent("audio_ended", { unloading: unloading$1 });
            this._emit(rtcManagerEvents.MICROPHONE_STOPPED_WORKING, {});
        };
        this._videoTrackOnEnded = () => {
            rtcStats.sendEvent("video_ended", { unloading: unloading$1 });
            this._emit(rtcManagerEvents.CAMERA_STOPPED_WORKING, {});
        };
        this._updateAndScheduleMediaServersRefresh({
            sfuServer,
            iceServers: iceServers.iceServers || [],
            mediaserverConfigTtlSeconds,
        });
        this.totalSessionsCreated = 0;
    }
    numberOfPeerconnections() {
        return Object.keys(this.peerConnections).length;
    }
    isInitializedWith({ selfId, roomName, isSfu }) {
        return this._selfId === selfId && this._roomName === roomName && isSfu === !!this._sfuServer;
    }
    supportsScreenShareAudio() {
        return true;
    }
    maybeRestrictRelayBandwidth(session) {
        session.maybeRestrictRelayBandwidth();
    }
    addNewStream(streamId, stream, audioPaused, videoPaused, beforeEffectTracks = []) {
        if (stream === this.localStreams[streamId]) {
            return;
        }
        this._addLocalStream(streamId, stream);
        if (streamId === CAMERA_STREAM_ID) {
            this._addStreamToPeerConnections(stream);
            const audioTrack = stream.getAudioTracks()[0];
            const videoTrack = stream.getVideoTracks()[0];
            if (audioTrack) {
                if (!audioTrack.effectTrack) {
                    this._monitorAudioTrack(audioTrack);
                }
                const beforeEffectTrack = beforeEffectTracks.find((t) => t.kind === "audio");
                if (beforeEffectTrack) {
                    this._monitorAudioTrack(beforeEffectTrack);
                }
            }
            if (videoTrack) {
                if (!videoTrack.effectTrack) {
                    this._monitorVideoTrack(videoTrack);
                }
                const beforeEffectTrack = beforeEffectTracks.find((t) => t.kind === "video");
                if (beforeEffectTrack) {
                    this._monitorVideoTrack(beforeEffectTrack);
                }
            }
            if (this._localStreamDeregisterFunction) {
                this._localStreamDeregisterFunction();
                this._localStreamDeregisterFunction = null;
            }
            const localStreamHandler = (e) => {
                const { enable, track } = e.detail;
                this._handleStopOrResumeVideo({ enable, track });
            };
            stream.addEventListener("stopresumevideo", localStreamHandler);
            this._localStreamDeregisterFunction = () => {
                stream.removeEventListener("stopresumevideo", localStreamHandler);
            };
            return;
        }
        this._screenshareVideoTrackIds.push(stream.getVideoTracks()[0].id);
        this._shareScreen(streamId, stream);
        return;
    }
    replaceTrack(oldTrack, newTrack) {
        if (newTrack.kind === "audio" && !newTrack.effectTrack) {
            this._monitorAudioTrack(newTrack);
        }
        if (newTrack.kind === "video" && !newTrack.effectTrack) {
            this._monitorVideoTrack(newTrack);
        }
        return this._replaceTrackToPeerConnections(oldTrack, newTrack);
    }
    disconnectAll() {
        Object.keys(this.peerConnections).forEach((peerConnectionId) => {
            this.disconnect(peerConnectionId);
        });
        this.peerConnections = {};
        this._socketListenerDeregisterFunctions.forEach((func) => {
            func();
        });
        this._socketListenerDeregisterFunctions = [];
        if (this._localStreamDeregisterFunction) {
            this._localStreamDeregisterFunction();
            this._localStreamDeregisterFunction = null;
        }
    }
    fixChromeAudio(constraints) {
        if (browserName$1 !== "chrome") {
            return;
        }
        const localStream = this._getLocalCameraStream();
        const audioTrack = localStream.getAudioTracks()[0];
        if (!audioTrack || audioTrack.readyState !== "ended") {
            return;
        }
        return navigator.mediaDevices.getUserMedia({ audio: constraints }).then((stream) => {
            const track = stream.getAudioTracks()[0];
            track.enabled = audioTrack.enabled;
            localStream.removeTrack(audioTrack);
            localStream.addTrack(track);
            return this.replaceTrack(audioTrack, track);
        });
    }
    setupSocketListeners() {
        this._socketListenerDeregisterFunctions = [
            () => this._clearMediaServersRefresh(),
            this._serverSocket.on(PROTOCOL_RESPONSES.MEDIASERVER_CONFIG, (data) => {
                if (data.error) {
                    logger$3.warn("FETCH_MEDIASERVER_CONFIG failed:", data.error);
                    return;
                }
                this._updateAndScheduleMediaServersRefresh(data);
            }),
            this._serverSocket.on(RELAY_MESSAGES.READY_TO_RECEIVE_OFFER, (data) => {
                this._connect(data.clientId);
            }),
            this._serverSocket.on(RELAY_MESSAGES.ICE_CANDIDATE, (data) => {
                const session = this._getSession(data.clientId);
                if (!session) {
                    logger$3.warn("No RTCPeerConnection on ICE_CANDIDATE", data);
                    return;
                }
                session.addIceCandidate(data.message);
            }),
            this._serverSocket.on(RELAY_MESSAGES.ICE_END_OF_CANDIDATES, (data) => {
                const session = this._getSession(data.clientId);
                if (!session) {
                    logger$3.warn("No RTCPeerConnection on ICE_END_OF_CANDIDATES", data);
                    return;
                }
                session.addIceCandidate(null);
            }),
            this._serverSocket.on(RELAY_MESSAGES.SDP_OFFER, (data) => {
                var _a, _b;
                const session = this._getSession(data.clientId);
                if (!session) {
                    logger$3.warn("No RTCPeerConnection on SDP_OFFER", data);
                    return;
                }
                const offer = this._transformIncomingSdp(data.message, session.pc);
                (_b = (_a = session
                    .handleOffer(offer)
                    .then((answer) => {
                    this._emitServerEvent(RELAY_MESSAGES.SDP_ANSWER, {
                        receiverId: data.clientId,
                        message: this._transformOutgoingSdp(answer),
                    });
                })).catch) === null || _b === void 0 ? void 0 : _b.call(_a, (e) => {
                    this._emit(rtcManagerEvents.PC_ON_OFFER_FAILURE, e);
                });
            }),
            this._serverSocket.on(RELAY_MESSAGES.SDP_ANSWER, (data) => {
                var _a, _b;
                const session = this._getSession(data.clientId);
                if (!session) {
                    logger$3.warn("No RTCPeerConnection on SDP_ANSWER", data);
                    return;
                }
                const answer = this._transformIncomingSdp(data.message, session.pc);
                (_b = (_a = session.handleAnswer(answer)) === null || _a === void 0 ? void 0 : _a.catch) === null || _b === void 0 ? void 0 : _b.call(_a, (e) => {
                    this._emit(rtcManagerEvents.PC_ON_ANSWER_FAILURE, e);
                });
            }),
            this._serverSocket.on(PROTOCOL_RESPONSES.ROOM_JOINED, ({ room: { sfuServer: isSfu } }) => {
                if (isSfu || !this._wasScreenSharing)
                    return;
                const screenShareStreamId = Object.keys(this.localStreams).find((id) => id !== CAMERA_STREAM_ID);
                if (!screenShareStreamId) {
                    return;
                }
                const screenshareStream = this.localStreams[screenShareStreamId];
                if (!screenshareStream) {
                    logger$3.warn("screenshare stream %s not found", screenShareStreamId);
                    return;
                }
                const hasAudioTrack = screenshareStream.getAudioTracks().length > 0;
                this._emitServerEvent(PROTOCOL_REQUESTS.START_SCREENSHARE, {
                    streamId: screenShareStreamId,
                    hasAudioTrack,
                });
            }),
        ];
    }
    sendAudioMutedStats(muted) {
        rtcStats.sendEvent("audio_muted", { muted });
    }
    sendVideoMutedStats(muted) {
        rtcStats.sendEvent("video_muted", { muted });
    }
    sendStatsCustomEvent(eventName, data) {
        rtcStats.sendEvent(eventName, data);
    }
    rtcStatsDisconnect() {
        rtcStats.server.close();
    }
    rtcStatsReconnect() {
        if (!rtcStats.server.connected) {
            rtcStats.server.connect();
        }
    }
    setAudioOnly(audioOnly) {
        this._isAudioOnlyMode = audioOnly;
        this._forEachPeerConnection((session) => {
            if (session.hasConnectedPeerConnection()) {
                this._withForcedRenegotiation(session, () => session.setAudioOnly(this._isAudioOnlyMode, this._screenshareVideoTrackIds));
            }
        });
    }
    setRemoteScreenshareVideoTrackIds(remoteScreenshareVideoTrackIds = []) {
        const localScreenshareStream = this._getFirstLocalNonCameraStream();
        this._screenshareVideoTrackIds = [
            ...((localScreenshareStream === null || localScreenshareStream === void 0 ? void 0 : localScreenshareStream.track) ? [localScreenshareStream.track.id] : []),
            ...remoteScreenshareVideoTrackIds,
        ];
    }
    setRoomSessionId(roomSessionId) {
        this._roomSessionId = roomSessionId;
    }
    _setConnectionStatus(session, newStatus, clientId) {
        const previousStatus = session.connectionStatus;
        if (previousStatus === newStatus) {
            return;
        }
        if (session.peerConnectionId === this._selfId) {
            return;
        }
        if (previousStatus === TYPES.CONNECTION_DISCONNECTED &&
            newStatus === TYPES.CONNECTING) {
            return;
        }
        session.connectionStatus = newStatus;
        setTimeout(() => {
            this._emit(EVENTS.CLIENT_CONNECTION_STATUS_CHANGED, {
                streamIds: session.streamIds,
                clientId,
                status: newStatus,
                previous: previousStatus,
            });
        }, 0);
    }
    _setJitterBufferTarget(pc) {
        try {
            const receivers = pc.getReceivers();
            receivers.forEach((receiver) => {
                receiver.jitterBufferTarget = MEDIA_JITTER_BUFFER_TARGET;
                receiver.playoutDelayHint = MEDIA_JITTER_BUFFER_TARGET / 1000;
            });
        }
        catch (error) {
            logger$3.error("Error during setting jitter buffer target:", error);
        }
    }
    _emitServerEvent(eventName, data, callback) {
        this._serverSocket.emit(eventName, data, callback);
    }
    _emit(eventName, data) {
        this._emitter.emit(eventName, data);
    }
    _addEnabledLocalStreamId(streamId) {
        this.enabledLocalStreamIds.push(streamId);
    }
    _deleteEnabledLocalStreamId(streamId) {
        const index = this.enabledLocalStreamIds.indexOf(streamId);
        if (index !== -1) {
            this.enabledLocalStreamIds.splice(index, 1);
        }
    }
    _getSession(peerConnectionId) {
        if (!(peerConnectionId in this.peerConnections)) {
            return null;
        }
        return this.peerConnections[peerConnectionId];
    }
    _getOrCreateSession(peerConnectionId, initialBandwidth) {
        let session = this.peerConnections[peerConnectionId];
        if (session === undefined) {
            const deprioritizeH264Encoding = browserName$1 === "safari" &&
                browserVersion &&
                browserVersion >= 14 &&
                this._features.deprioritizeH264OnSafari;
            this.peerConnections[peerConnectionId] = session = new Session({
                peerConnectionId,
                bandwidth: initialBandwidth,
                maximumTurnBandwidth: this._features.unlimitedBandwidthWhenUsingRelayP2POn
                    ? MAXIMUM_TURN_BANDWIDTH_UNLIMITED
                    : MAXIMUM_TURN_BANDWIDTH,
                deprioritizeH264Encoding,
            });
            this.totalSessionsCreated++;
        }
        return session;
    }
    _getLocalCameraStream() {
        return this.localStreams[CAMERA_STREAM_ID];
    }
    _getNonLocalCameraStreamIds() {
        return Object.keys(this.localStreams).filter((streamId) => streamId !== CAMERA_STREAM_ID);
    }
    _isScreensharingLocally() {
        return this._getNonLocalCameraStreamIds().length > 0;
    }
    _getFirstLocalNonCameraStream() {
        const streamIds = this._getNonLocalCameraStreamIds();
        return streamIds.length === 0 ? null : this.localStreams[streamIds[0]];
    }
    _transformIncomingSdp(original, _) {
        return { type: original.type, sdp: original.sdpU };
    }
    _transformOutgoingSdp(original) {
        return { type: original.type, sdpU: original.sdp };
    }
    _createSession({ clientId, initialBandwidth, isOfferer, peerConnectionId, shouldAddLocalVideo, }) {
        if (!peerConnectionId) {
            throw new Error("peerConnectionId is missing");
        }
        if (!clientId) {
            throw new Error("clientId is missing");
        }
        const session = this._getOrCreateSession(peerConnectionId, initialBandwidth);
        const constraints = { optional: [] };
        if (browserName$1 === "chrome") {
            constraints.optional.push({
                googCpuOveruseDetection: true,
            });
        }
        constraints.optional.push({ rtcStatsRoomSessionId: this._roomSessionId });
        constraints.optional.push({ rtcStatsClientId: this._selfId });
        constraints.optional.push({ rtcStatsPeerId: peerConnectionId });
        constraints.optional.push({ rtcStatsConferenceId: this._roomName });
        const peerConnectionConfig = {
            iceServers: this._iceServers,
        };
        if (this._features.turnServerOverrideHost) {
            const host = this._features.turnServerOverrideHost;
            const port = host.indexOf(":") > 0 ? "" : ":443";
            const override = ":" + host + port;
            peerConnectionConfig.iceServers = peerConnectionConfig.iceServers.map((original) => {
                const entry = Object.assign({}, original);
                if (entry.url) {
                    entry.url = entry.url.replace(/:[^?]*/, override);
                }
                if (entry.urls) {
                    entry.urls = entry.urls.map((url) => url.replace(/:[^?]*/, override));
                }
                return entry;
            });
        }
        if (this._features.addGoogleStunServers) {
            peerConnectionConfig.iceServers = [
                ...peerConnectionConfig.iceServers,
                { urls: "stun:stun.l.google.com:19302" },
                { urls: "stun:stun2.l.google.com:19302" },
            ];
        }
        if (this._features.addCloudflareStunServers) {
            peerConnectionConfig.iceServers = [
                ...peerConnectionConfig.iceServers,
                { urls: "stun:stun.cloudflare.com:3478" },
                { urls: "stun:stun.cloudflare.com:53" },
            ];
        }
        if (this._features.useOnlyTURN) {
            peerConnectionConfig.iceTransportPolicy = "relay";
            const filter = {
                onlyudp: /^turn:.*transport=udp$/,
                onlytcp: /^turn:.*transport=tcp$/,
                onlytls: /^turns:.*transport=tcp$/,
            }[this._features.useOnlyTURN];
            if (filter) {
                peerConnectionConfig.iceServers = peerConnectionConfig.iceServers.filter((entry) => entry.url && entry.url.match(filter));
            }
        }
        if (browserName$1 === "chrome") {
            peerConnectionConfig.sdpSemantics = "unified-plan";
        }
        const pc = session.setAndGetPeerConnection({
            constraints,
            peerConnectionConfig,
            shouldAddLocalVideo,
            clientId,
        });
        setTimeout(() => this._emit(rtcManagerEvents.NEW_PC), 0);
        pc.ontrack = (event) => {
            const stream = event.streams[0];
            if (stream.id === "default" && stream.getAudioTracks().length === 0) {
                return;
            }
            if (session.streamIds.indexOf(stream.id) === -1) {
                session.streamIds.push(stream.id);
                this._emit(EVENTS.STREAM_ADDED, {
                    clientId,
                    stream,
                });
                if (session.connectionStatus === TYPES.CONNECTION_SUCCESSFUL) {
                    setTimeout(() => {
                        this._emit(EVENTS.CLIENT_CONNECTION_STATUS_CHANGED, {
                            streamIds: session.streamIds,
                            clientId,
                            status: session.connectionStatus,
                            previous: TYPES.CONNECTING,
                        });
                    }, 0);
                }
            }
        };
        pc.oniceconnectionstatechange = () => {
            let newStatus;
            const currentStatus = session.connectionStatus;
            switch (pc.iceConnectionState) {
                case "checking":
                    newStatus = TYPES.CONNECTING;
                    break;
                case "connected":
                case "completed":
                    newStatus = TYPES.CONNECTION_SUCCESSFUL;
                    if (!session.wasEverConnected) {
                        this._pendingActionsForConnectedPeerConnections.forEach((action) => {
                            if (typeof action === "function") {
                                action();
                            }
                        });
                        this._pendingActionsForConnectedPeerConnections = [];
                    }
                    if (!session.wasEverConnected &&
                        (pc.iceConnectionState.match(/connected|completed/) ||
                            (browserName$1 === "chrome" && pc.localDescription && pc.localDescription.type === "answer"))) {
                        session.wasEverConnected = true;
                        if (this._features.bandwidth !== "false") {
                            this.maybeRestrictRelayBandwidth(session);
                        }
                    }
                    if (this._isAudioOnlyMode) {
                        session.setAudioOnly(true, this._screenshareVideoTrackIds);
                    }
                    session.registerConnected();
                    break;
                case "disconnected":
                    newStatus = TYPES.CONNECTION_DISCONNECTED;
                    setTimeout(() => {
                        if (pc.iceConnectionState === "disconnected") {
                            this._maybeRestartIce(clientId, session);
                        }
                    }, 2000);
                    break;
                case "failed":
                    newStatus = TYPES.CONNECTION_FAILED;
                    if (currentStatus !== newStatus) {
                        this._maybeRestartIce(clientId, session);
                    }
                    if (session.relayCandidateSeen === null && !session.wasEverConnected) {
                        this._emit(rtcManagerEvents.CONNECTION_BLOCKED_BY_NETWORK);
                    }
                    break;
                default:
                    return;
            }
            this._setConnectionStatus(session, newStatus, clientId);
        };
        pc.onconnectionstate = () => {
            switch (pc.connectionState) {
                case "connected":
                    setTimeout(() => {
                        detectMicrophoneNotWorking(session.pc).then((failureDirection) => {
                            if (failureDirection !== false) {
                                this._emit(rtcManagerEvents.MICROPHONE_NOT_WORKING, {
                                    failureDirection,
                                    clientId,
                                });
                            }
                        });
                    }, 3000);
                    session.registerConnected();
                    break;
                case "failed":
                    const newStatus = TYPES.CONNECTION_FAILED;
                    if (session.relayCandidateSeen === null && !session.wasEverConnected) {
                        this._emit(rtcManagerEvents.CONNECTION_BLOCKED_BY_NETWORK);
                    }
                    this._setConnectionStatus(session, newStatus, clientId);
                    break;
            }
        };
        const localCameraStream = this.localStreams[CAMERA_STREAM_ID];
        if (shouldAddLocalVideo && localCameraStream) {
            session.addStream(localCameraStream);
        }
        if (shouldAddLocalVideo) {
            Object.keys(this.localStreams).forEach((id) => {
                if (id === CAMERA_STREAM_ID) {
                    return;
                }
                const screenshareStream = this.localStreams[id];
                if (isOfferer) {
                    session.addStream(screenshareStream);
                }
                else {
                    session.afterConnected.then(() => {
                        this._emitServerEvent(PROTOCOL_REQUESTS.START_SCREENSHARE, {
                            receiverId: session.clientId,
                            streamId: screenshareStream.id,
                            hasAudioTrack: !!screenshareStream.getAudioTracks().length,
                        });
                        this._withForcedRenegotiation(session, () => session.addStream(screenshareStream));
                    });
                }
            });
        }
        return session;
    }
    _cleanup(peerConnectionId) {
        const session = this._getSession(peerConnectionId);
        if (!session) {
            logger$3.warn("No RTCPeerConnection in RTCManager.disconnect()", peerConnectionId);
            return;
        }
        session.close();
        delete this.peerConnections[peerConnectionId];
    }
    _forEachPeerConnection(func) {
        Object.values(this.peerConnections).forEach((peerConnection) => {
            func(peerConnection);
        });
    }
    _addStreamToPeerConnections(stream) {
        this._forEachPeerConnection((session) => {
            this._withForcedRenegotiation(session, () => session.addStream(stream));
        });
    }
    _addTrackToPeerConnections(track, stream) {
        this._forEachPeerConnection((session) => {
            this._withForcedRenegotiation(session, () => session.addTrack(track, stream));
        });
    }
    _replaceTrackToPeerConnections(oldTrack, newTrack) {
        const promises = [];
        this._forEachPeerConnection((session) => {
            if (!session.hasConnectedPeerConnection()) {
                logger$3.info("Session doesn't have a connected PeerConnection, adding pending action!");
                const pendingActions = this._pendingActionsForConnectedPeerConnections;
                if (!pendingActions) {
                    logger$3.warn(`No pending action is created to repalce track, because the pending actions array is null`);
                    return;
                }
                const promise = new Promise((resolve, reject) => {
                    const action = () => {
                        const replacedTrackPromise = session.replaceTrack(oldTrack, newTrack);
                        if (!replacedTrackPromise) {
                            logger$3.error("replaceTrack returned false!");
                            reject(`ReplaceTrack returned false`);
                            return;
                        }
                        replacedTrackPromise.then((track) => resolve(track)).catch((error) => reject(error));
                    };
                    pendingActions.push(action);
                });
                promises.push(promise);
                return;
            }
            const replacedTrackResult = session.replaceTrack(oldTrack, newTrack);
            if (!replacedTrackResult) {
                logger$3.error("replaceTrack returned false!");
                return;
            }
            promises.push(replacedTrackResult);
        });
        return Promise.all(promises);
    }
    _removeStreamFromPeerConnections(stream) {
        this._forEachPeerConnection((session) => {
            this._withForcedRenegotiation(session, () => session.removeStream(stream));
        });
    }
    _removeTrackFromPeerConnections(track) {
        this._forEachPeerConnection((session) => {
            this._withForcedRenegotiation(session, () => session.removeTrack(track));
        });
    }
    _addLocalStream(streamId, stream) {
        this._addEnabledLocalStreamId(streamId);
        this.localStreams[streamId] = stream;
    }
    _removeLocalStream(streamId) {
        delete this.localStreams[streamId];
        this._deleteEnabledLocalStreamId(streamId);
    }
    _updateAndScheduleMediaServersRefresh({ iceServers, sfuServer, mediaserverConfigTtlSeconds }) {
        this._iceServers = iceServers;
        this._sfuServer = sfuServer;
        this._mediaserverConfigTtlSeconds = mediaserverConfigTtlSeconds;
        this._clearMediaServersRefresh();
        if (!mediaserverConfigTtlSeconds) {
            return;
        }
        this._fetchMediaServersTimer = setTimeout(() => this._emitServerEvent(PROTOCOL_REQUESTS.FETCH_MEDIASERVER_CONFIG), mediaserverConfigTtlSeconds * 1000);
    }
    _clearMediaServersRefresh() {
        if (!this._fetchMediaServersTimer)
            return;
        clearTimeout(this._fetchMediaServersTimer);
        this._fetchMediaServersTimer = null;
    }
    _monitorAudioTrack(track) {
        var _a, _b;
        if (((_a = this._audioTrackBeingMonitored) === null || _a === void 0 ? void 0 : _a.id) === track.id)
            return;
        (_b = this._audioTrackBeingMonitored) === null || _b === void 0 ? void 0 : _b.removeEventListener("ended", this._audioTrackOnEnded);
        track.addEventListener("ended", this._audioTrackOnEnded);
        this._audioTrackBeingMonitored = track;
    }
    _monitorVideoTrack(track) {
        var _a, _b;
        if (((_a = this._videoTrackBeingMonitored) === null || _a === void 0 ? void 0 : _a.id) === track.id)
            return;
        (_b = this._videoTrackBeingMonitored) === null || _b === void 0 ? void 0 : _b.removeEventListener("ended", this._videoTrackOnEnded);
        track.addEventListener("ended", this._videoTrackOnEnded);
        this._videoTrackBeingMonitored = track;
    }
    _connect(clientId) {
        let session = this._getSession(clientId);
        let initialBandwidth = (session && session.bandwidth) || 0;
        if (session) {
            logger$3.warn("Replacing peer session", clientId);
            this._cleanup(clientId);
        }
        else {
            initialBandwidth = this._changeBandwidthForAllClients(true);
        }
        session = this._createP2pSession({
            clientId,
            initialBandwidth,
            shouldAddLocalVideo: true,
            isOfferer: true,
        });
        this._negotiatePeerConnection(clientId, session);
        return Promise.resolve(session);
    }
    _maybeRestartIce(clientId, session) {
        const pc = session.pc;
        if (!(pc.iceConnectionState === "disconnected" || pc.iceConnectionState === "failed")) {
            return;
        }
        if (pc.localDescription.type === "offer") {
            session.wasEverConnected = false;
            session.relayCandidateSeen = false;
            session.serverReflexiveCandidateSeen = false;
            session.publicHostCandidateSeen = false;
            session.ipv6HostCandidateSeen = false;
            this.ipv6HostCandidateTeredoSeen = false;
            this.ipv6HostCandidate6to4Seen = false;
            this.mdnsHostCandidateSeen = false;
            this._emit(rtcManagerEvents.ICE_RESTART);
            this._negotiatePeerConnection(clientId, session, Object.assign({}, this.offerOptions, { iceRestart: true }));
        }
    }
    _setCodecPreferences(pc, vp9On, av1On, redOn) {
        try {
            const audioTransceivers = pc
                .getTransceivers()
                .filter((transceiver) => { var _a, _b; return ((_b = (_a = transceiver === null || transceiver === void 0 ? void 0 : transceiver.sender) === null || _a === void 0 ? void 0 : _a.track) === null || _b === void 0 ? void 0 : _b.kind) === "audio"; });
            audioTransceivers.forEach((audioTransceiver) => {
                if (typeof RTCRtpSender.getCapabilities === "undefined")
                    return;
                const capabilities = RTCRtpSender.getCapabilities("audio");
                for (let i = 0; i < capabilities.codecs.length; i++) {
                    if (redOn && capabilities.codecs[i].mimeType.toLowerCase() === "audio/red") {
                        capabilities.codecs.unshift(capabilities.codecs.splice(i, 1)[0]);
                        break;
                    }
                }
                if (typeof audioTransceiver.setCodecPreferences === "undefined")
                    return;
                audioTransceiver.setCodecPreferences(capabilities.codecs);
            });
            const videoTransceivers = pc
                .getTransceivers()
                .filter((transceiver) => { var _a, _b; return ((_b = (_a = transceiver === null || transceiver === void 0 ? void 0 : transceiver.sender) === null || _a === void 0 ? void 0 : _a.track) === null || _b === void 0 ? void 0 : _b.kind) === "video"; });
            videoTransceivers.forEach((videoTransceiver) => {
                if (RTCRtpSender.getCapabilities === undefined)
                    return;
                const capabilities = RTCRtpSender.getCapabilities("video");
                for (let i = 0; i < capabilities.codecs.length; i++) {
                    if (vp9On && capabilities.codecs[i].mimeType.toLowerCase() === "video/vp9") {
                        capabilities.codecs.unshift(capabilities.codecs.splice(i, 1)[0]);
                        break;
                    }
                    if (av1On && capabilities.codecs[i].mimeType.toLowerCase() === "video/av1") {
                        capabilities.codecs.unshift(capabilities.codecs.splice(i, 1)[0]);
                        break;
                    }
                }
                if (videoTransceiver.setCodecPreferences === undefined)
                    return;
                videoTransceiver.setCodecPreferences(capabilities.codecs);
            });
        }
        catch (error) {
            logger$3.error("Error during setting setCodecPreferences:", error);
        }
    }
    _negotiatePeerConnection(clientId, session, constraints) {
        if (!session) {
            logger$3.warn("No RTCPeerConnection in negotiatePeerConnection()", clientId);
            return;
        }
        const pc = session.pc;
        if (!session.canModifyPeerConnection()) {
            session.pending.push(() => {
                this._negotiatePeerConnection(clientId, session, constraints);
            });
            return;
        }
        session.isOperationPending = true;
        const { vp9On, av1On, redOn, rtpAbsCaptureTimeOn, cleanSdpOn } = this._features;
        if (vp9On || av1On || redOn) {
            this._setCodecPreferences(pc, vp9On, av1On, redOn);
        }
        pc.createOffer(constraints || this.offerOptions)
            .then((offer) => {
            if (rtpAbsCaptureTimeOn)
                offer.sdp = addAbsCaptureTimeExtMap(offer.sdp);
            if ((vp9On || redOn) && browserName$1 === "firefox") {
                offer.sdp = setCodecPreferenceSDP(offer.sdp, vp9On, redOn);
            }
            if (cleanSdpOn)
                offer.sdp = cleanSdp(offer.sdp);
            pc.setLocalDescription(offer)
                .catch((e) => {
                logger$3.warn("RTCPeerConnection.setLocalDescription() failed with local offer", e);
                this._emit(rtcManagerEvents.PC_SLD_FAILURE, e);
                throw e;
            })
                .then(() => {
                this._emitServerEvent(RELAY_MESSAGES.SDP_OFFER, {
                    receiverId: clientId,
                    message: this._transformOutgoingSdp(offer),
                });
            });
        })
            .catch((e) => {
            logger$3.warn("RTCPeerConnection.createOffer() failed to create local offer", e);
        });
    }
    _withForcedRenegotiation(session, action) {
        const pc = session.pc;
        const originalOnnegotationneeded = pc.onnegotationneeded;
        pc.onnegotiationneeded = null;
        action();
        this._negotiatePeerConnection(session.clientId, session);
        setTimeout(() => (pc.onnegotiationneeded = originalOnnegotationneeded), 0);
    }
    _changeBandwidthForAllClients(isJoining) {
        let numPeers = this.numberOfPeerconnections();
        if (isJoining) {
            numPeers += 1;
        }
        if (numPeers === 0) {
            return 0;
        }
        if (numPeers > 7) {
            numPeers = 7;
        }
        let bandwidth = this._features.bandwidth
            ? parseInt(this._features.bandwidth, 10)
            : {
                1: 0,
                2: this._features.highP2PBandwidth ? 768 : 384,
                3: this._features.highP2PBandwidth ? 512 : 256,
                4: 192,
                5: 128,
                6: 128,
                7: 64,
            }[numPeers];
        if (bandwidth === undefined) {
            return 0;
        }
        if (this._features.adjustBitratesFromResolution) {
            const cameraStream = this._getLocalCameraStream();
            if (cameraStream) {
                const cameraTrack = cameraStream && cameraStream.getVideoTracks()[0];
                if (cameraTrack) {
                    const { width, height, frameRate } = cameraTrack.getSettings();
                    if (width && height && frameRate) {
                        const optimalBandwidth = Math.round(getOptimalBitrate(width, height, frameRate) / 1000);
                        bandwidth = Math.min(optimalBandwidth, bandwidth || optimalBandwidth);
                    }
                }
            }
        }
        if (this._features.higherP2PBitrates) {
            bandwidth = bandwidth * 1.5;
        }
        this._forEachPeerConnection((session) => {
            session.changeBandwidth(bandwidth);
        });
        return bandwidth;
    }
    _createP2pSession({ clientId, initialBandwidth, shouldAddLocalVideo = false, isOfferer = false, }) {
        const session = this._createSession({
            peerConnectionId: clientId,
            clientId,
            initialBandwidth,
            shouldAddLocalVideo,
            isOfferer,
        });
        const pc = session.pc;
        if (this._features.increaseIncomingMediaBufferOn) {
            this._setJitterBufferTarget(pc);
        }
        const localCameraStream = this.localStreams[CAMERA_STREAM_ID];
        if (shouldAddLocalVideo &&
            localCameraStream &&
            !localCameraStream.getVideoTracks().length &&
            this._stoppedVideoTrack) {
            pc.addTrack(this._stoppedVideoTrack, localCameraStream);
        }
        pc.onicegatheringstatechange = (event) => {
            const connection = event.target;
            switch (connection.iceGatheringState) {
                case "gathering":
                    if (this.icePublicIPGatheringTimeoutID)
                        clearTimeout(this.icePublicIPGatheringTimeoutID);
                    this.icePublicIPGatheringTimeoutID = setTimeout(() => {
                        if (!session.publicHostCandidateSeen &&
                            !session.relayCandidateSeen &&
                            !session.serverReflexiveCandidateSeen) {
                            this._emit(rtcManagerEvents.ICE_NO_PUBLIC_IP_GATHERED_3SEC);
                        }
                    }, ICE_PUBLIC_IP_GATHERING_TIMEOUT);
                    break;
                case "complete":
                    if (this.icePublicIPGatheringTimeoutID)
                        clearTimeout(this.icePublicIPGatheringTimeoutID);
                    this.icePublicIPGatheringTimeoutID = undefined;
                    break;
            }
        };
        pc.onicecandidate = (event) => {
            var _a, _b;
            if (event.candidate) {
                switch ((_a = event.candidate) === null || _a === void 0 ? void 0 : _a.type) {
                    case "host":
                        const address = (_b = event === null || event === void 0 ? void 0 : event.candidate) === null || _b === void 0 ? void 0 : _b.address;
                        try {
                            if (ipRegex.v4({ exact: true }).test(address)) {
                                const ipv4 = checkIp(address);
                                if (ipv4.isPublicIp)
                                    session.publicHostCandidateSeen = true;
                            }
                            else if (ipRegex.v6({ exact: true }).test(address.replace(/^\[(.*)\]/, "$1"))) {
                                const ipv6 = new Address6(address.replace(/^\[(.*)\]/, "$1"));
                                session.ipv6HostCandidateSeen = true;
                                if (ipv6.getScope() === "Global") {
                                    session.publicHostCandidateSeen = true;
                                }
                                if (ipv6.isTeredo()) {
                                    session.ipv6HostCandidateTeredoSeen = true;
                                }
                                if (ipv6.is6to4()) {
                                    session.ipv6HostCandidate6to4Seen = true;
                                }
                            }
                            else {
                                const uuidv4 = address.replace(/.local/, "");
                                if (uuidv4 && validate(uuidv4, 4)) {
                                    session.mdnsHostCandidateSeen = true;
                                }
                            }
                        }
                        catch (error) {
                            logger$3.info("Error during parsing candidates! Error: ", { error });
                        }
                        break;
                    case "srflx":
                        if (!session.serverReflexiveCandidateSeen) {
                            session.serverReflexiveCandidateSeen = true;
                        }
                        break;
                    case "relay":
                    case "relayed":
                        if (!session.relayCandidateSeen) {
                            session.relayCandidateSeen = true;
                        }
                        break;
                }
                this._emitServerEvent(RELAY_MESSAGES.ICE_CANDIDATE, {
                    receiverId: clientId,
                    message: event.candidate,
                });
            }
            else {
                this._emitServerEvent(RELAY_MESSAGES.ICE_END_OF_CANDIDATES, {
                    receiverId: clientId,
                });
                if (!session.publicHostCandidateSeen &&
                    !session.relayCandidateSeen &&
                    !session.serverReflexiveCandidateSeen) {
                    this._emit(rtcManagerEvents.ICE_NO_PUBLIC_IP_GATHERED);
                }
                if (session.ipv6HostCandidateSeen) {
                    this._emit(rtcManagerEvents.ICE_IPV6_SEEN, {
                        teredoSeen: session.ipv6HostCandidateTeredoSeen,
                        sixtofourSeen: session.ipv6HostCandidate6to4Seen,
                    });
                }
                if (session.mdnsHostCandidateSeen)
                    this._emit(rtcManagerEvents.ICE_MDNS_SEEN);
            }
        };
        pc.onnegotiationneeded = () => {
            if (pc.iceConnectionState === "new" || !session.connectionStatus) {
                return;
            }
            this._negotiatePeerConnection(clientId, session);
        };
        return session;
    }
    acceptNewStream({ streamId, clientId, shouldAddLocalVideo, }) {
        let session = this._getSession(clientId);
        if (session && streamId !== clientId) {
            return session;
        }
        let initialBandwidth = (session && session.bandwidth) || 0;
        if (session) {
            logger$3.warn("Replacing peer session", clientId);
            this._cleanup(clientId);
        }
        else {
            initialBandwidth = this._changeBandwidthForAllClients(true);
        }
        session = this._createP2pSession({
            clientId,
            initialBandwidth,
            shouldAddLocalVideo: !!shouldAddLocalVideo,
            isOfferer: false,
        });
        this._emitServerEvent(RELAY_MESSAGES.READY_TO_RECEIVE_OFFER, {
            receiverId: clientId,
        });
        return session;
    }
    disconnect(clientId) {
        this._cleanup(clientId);
        this._changeBandwidthForAllClients(false);
        const numPeers = this.numberOfPeerconnections();
        if (numPeers === 0) {
            setTimeout(() => {
                const numPeers = this.numberOfPeerconnections();
                if (numPeers === 0) {
                    this.rtcStatsDisconnect();
                }
            }, 60 * 1000);
        }
    }
    updateStreamResolution() { }
    stopOrResumeAudio() {
    }
    _handleStopOrResumeVideo({ enable, track }) {
        if (!enable) {
            this._stoppedVideoTrack = track;
        }
        else {
            if (this._stoppedVideoTrack) {
                this._replaceTrackToPeerConnections(this._stoppedVideoTrack, track);
                delete this._stoppedVideoTrack;
            }
            else {
                this._addTrackToPeerConnections(track);
            }
        }
    }
    stopOrResumeVideo(localStream, enable) {
        var _a;
        if (!["chrome", "safari"].includes(browserName$1)) {
            return;
        }
        if (enable === false) {
            const stopCameraDelay = ((_a = localStream.getVideoTracks().find((t) => !t.enabled)) === null || _a === void 0 ? void 0 : _a.readyState) === "ended"
                ? 0
                : 5000;
            setTimeout(() => {
                localStream.getVideoTracks().forEach((track) => {
                    if (track.enabled === false) {
                        track.stop();
                        localStream.removeTrack(track);
                        this._emit(EVENTS.LOCAL_STREAM_TRACK_REMOVED, {
                            stream: localStream,
                            track,
                        });
                        this._handleStopOrResumeVideo({ enable, track });
                    }
                });
            }, stopCameraDelay);
        }
        else {
            if (localStream.getVideoTracks().length === 0) {
                const constraints = this._webrtcProvider.getMediaConstraints().video;
                if (!constraints) {
                    return;
                }
                navigator.mediaDevices.getUserMedia({ video: constraints }).then((stream) => {
                    const track = stream.getVideoTracks()[0];
                    localStream.addTrack(track);
                    this._monitorVideoTrack(track);
                    this._emit(EVENTS.LOCAL_STREAM_TRACK_ADDED, {
                        streamId: localStream.id,
                        tracks: [track],
                        screenShare: false,
                    });
                    this._handleStopOrResumeVideo({ enable, track });
                });
            }
        }
    }
    _shareScreen(streamId, stream) {
        this._emitServerEvent(PROTOCOL_REQUESTS.START_SCREENSHARE, {
            streamId,
            hasAudioTrack: !!stream.getAudioTracks().length,
        });
        this._wasScreenSharing = true;
        this._addStreamToPeerConnections(stream);
    }
    removeStream(streamId, stream, requestedByClientId) {
        this._removeLocalStream(streamId);
        this._removeStreamFromPeerConnections(stream);
        this._wasScreenSharing = false;
        this._emitServerEvent(PROTOCOL_REQUESTS.STOP_SCREENSHARE, { streamId, requestedByClientId });
    }
    hasClient(clientId) {
        return Object.keys(this.peerConnections).includes(clientId);
    }
}

class KalmanFilter {
    constructor({ R = 0.01, Q = 2, A = 1.1, B = 1, C = 1.2 } = { R: 0.01, Q: 2, A: 1.1, B: 1, C: 1.2 }) {
        this.R = R;
        this.Q = Q;
        this.A = A;
        this.C = C;
        this.B = B;
        this.cov = NaN;
        this.x = NaN;
    }
    filter(z, u = 0) {
        if (isNaN(this.x)) {
            this.x = (1 / this.C) * z;
            this.cov = (1 / this.C) * this.Q * (1 / this.C);
        }
        else {
            const predX = this.predict(u);
            const predCov = this.uncertainty();
            const K = predCov * this.C * (1 / (this.C * predCov * this.C + this.Q));
            this.x = predX + K * (z - this.C * predX);
            this.cov = predCov - K * this.C * predCov;
        }
        return this.x;
    }
    predict(u = 0) {
        return this.A * this.x + this.B * u;
    }
    uncertainty() {
        return this.A * this.cov * this.A + this.R;
    }
    lastMeasurement() {
        return this.x;
    }
    setMeasurementNoise(noise) {
        this.Q = noise;
    }
    setProcessNoise(noise) {
        this.R = noise;
    }
}
const createACFCalculator = () => {
    let ewma = -1;
    let lastMax = 0;
    let counter = 0;
    return (arr) => {
        const max = arr.reduce((acc, val) => acc + val, 0) / arr.length;
        if (ewma < 0) {
            ewma = max;
        }
        else {
            ewma = ewma * 0.95 + max * 0.05;
        }
        if (++counter < 2) {
            lastMax = max;
            return 0;
        }
        const acf = (max - ewma) * (lastMax - ewma);
        lastMax = max;
        return acf;
    };
};
const calculateStd = (arr) => {
    const mean = arr.reduce((acc, val) => acc + val, 0) / arr.length;
    return (arr.reduce((acc, val) => acc.concat(Math.pow((val - mean), 2)), []).reduce((acc, val) => acc + val, 0) /
        arr.length);
};
const standardDeviation = (arr) => {
    const mean = arr.reduce((acc, val) => acc + val, 0) / arr.length;
    return {
        mean,
        SD: Math.sqrt(arr.reduce((acc, val) => acc + Math.pow((val - mean), 2), 0) / arr.length),
    };
};
const variance = (arr, usePopulation = false) => {
    const mean = arr.reduce((acc, val) => acc + val, 0) / arr.length;
    return (arr.reduce((acc, val) => acc.concat(Math.pow((val - mean), 2)), []).reduce((acc, val) => acc + val, 0) /
        (arr.length - (usePopulation ? 0 : 1)));
};

var scriptFunctions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    KalmanFilter: KalmanFilter,
    calculateStd: calculateStd,
    createACFCalculator: createACFCalculator,
    standardDeviation: standardDeviation,
    variance: variance
});

const defaultParams = {
    minDecibels: -80,
    maxDecibels: -10,
    smoothingTimeConstant: 0.8,
    aggSize: 1,
    sampleRate: 10,
    reportRate: 2,
    bins: 32,
    bands: [
        [0, 1, 2, 3],
        [4, 5, 6, 7],
    ],
    separateTrack: {
        autoGainControl: false,
        noiseSupression: false,
    },
    binFormula: "bin",
    scoreFormula: "300 * bands[1].avg / Math.max(bands[1].avg + bands[0].avg, 0.01)",
    outFormula: "score",
};
const logger$2 = new Logger();
function createMicAnalyser({ micTrack, params, onScoreUpdated, }) {
    const audioCtx = new AudioContext();
    let analyser = null;
    let source = null;
    let lastTrack = null;
    let lastTrackWasOurs = false;
    const setTrack = (track) => __awaiter(this, void 0, void 0, function* () {
        if (lastTrack && lastTrackWasOurs) {
            lastTrack.stop();
        }
        lastTrackWasOurs = false;
        if (track && params.separateTrack) {
            try {
                const stream = yield navigator.mediaDevices.getUserMedia({
                    audio: Object.assign({ deviceId: lastTrack === null || lastTrack === void 0 ? void 0 : lastTrack.getSettings().deviceId }, params.separateTrack),
                    video: false,
                });
                track = stream.getAudioTracks()[0];
                lastTrackWasOurs = true;
            }
            catch (ex) {
                logger$2.warn("unable to fetch new track for colocation speaker analysis, using current", ex);
            }
        }
        lastTrack = track;
        if (source) {
            source.disconnect();
            source = null;
        }
        if (track) {
            const stream = new MediaStream([track]);
            source = audioCtx.createMediaStreamSource(stream);
            source.connect(analyser);
        }
    });
    let samplerInterval;
    const setParams = (newParams) => {
        clearInterval(samplerInterval);
        params = Object.assign(Object.assign({}, defaultParams), newParams);
        if (!analyser || analyser.fftSize !== params.bins * 2) {
            let restoreTrack = null;
            if (analyser) {
                restoreTrack = lastTrack;
                setTrack(null);
            }
            analyser = audioCtx.createAnalyser();
            analyser.fftSize = params.bins * 2;
            if (restoreTrack)
                setTrack(restoreTrack);
        }
        const bins = new Uint8Array(analyser.frequencyBinCount);
        const scores = [];
        analyser.minDecibels = params.minDecibels;
        analyser.maxDecibels = params.maxDecibels;
        analyser.smoothingTimeConstant = params.smoothingTimeConstant;
        const initScriptFunc = params.initScript && new Function("f", "setTrack", "lastTrack", params.initScript.join("\n"));
        const scoreScriptFunc = params.scoreScript && new Function("bins", "g", "f", params.scoreScript.join("\n"));
        const calcScore = params.scoreFormula && new Function("bands", `return ${params.scoreFormula}`);
        const getOutValue = params.outFormula && new Function("score", "avg", "max", "scores", `return ${params.outFormula}`);
        const binMap = params.binFormula && new Function("bin", "index", `return ${params.binFormula}`);
        let reportRateCounter = 0;
        const globals = initScriptFunc && initScriptFunc(scriptFunctions, setTrack, lastTrack);
        const sampler = () => {
            analyser.getByteFrequencyData(bins);
            let score = 0;
            if (calcScore) {
                const processedBands = params.bands.map((range) => {
                    const bands = range.map((index) => binMap(bins[index] / 255, index));
                    return {
                        avg: bands.reduce((sum, current) => sum + current, 0) / range.length,
                        min: bands.reduce((min, current) => Math.min(min, current / 255), Number.MAX_VALUE),
                        max: bands.reduce((max, current) => Math.max(max, current / 255), 0),
                    };
                });
                score = calcScore(processedBands) || 0;
            }
            else if (scoreScriptFunc) {
                score = scoreScriptFunc(bins, globals, scriptFunctions);
                if (isNaN(score))
                    score = 0;
            }
            scores.push(score);
            if (scores.length > params.aggSize)
                scores.shift();
            reportRateCounter++;
            if (reportRateCounter >= params.reportRate) {
                reportRateCounter = 0;
                const max = scores.reduce((a, b) => Math.max(a, b), 0);
                const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
                const out = getOutValue(score, avg, max, scores);
                onScoreUpdated({ bins, score, max, avg, out });
            }
        };
        samplerInterval = setInterval(sampler, 1000 / params.sampleRate);
    };
    setParams(params);
    setTrack(micTrack);
    return {
        setParams,
        setTrack,
        close() {
            setTrack(null);
            clearInterval(samplerInterval);
            audioCtx.close();
        },
    };
}

const logger$1 = new Logger();
const MEDIA_QUALITY = Object.freeze({
    ok: "ok",
    warning: "warning",
    critical: "critical",
});
const MONITOR_INTERVAL = 600;
const TREND_HORIZON = 3;
const WARNING_SCORE = 9;
const CRITICAL_SCORE = 7;
class VegaMediaQualityMonitor extends EventEmitter {
    constructor() {
        super();
        this._clients = {};
        this._producers = {};
        this._startMonitor();
    }
    close() {
        clearInterval(this._intervalHandle);
        delete this._intervalHandle;
        this._producers = {};
        this._clients = {};
    }
    _startMonitor() {
        this._intervalHandle = setInterval(() => {
            Object.entries(this._producers).forEach(([clientId, producers]) => {
                this._evaluateClient(clientId, producers);
            });
        }, MONITOR_INTERVAL);
    }
    _evaluateClient(clientId, producers) {
        if (!this._clients[clientId]) {
            this._clients[clientId] = {
                audio: { currentQuality: MEDIA_QUALITY.ok, trend: [] },
                video: { currentQuality: MEDIA_QUALITY.ok, trend: [] },
            };
        }
        this._evaluateProducer(clientId, Object.values(producers).filter((p) => p.kind === "audio"), "audio");
        this._evaluateProducer(clientId, Object.values(producers).filter((p) => p.kind === "video"), "video");
    }
    _evaluateProducer(clientId, producers, kind) {
        if (producers.length === 0) {
            return;
        }
        const avgScore = producers.reduce((prev, curr) => prev + curr.score, 0) / producers.length;
        const newQuality = this._evaluateScore(avgScore);
        const qualityChanged = this._updateTrend(newQuality, this._clients[clientId][kind]);
        if (qualityChanged) {
            this.emit(PROTOCOL_EVENTS.MEDIA_QUALITY_CHANGED, {
                clientId,
                kind,
                quality: newQuality,
            });
        }
    }
    _updateTrend(newQuality, state) {
        state.trend.push(newQuality);
        if (state.trend.length > TREND_HORIZON) {
            state.trend.shift();
        }
        if (newQuality !== state.currentQuality && state.trend.every((t) => t !== state.currentQuality)) {
            state.currentQuality = newQuality;
            return true;
        }
        else {
            return false;
        }
    }
    addProducer(clientId, producerId) {
        if (!clientId || !producerId || !(typeof clientId === "string" && typeof producerId === "string")) {
            logger$1.warn("Missing clientId or producerId");
            return;
        }
        if (!this._producers[clientId]) {
            this._producers[clientId] = {};
        }
        this._producers[clientId][producerId] = {};
    }
    removeProducer(clientId, producerId) {
        delete this._producers[clientId][producerId];
        if (Object.keys(this._producers[clientId]).length === 0) {
            delete this._producers[clientId];
        }
    }
    addConsumer(clientId, consumerId) {
        if (!clientId || !consumerId) {
            logger$1.warn("Missing clientId or consumerId");
            return;
        }
        if (!this._producers[clientId]) {
            this._producers[clientId] = {};
        }
        this._producers[clientId][consumerId] = {};
    }
    removeConsumer(clientId, consumerId) {
        delete this._producers[clientId][consumerId];
        if (Object.keys(this._producers[clientId]).length === 0) {
            delete this._producers[clientId];
        }
    }
    addProducerScore(clientId, producerId, kind, score) {
        if (!Array.isArray(score) ||
            score.length === 0 ||
            score.some((s) => !s || !s.hasOwnProperty("score") || typeof s.score !== "number" || isNaN(s.score))) {
            logger$1.warn("Unexpected producer score format");
            return;
        }
        this._producers[clientId][producerId] = { kind, score: this._calcAvgProducerScore(score.map((s) => s.score)) };
    }
    addConsumerScore(clientId, consumerId, kind, score) {
        if (!score || !score.hasOwnProperty("producerScores") || !Array.isArray(score.producerScores)) {
            logger$1.warn("Unexpected consumer score format");
            return;
        }
        this._producers[clientId][consumerId] = { kind, score: this._calcAvgProducerScore(score.producerScores) };
    }
    _evaluateScore(score) {
        if (score <= WARNING_SCORE && score > CRITICAL_SCORE) {
            return MEDIA_QUALITY.warning;
        }
        else if (score <= CRITICAL_SCORE && score > 0) {
            return MEDIA_QUALITY.critical;
        }
        else {
            return MEDIA_QUALITY.ok;
        }
    }
    _calcAvgProducerScore(scores) {
        try {
            if (!Array.isArray(scores) || scores.length === 0) {
                return 0;
            }
            let totalScore = 0;
            let divisor = 0;
            scores.forEach((score) => {
                if (score > 0) {
                    totalScore += score;
                    divisor++;
                }
            });
            if (totalScore === 0 || divisor === 0) {
                return 0;
            }
            else {
                return totalScore / divisor;
            }
        }
        catch (error) {
            logger$1.error(error);
            return 0;
        }
    }
}

function getLayers({ width, height, }, { numberOfActiveVideos, numberOfTemporalLayers, uncappedSingleRemoteVideoOn, }) {
    const maxSide = Math.max(width, height);
    let spatialLayer = maxSide >= 480 ? (maxSide >= 960 ? 2 : 1) : 0;
    let temporalLayer = numberOfTemporalLayers - 1;
    if (maxSide < 100) {
        temporalLayer = Math.max(numberOfTemporalLayers - 2, 0);
    }
    if (numberOfActiveVideos > 8 && spatialLayer === 0) {
        temporalLayer = Math.max(numberOfTemporalLayers - 2, 0);
    }
    if (numberOfActiveVideos < 4 && maxSide > 300 && spatialLayer === 0) {
        spatialLayer = 1;
    }
    if (uncappedSingleRemoteVideoOn && numberOfActiveVideos === 1) {
        spatialLayer = 2;
        temporalLayer = numberOfTemporalLayers - 1;
    }
    return { spatialLayer, temporalLayer };
}
function getNumberOfActiveVideos(consumers) {
    let numberOfActiveVideos = 0;
    consumers.forEach((c) => {
        var _a, _b;
        if (c._closed || c._paused)
            return;
        if (((_a = c._appData) === null || _a === void 0 ? void 0 : _a.source) === "webcam" || ((_b = c._appData) === null || _b === void 0 ? void 0 : _b.source) === "screenvideo")
            numberOfActiveVideos++;
    });
    return numberOfActiveVideos;
}
function getNumberOfTemporalLayers(consumer) {
    var _a, _b, _c;
    return /T3/.test(((_c = (_b = (_a = consumer._rtpParameters) === null || _a === void 0 ? void 0 : _a.encodings) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.scalabilityMode) || "") ? 3 : 2;
}

var _a;
const adapter = (_a = adapterRaw.default) !== null && _a !== void 0 ? _a : adapterRaw;
const logger = new Logger();
const browserName = adapter.browserDetails.browser;
let unloading = false;
const RESTARTICE_ERROR_RETRY_THRESHOLD_IN_MS = 3500;
const RESTARTICE_ERROR_MAX_RETRY_COUNT = 5;
const OUTBOUND_CAM_OUTBOUND_STREAM_ID = v4$1();
const OUTBOUND_SCREEN_OUTBOUND_STREAM_ID = v4$1();
if (browserName === "chrome")
    window.document.addEventListener("beforeunload", () => (unloading = true));
class VegaRtcManager {
    constructor({ selfId, room, emitter, serverSocket, webrtcProvider, features, eventClaim, deviceHandlerFactory, }) {
        const { session, iceServers, sfuServer, mediaserverConfigTtlSeconds } = room;
        this._selfId = selfId;
        this._room = room;
        this._roomSessionId = session === null || session === void 0 ? void 0 : session.id;
        this._emitter = emitter;
        this._serverSocket = serverSocket;
        this._webrtcProvider = webrtcProvider;
        this._features = features || {};
        this._eventClaim = eventClaim;
        this._vegaConnection = null;
        this._micAnalyser = null;
        this._micAnalyserDebugger = null;
        if (deviceHandlerFactory) {
            this._mediasoupDevice = new Device({ handlerFactory: deviceHandlerFactory });
        }
        else {
            this._mediasoupDevice = new Device({ handlerName: getHandler() });
        }
        this._routerRtpCapabilities = null;
        this._sendTransport = null;
        this._receiveTransport = null;
        this._clientStates = new Map();
        this._streamIdToVideoConsumerId = new Map();
        this._streamIdToVideoResolution = new Map();
        this._consumers = new Map();
        this._dataConsumers = new Map();
        this._localStreamDeregisterFunction = null;
        this._micTrack = null;
        this._webcamTrack = null;
        this._screenVideoTrack = null;
        this._screenAudioTrack = null;
        this._micProducer = null;
        this._micProducerPromise = null;
        this._micPaused = false;
        this._micScoreProducer = null;
        this._micScoreProducerPromise = null;
        this._webcamProducer = null;
        this._webcamProducerPromise = null;
        this._webcamPaused = false;
        this._screenVideoProducer = null;
        this._screenVideoProducerPromise = null;
        this._screenAudioProducer = null;
        this._screenAudioProducerPromise = null;
        this._sndTransportIceRestartPromise = null;
        this._rcvTransportIceRestartPromise = null;
        this._colocation = null;
        this._stopCameraTimeout = null;
        this._audioTrackOnEnded = () => {
            rtcStats.sendEvent("audio_ended", { unloading });
            this._emitToPWA(rtcManagerEvents.MICROPHONE_STOPPED_WORKING, {});
        };
        this._videoTrackOnEnded = () => {
            rtcStats.sendEvent("video_ended", { unloading });
            this._emitToPWA(rtcManagerEvents.CAMERA_STOPPED_WORKING, {});
        };
        this._updateAndScheduleMediaServersRefresh({
            sfuServer,
            iceServers: iceServers.iceServers || [],
            mediaserverConfigTtlSeconds,
        });
        this._socketListenerDeregisterFunctions = [];
        this._reconnect = true;
        this._reconnectTimeOut = null;
        this._hasVegaConnection = false;
        this._qualityMonitor = new VegaMediaQualityMonitor();
        this._qualityMonitor.on(PROTOCOL_EVENTS.MEDIA_QUALITY_CHANGED, (payload) => {
            this._emitToPWA(PROTOCOL_EVENTS.MEDIA_QUALITY_CHANGED, payload);
        });
    }
    _updateAndScheduleMediaServersRefresh({ iceServers, sfuServer, mediaserverConfigTtlSeconds, }) {
        var _a, _b;
        this._iceServers = iceServers;
        this._sfuServer = sfuServer;
        this._mediaserverConfigTtlSeconds = mediaserverConfigTtlSeconds;
        (_a = this._sendTransport) === null || _a === void 0 ? void 0 : _a.updateIceServers({ iceServers: this._iceServers });
        (_b = this._receiveTransport) === null || _b === void 0 ? void 0 : _b.updateIceServers({ iceServers: this._iceServers });
        this._clearMediaServersRefresh();
        if (!mediaserverConfigTtlSeconds) {
            return;
        }
        this._fetchMediaServersTimer = setTimeout(() => this._emitToSignal(PROTOCOL_REQUESTS.FETCH_MEDIASERVER_CONFIG), mediaserverConfigTtlSeconds * 1000);
    }
    _clearMediaServersRefresh() {
        if (!this._fetchMediaServersTimer)
            return;
        clearTimeout(this._fetchMediaServersTimer);
        this._fetchMediaServersTimer = null;
    }
    setupSocketListeners() {
        this._socketListenerDeregisterFunctions.push(() => this._clearMediaServersRefresh(), this._serverSocket.on(PROTOCOL_RESPONSES.MEDIASERVER_CONFIG, (data) => {
            if (data.error) {
                logger.warn("FETCH_MEDIASERVER_CONFIG failed:", data.error);
                return;
            }
            this._updateAndScheduleMediaServersRefresh(data);
        }), this._serverSocket.on(PROTOCOL_RESPONSES.ROOM_JOINED, () => {
            if (this._screenVideoTrack)
                this._emitScreenshareStarted();
            if (this._features.sfuReconnectV2On && !this._hasVegaConnection && this._reconnect) {
                this._connect();
            }
        }));
        this._connect();
    }
    _emitScreenshareStarted() {
        this._emitToSignal(PROTOCOL_REQUESTS.START_SCREENSHARE, {
            streamId: OUTBOUND_SCREEN_OUTBOUND_STREAM_ID,
            hasAudioTrack: Boolean(this._screenAudioTrack),
        });
    }
    _connect() {
        if (this._features.sfuReconnectV2On) {
            if (this._hasVegaConnection)
                return;
            if (!this._serverSocket.isConnected()) {
                const reconnectThresholdInMs = this._serverSocket.getReconnectThreshold();
                if (!reconnectThresholdInMs)
                    return;
                if (Date.now() > (this._serverSocket.disconnectTimestamp || 0) + reconnectThresholdInMs)
                    return;
            }
            if (this._reconnectTimeOut)
                clearTimeout(this._reconnectTimeOut);
        }
        const host = this._features.sfuServerOverrideHost || [this._sfuServer.url];
        const searchParams = new URLSearchParams(Object.assign({ clientId: this._selfId, organizationId: this._room.organizationId, roomName: this._room.name, eventClaim: this._room.isClaimed ? this._eventClaim : null, lowBw: "true" }, Object.keys(this._features || {})
            .filter((featureKey) => this._features[featureKey] && /^sfu/.test(featureKey))
            .reduce((prev, current) => (Object.assign(Object.assign({}, prev), { [current]: this._features[current] })), {})));
        const queryString = searchParams.toString();
        const wsUrl = `wss://${host}?${queryString}`;
        this._vegaConnection = new VegaConnection(wsUrl);
        this._hasVegaConnection = true;
        this._vegaConnection.on("open", () => this._join());
        this._vegaConnection.on("close", () => this._onClose());
        this._vegaConnection.on("message", (message) => this._onMessage(message));
    }
    _onClose() {
        var _a, _b;
        logger.info("_onClose()");
        this._hasVegaConnection = false;
        (_a = this._sendTransport) === null || _a === void 0 ? void 0 : _a.close();
        (_b = this._receiveTransport) === null || _b === void 0 ? void 0 : _b.close();
        this._sendTransport = null;
        this._receiveTransport = null;
        this._streamIdToVideoConsumerId.clear();
        this._streamIdToVideoResolution.clear();
        if (this._reconnect) {
            this._reconnectTimeOut = setTimeout(() => this._connect(), 1000);
        }
        this._qualityMonitor.close();
        this._emitToPWA(rtcManagerEvents.SFU_CONNECTION_CLOSED);
    }
    _join() {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("join()");
            this._emitToPWA(rtcManagerEvents.SFU_CONNECTION_OPEN);
            try {
                const { routerRtpCapabilities } = yield this._vegaConnection.request("getCapabilities");
                if (!this._routerRtpCapabilities) {
                    modifyMediaCapabilities(routerRtpCapabilities, this._features);
                    this._routerRtpCapabilities = routerRtpCapabilities;
                    yield this._mediasoupDevice.load({ routerRtpCapabilities });
                }
                this._vegaConnection.message("setCapabilities", {
                    rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
                });
                if (this._colocation)
                    this._vegaConnection.message("setColocation", { colocation: this._colocation });
                yield Promise.all([this._createTransport(true), this._createTransport(false)]);
                const mediaPromises = [];
                if (this._micTrack && !this._micProducer && !this._micProducerPromise)
                    mediaPromises.push(this._internalSendMic());
                if (this._webcamTrack && !this._webcamProducer && !this._webcamProducerPromise)
                    mediaPromises.push(this._internalSendWebcam());
                if (this._screenVideoTrack && !this._screenVideoProducer && !this._screenVideoProducerPromise)
                    mediaPromises.push(this._internalSendScreenVideo());
                if (this._screenAudioTrack && !this._screenAudioProducer && !this._screenAudioProducerPromise)
                    mediaPromises.push(this._internalSendScreenAudio());
                yield Promise.all(mediaPromises);
            }
            catch (error) {
                logger.error("_join() [error:%o]", error);
            }
        });
    }
    _createTransport(send) {
        return __awaiter(this, void 0, void 0, function* () {
            const creator = send ? "createSendTransport" : "createRecvTransport";
            const transportOptions = yield this._vegaConnection.request("createTransport", {
                producing: send,
                consuming: !send,
                enableTcp: true,
                enableUdp: true,
                preferUdp: true,
                sctpParameters: {
                    enableSctp: true,
                    numSctpStreams: {
                        OS: 1024,
                        MIS: 1024,
                    },
                    maxSctpMessageSize: 262144,
                    sctpSendBufferSize: 262144,
                },
            });
            transportOptions.iceServers = this._iceServers;
            maybeTurnOnly(transportOptions, this._features);
            const transport = this._mediasoupDevice[creator](transportOptions);
            const onConnectionStateListener = (connectionState) => __awaiter(this, void 0, void 0, function* () {
                logger.info(`Transport ConnectionStateChanged ${connectionState}`);
                if (connectionState !== "disconnected" && connectionState !== "failed") {
                    return;
                }
                if (send) {
                    if (this._sndTransportIceRestartPromise) {
                        return;
                    }
                    this._sndTransportIceRestartPromise = this._restartIce(transport).finally(() => {
                        this._sndTransportIceRestartPromise = null;
                    });
                }
                else {
                    if (this._rcvTransportIceRestartPromise) {
                        return;
                    }
                    this._rcvTransportIceRestartPromise = this._restartIce(transport).finally(() => {
                        this._rcvTransportIceRestartPromise = null;
                    });
                }
            });
            transport
                .on("connect", ({ dtlsParameters }, callback) => {
                var _a;
                (_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.message("connectTransport", {
                    transportId: transport.id,
                    dtlsParameters,
                });
                callback();
            })
                .on("connectionstatechange", onConnectionStateListener);
            transport.observer.once("close", () => {
                transport.removeListener(onConnectionStateListener);
            });
            if (send) {
                transport.on("produce", ({ kind, rtpParameters, appData, }, callback, errback) => __awaiter(this, void 0, void 0, function* () {
                    var _a;
                    try {
                        const { paused } = appData;
                        const { id } = yield ((_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.request("produce", {
                            transportId: transport.id,
                            kind,
                            rtpParameters,
                            paused,
                            appData,
                        }));
                        callback({ id });
                    }
                    catch (error) {
                        errback(error);
                    }
                }));
                transport.on("producedata", ({ appData, sctpStreamParameters, }, callback, errback) => __awaiter(this, void 0, void 0, function* () {
                    var _b;
                    try {
                        const { id } = yield ((_b = this._vegaConnection) === null || _b === void 0 ? void 0 : _b.request("produceData", {
                            transportId: transport.id,
                            sctpStreamParameters,
                            appData,
                        }));
                        callback({ id });
                    }
                    catch (error) {
                        errback(error);
                    }
                }));
                this._sendTransport = transport;
            }
            else {
                this._receiveTransport = transport;
            }
        });
    }
    _restartIce(transport, retried = 0) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!transport || !("closed" in transport) || !("connectionState" in transport)) {
                logger.info("_restartIce: No transport or property closed or connectionState!");
                return;
            }
            if (transport.closed) {
                logger.info("_restartIce: Transport is closed!");
                return;
            }
            if (transport.connectionState !== "disconnected" && transport.connectionState !== "failed") {
                logger.info("_restartIce: Connection is healthy ICE restart no loneger needed!");
                return;
            }
            const { iceRestartStarted } = transport.appData;
            const now = Date.now();
            if (Number.isFinite(iceRestartStarted) && now - iceRestartStarted < RESTARTICE_ERROR_RETRY_THRESHOLD_IN_MS) {
                return;
            }
            transport.appData.iceRestartStarted = now;
            if (RESTARTICE_ERROR_MAX_RETRY_COUNT <= retried) {
                logger.info("_restartIce: Reached restart ICE  maximum retry count!");
                return;
            }
            if (!this._vegaConnection) {
                logger.info(`_restartIce: Connection is undefined`);
                return;
            }
            const { iceParameters } = yield this._vegaConnection.request("restartIce", { transportId: transport.id });
            logger.info("_restartIce: ICE restart iceParameters received from SFU: ", iceParameters);
            const error = yield transport
                .restartIce({ iceParameters })
                .then(() => null)
                .catch((err) => err);
            if (error) {
                logger.error(`_restartIce: ICE restart failed: ${error}`);
                switch (error.message) {
                    case "missing transportId":
                    case "no such transport":
                        break;
                    default:
                        yield new Promise((resolve) => {
                            setTimeout(() => {
                                resolve(undefined);
                            }, Math.min(RESTARTICE_ERROR_RETRY_THRESHOLD_IN_MS * Math.pow(2, retried), 60000));
                        });
                        yield this._restartIce(transport, retried + 1);
                        break;
                }
                return;
            }
            yield new Promise((resolve) => {
                setTimeout(() => {
                    resolve(undefined);
                }, 60000 * Math.min(8, retried + 1));
            });
            if (transport.connectionState === "failed" || transport.connectionState === "disconnected") {
                yield this._restartIce(transport, retried + 1);
                return;
            }
        });
    }
    _internalSendMic() {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_internalSendMic()");
            this._micProducerPromise = (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    if (!this._micTrack || !this._sendTransport || this._micProducer) {
                        this._micProducerPromise = null;
                        return;
                    }
                    const currentPaused = this._micPaused;
                    const producer = yield this._sendTransport.produce(Object.assign(Object.assign({ track: this._micTrack, disableTrackOnPause: false, stopTracks: false }, getMediaSettings("audio", false, this._features)), { appData: {
                            streamId: OUTBOUND_CAM_OUTBOUND_STREAM_ID,
                            sourceClientId: this._selfId,
                            screenShare: false,
                            source: "mic",
                            paused: currentPaused,
                        } }));
                    currentPaused ? producer.pause() : producer.resume();
                    this._micProducer = producer;
                    this._qualityMonitor.addProducer(this._selfId, producer.id);
                    producer.observer.once("close", () => {
                        var _a;
                        logger.info('micProducer "close" event');
                        if (producer.appData.localClosed)
                            (_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.message("closeProducers", { producerIds: [producer.id] });
                        this._micProducer = null;
                        this._micProducerPromise = null;
                        this._qualityMonitor.removeProducer(this._selfId, producer.id);
                    });
                    if (this._micTrack !== this._micProducer.track)
                        yield this._replaceMicTrack();
                    if (this._micPaused !== this._micProducer.paused)
                        this._pauseResumeMic();
                }
                catch (error) {
                    logger.error("micProducer failed:%o", error);
                }
                finally {
                    this._micProducerPromise = null;
                    if (!this._micTrack) {
                        this._stopProducer(this._micProducer);
                        this._micProducer = null;
                    }
                }
            }))();
        });
    }
    _internalSetupMicScore() {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_internalSetupMicScore()");
            this._micScoreProducerPromise = (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    if (!this._micProducer || !this._colocation || this._micScoreProducer) {
                        this._micScoreProducerPromise = null;
                        return;
                    }
                    const producer = yield this._sendTransport.produceData({
                        ordered: false,
                        maxPacketLifeTime: 3000,
                        label: "micscore",
                        appData: {
                            producerId: this._micProducer.id,
                            clientId: this._selfId,
                        },
                    });
                    this._micScoreProducer = producer;
                    producer.observer.once("close", () => {
                        var _a;
                        logger.info('micScoreProducer "close" event');
                        if (producer.appData.localClosed) {
                            (_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.message("closeDataProducers", { dataProducerIds: [producer.id] });
                        }
                        this._micScoreProducer = null;
                        this._micScoreProducerPromise = null;
                    });
                }
                catch (error) {
                    logger.error("micScoreProducer failed:%o", error);
                }
                finally {
                    this._micScoreProducerPromise = null;
                    if (!this._micProducer || !this._colocation) {
                        this._stopMicScoreProducer();
                    }
                }
            }))();
        });
    }
    _stopMicScoreProducer() {
        this._stopProducer(this._micScoreProducer);
        this._micScoreProducer = null;
    }
    _replaceMicTrack() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_replaceMicTrack()");
            if (!this._micTrack || !this._micProducer || this._micProducer.closed)
                return;
            if (this._micProducer.track !== this._micTrack) {
                yield this._micProducer.replaceTrack({ track: this._micTrack });
                (_a = this._micAnalyser) === null || _a === void 0 ? void 0 : _a.setTrack(this._micTrack);
                yield this._replaceMicTrack();
            }
        });
    }
    _pauseResumeMic() {
        var _a, _b;
        logger.info("_pauseResumeMic()");
        if (!this._micProducer || this._micProducer.closed)
            return;
        if (this._micPaused !== this._micProducer.paused) {
            if (this._micPaused) {
                this._micProducer.pause();
                (_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.message("pauseProducers", {
                    producerIds: [this._micProducer.id],
                });
            }
            else {
                this._micProducer.resume();
                (_b = this._vegaConnection) === null || _b === void 0 ? void 0 : _b.message("resumeProducers", {
                    producerIds: [this._micProducer.id],
                });
            }
            this._pauseResumeMic();
        }
    }
    _sendMic(track) {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_sendMic() [track:%o]", track);
            this._micTrack = track;
            if (this._micProducer) {
                return yield this._replaceMicTrack();
            }
            else if (this._micProducerPromise) {
                return;
            }
            if (this._sendTransport)
                return yield this._internalSendMic();
        });
    }
    _sendMicScore(score) {
        if (isNaN(score))
            return;
        if (!isFinite(score))
            return;
        if (this._micScoreProducer) {
            if (this._micScoreProducer.closed) {
                this._stopMicScoreProducer();
                return;
            }
            try {
                this._micScoreProducer.send(JSON.stringify({ score }));
            }
            catch (ex) {
                logger.error("_sendMicScore failed [error:%o]", ex);
            }
            return;
        }
        if (!this._micScoreProducerPromise && this._micProducer && this._colocation && score !== 0) {
            this._internalSetupMicScore();
        }
    }
    _internalSendWebcam() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_internalSendWebcam()");
            if (!this._sendTransport ||
                this._webcamProducer ||
                this._webcamProducerPromise ||
                ((_a = this._webcamTrack) === null || _a === void 0 ? void 0 : _a.readyState) !== "live") {
                return;
            }
            this._webcamProducerPromise = (() => __awaiter(this, void 0, void 0, function* () {
                var _b;
                try {
                    const currentPaused = this._webcamPaused;
                    const producer = yield this._sendTransport.produce(Object.assign(Object.assign({ track: this._webcamTrack, disableTrackOnPause: false, stopTracks: false }, getMediaSettings("video", false, this._features)), { appData: {
                            streamId: OUTBOUND_CAM_OUTBOUND_STREAM_ID,
                            sourceClientId: this._selfId,
                            screenShare: false,
                            source: "webcam",
                            paused: currentPaused,
                        } }));
                    currentPaused ? producer.pause() : producer.resume();
                    this._webcamProducer = producer;
                    this._qualityMonitor.addProducer(this._selfId, producer.id);
                    producer.observer.once("close", () => {
                        var _a;
                        logger.info('webcamProducer "close" event');
                        if (producer.appData.localClosed)
                            (_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.message("closeProducers", { producerIds: [producer.id] });
                        this._webcamProducer = null;
                        this._webcamProducerPromise = null;
                        this._qualityMonitor.removeProducer(this._selfId, producer.id);
                    });
                    if (this._webcamTrack && this._webcamTrack !== ((_b = this._webcamProducer) === null || _b === void 0 ? void 0 : _b.track)) {
                        this._webcamProducerPromise = null;
                        this._replaceWebcamTrack();
                    }
                    if (this._webcamPaused !== this._webcamProducer.paused)
                        this._pauseResumeWebcam();
                }
                catch (error) {
                    logger.error("webcamProducer failed:%o", error);
                }
                finally {
                    this._webcamProducerPromise = null;
                    if (!this._webcamTrack) {
                        this._stopProducer(this._webcamProducer);
                        this._webcamProducer = null;
                    }
                }
            }))();
        });
    }
    _replaceWebcamTrack() {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_replaceWebcamTrack()");
            if (!this._sendTransport || !this._webcamTrack || this._webcamProducerPromise)
                return;
            if (!this._webcamProducer && this._webcamTrack.enabled) {
                yield this._internalSendWebcam();
                return;
            }
            if (this._webcamProducer.track !== this._webcamTrack) {
                yield this._webcamProducer.replaceTrack({ track: this._webcamTrack });
                yield this._replaceWebcamTrack();
            }
        });
    }
    _pauseResumeWebcam() {
        var _a, _b;
        logger.info("_pauseResumeWebcam()");
        if (!this._webcamProducer || this._webcamProducer.closed)
            return;
        if (this._webcamPaused !== this._webcamProducer.paused) {
            if (this._webcamPaused) {
                this._webcamProducer.pause();
                (_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.message("pauseProducers", {
                    producerIds: [this._webcamProducer.id],
                });
            }
            else {
                this._webcamProducer.resume();
                (_b = this._vegaConnection) === null || _b === void 0 ? void 0 : _b.message("resumeProducers", {
                    producerIds: [this._webcamProducer.id],
                });
            }
            this._pauseResumeWebcam();
        }
    }
    _sendWebcam(track) {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_sendWebcam() [track:%o]", track);
            this._webcamTrack = track;
            if (this._webcamProducer) {
                return yield this._replaceWebcamTrack();
            }
            else if (this._webcamProducerPromise) {
                return;
            }
            if (this._sendTransport)
                return yield this._internalSendWebcam();
        });
    }
    _internalSendScreenVideo() {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_internalSendScreenVideo()");
            this._screenVideoProducerPromise = (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    if (!this._screenVideoTrack || !this._sendTransport || this._screenVideoProducer) {
                        this._screenVideoProducerPromise = null;
                        return;
                    }
                    const producer = yield this._sendTransport.produce(Object.assign(Object.assign({ track: this._screenVideoTrack, disableTrackOnPause: false, stopTracks: false }, getMediaSettings("video", true, this._features)), { appData: {
                            streamId: OUTBOUND_SCREEN_OUTBOUND_STREAM_ID,
                            sourceClientId: this._selfId,
                            screenShare: true,
                            source: "screenvideo",
                            paused: false,
                        } }));
                    this._screenVideoProducer = producer;
                    this._qualityMonitor.addProducer(this._selfId, producer.id);
                    producer.observer.once("close", () => {
                        var _a;
                        logger.info('screenVideoProducer "close" event');
                        if (producer.appData.localClosed)
                            (_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.message("closeProducers", { producerIds: [producer.id] });
                        this._screenVideoProducer = null;
                        this._screenVideoProducerPromise = null;
                        this._qualityMonitor.removeProducer(this._selfId, producer.id);
                    });
                    if (this._screenVideoTrack !== this._screenVideoProducer.track)
                        yield this._replaceScreenVideoTrack();
                }
                catch (error) {
                    logger.error("screenVideoProducer failed:%o", error);
                }
                finally {
                    this._screenVideoProducerPromise = null;
                    if (!this._screenVideoTrack) {
                        yield this._stopProducer(this._screenVideoProducer);
                        this._screenVideoProducer = null;
                    }
                }
            }))();
        });
    }
    _replaceScreenVideoTrack() {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_replaceScreenVideoTrack()");
            if (!this._screenVideoTrack || !this._screenVideoProducer || this._screenVideoProducer.closed)
                return;
            if (this._screenVideoProducer.track !== this._screenVideoTrack) {
                yield this._screenVideoProducer.replaceTrack({ track: this._screenVideoTrack });
                yield this._replaceScreenVideoTrack();
            }
        });
    }
    _sendScreenVideo(track) {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_sendScreenVideo() [track:%o]", track);
            this._screenVideoTrack = track;
            if (this._screenVideoProducer) {
                return yield this._replaceScreenVideoTrack();
            }
            else if (this._screenVideoProducerPromise) {
                return;
            }
            if (this._sendTransport)
                return yield this._internalSendScreenVideo();
        });
    }
    _internalSendScreenAudio() {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_internalSendScreenAudio()");
            this._screenAudioProducerPromise = (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    if (!this._screenAudioTrack || !this._sendTransport || this._screenAudioProducer) {
                        this._screenAudioProducerPromise = null;
                        return;
                    }
                    const producer = yield this._sendTransport.produce(Object.assign(Object.assign({ track: this._screenAudioTrack, disableTrackOnPause: false, stopTracks: false }, getMediaSettings("audio", true, this._features)), { appData: {
                            streamId: OUTBOUND_SCREEN_OUTBOUND_STREAM_ID,
                            sourceClientId: this._selfId,
                            screenShare: true,
                            source: "screenaudio",
                            paused: false,
                        } }));
                    this._screenAudioProducer = producer;
                    this._qualityMonitor.addProducer(this._selfId, producer.id);
                    producer.observer.once("close", () => {
                        var _a;
                        logger.info('screenAudioProducer "close" event');
                        if (producer.appData.localClosed)
                            (_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.message("closeProducers", { producerIds: [producer.id] });
                        this._screenAudioProducer = null;
                        this._screenAudioProducerPromise = null;
                        this._qualityMonitor.removeProducer(this._selfId, producer.id);
                    });
                    if (this._screenAudioTrack !== this._screenAudioProducer.track)
                        yield this._replaceScreenAudioTrack();
                }
                catch (error) {
                    logger.error("screenAudioProducer failed:%o", error);
                }
                finally {
                    this._screenAudioProducerPromise = null;
                    if (!this._screenAudioTrack) {
                        yield this._stopProducer(this._screenAudioProducer);
                        this._screenAudioProducer = null;
                    }
                }
            }))();
        });
    }
    _replaceScreenAudioTrack() {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_replaceScreenAudioTrack()");
            if (!this._screenAudioTrack || !this._screenAudioProducer || this._screenAudioProducer.closed)
                return;
            if (this._screenAudioProducer.track !== this._screenAudioTrack) {
                yield this._screenAudioProducer.replaceTrack({ track: this._screenAudioTrack });
                yield this._replaceScreenAudioTrack();
            }
        });
    }
    _sendScreenAudio(track) {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_sendScreenAudio() [track:%o]", track);
            this._screenAudioTrack = track;
            if (this._screenAudioProducer) {
                return yield this._replaceScreenAudioTrack();
            }
            else if (this._screenAudioProducerPromise) {
                return;
            }
            if (this._sendTransport)
                return yield this._internalSendScreenAudio();
        });
    }
    _stopProducer(producer) {
        logger.info("_stopProducer()");
        if (!producer || producer.closed)
            return;
        producer.appData.localClosed = true;
        producer.close();
    }
    isInitializedWith({ selfId, roomName, isSfu }) {
        return this._selfId === selfId && this._room.name === roomName && Boolean(isSfu);
    }
    setEventClaim(eventClaim) {
        var _a;
        this._eventClaim = eventClaim;
        (_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.message("eventClaim", { eventClaim });
    }
    setColocation(colocation) {
        var _a;
        this._colocation = colocation;
        (_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.message("setColocation", { colocation });
        if (!colocation && this._micScoreProducer && !this._micScoreProducerPromise) {
            this._stopMicScoreProducer();
        }
        this._syncMicAnalyser();
        rtcStats.sendEvent("colocation_changed", { colocation });
    }
    setAudioOnly(audioOnly) {
        var _a;
        (_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.message(audioOnly ? "enableAudioOnly" : "disableAudioOnly");
    }
    setRemoteScreenshareVideoTrackIds() { }
    setRoomSessionId(roomSessionId) {
        this._roomSessionId = roomSessionId;
    }
    disconnect(clientIdOrStreamId, _activeBreakout, eventClaim) {
        var _a;
        logger.info("disconnect() [clientIdOrStreamId:%s, eventClaim:%s]", clientIdOrStreamId, eventClaim);
        if (this._clientStates.has(clientIdOrStreamId)) {
            const clientState = this._clientStates.get(clientIdOrStreamId);
            clientState.hasAcceptedWebcamStream = false;
            clientState.hasAcceptedScreenStream = false;
            this._syncIncomingStreamsWithPWA(clientIdOrStreamId);
        }
        if (eventClaim) {
            this._eventClaim = eventClaim;
            (_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.message("eventClaim", { eventClaim });
        }
    }
    replaceTrack(oldTrack, track) {
        if (track.kind === "audio") {
            if (!track.effectTrack) {
                this._monitorAudioTrack(track);
            }
            this._micTrack = track;
            this._replaceMicTrack();
        }
        if (track.kind === "video") {
            if (!track.effectTrack) {
                this._monitorVideoTrack(track);
            }
            this._webcamTrack = track;
            this._replaceWebcamTrack();
        }
    }
    removeStream(streamId, _stream, requestedByClientId) {
        logger.info("removeStream() [streamId:%s, requestedByClientId:%s]", streamId, requestedByClientId);
        this._emitToSignal(PROTOCOL_REQUESTS.STOP_SCREENSHARE, {
            streamId: OUTBOUND_SCREEN_OUTBOUND_STREAM_ID,
            requestedByClientId,
        });
        this._stopProducer(this._screenVideoProducer);
        this._screenVideoProducer = null;
        this._screenVideoTrack = null;
        this._stopProducer(this._screenAudioProducer);
        this._screenAudioProducer = null;
        this._screenAudioTrack = null;
    }
    _onMicAnalyserScoreUpdated(data) {
        var _a, _b;
        (_b = (_a = this._micAnalyserDebugger) === null || _a === void 0 ? void 0 : _a.onScoreUpdated) === null || _b === void 0 ? void 0 : _b.call(_a, data);
        this._sendMicScore(this._micPaused ? 0 : data.out);
    }
    addNewStream(streamId, stream, audioPaused, videoPaused, beforeEffectTracks = []) {
        if (streamId === "0") {
            this._micPaused = audioPaused;
            this._webcamPaused = videoPaused;
            const videoTrack = stream.getVideoTracks()[0];
            const audioTrack = stream.getAudioTracks()[0];
            if (videoTrack) {
                this._sendWebcam(videoTrack);
                if (!videoTrack.effectTrack) {
                    this._monitorVideoTrack(videoTrack);
                }
                const beforeEffectTrack = beforeEffectTracks.find((t) => t.kind === "video");
                if (beforeEffectTrack) {
                    this._monitorVideoTrack(beforeEffectTrack);
                }
            }
            if (audioTrack) {
                this._sendMic(audioTrack);
                this._syncMicAnalyser();
                if (!audioTrack.effectTrack) {
                    this._monitorAudioTrack(audioTrack);
                }
                const beforeEffectTrack = beforeEffectTracks.find((t) => t.kind === "audio");
                if (beforeEffectTrack) {
                    this._monitorAudioTrack(beforeEffectTrack);
                }
            }
            if (this._localStreamDeregisterFunction) {
                this._localStreamDeregisterFunction();
                this._localStreamDeregisterFunction = null;
            }
            const localStreamHandler = (e) => {
                const { enable, track } = e.detail;
                this._webcamPaused = !enable;
                this._pauseResumeWebcam();
                this._handleStopOrResumeVideo({ enable, track });
            };
            stream.addEventListener("stopresumevideo", localStreamHandler);
            this._localStreamDeregisterFunction = () => {
                stream.removeEventListener("stopresumevideo", localStreamHandler);
            };
        }
        else {
            const videoTrack = stream.getVideoTracks()[0];
            const audioTrack = stream.getAudioTracks()[0];
            if (videoTrack)
                this._sendScreenVideo(videoTrack);
            if (audioTrack)
                this._sendScreenAudio(audioTrack);
            this._emitScreenshareStarted();
        }
    }
    _syncMicAnalyser() {
        if (this._micTrack && this._colocation && !this._micAnalyser) {
            this._micAnalyser = createMicAnalyser({
                micTrack: this._micTrack,
                onScoreUpdated: this._onMicAnalyserScoreUpdated.bind(this),
            });
            return;
        }
        if (this._micAnalyser && !this._colocation) {
            this._micAnalyser.close();
            this._micAnalyser = null;
            return;
        }
    }
    stopOrResumeAudio(stream, enabled) {
        logger.info("stopOrResumeAudio() [enabled:%s]", enabled);
        this._micPaused = !enabled;
        this._pauseResumeMic();
    }
    _handleStopOrResumeVideo({ enable, track }) {
        if (!enable) {
            if (this._webcamProducer && !this._webcamProducer.closed && this._webcamProducer.track === track) {
                this._stopProducer(this._webcamProducer);
                this._webcamProducer = null;
                this._webcamTrack = null;
            }
        }
        else {
            this._sendWebcam(track);
        }
    }
    stopOrResumeVideo(localStream, enable) {
        var _a;
        logger.info("stopOrResumeVideo() [enable:%s]", enable);
        this._webcamPaused = !enable;
        this._pauseResumeWebcam();
        if (!["chrome", "safari"].includes(browserName)) {
            return;
        }
        if (!enable) {
            clearTimeout(this._stopCameraTimeout);
            const stopCameraDelay = ((_a = localStream.getVideoTracks().find((t) => !t.enabled)) === null || _a === void 0 ? void 0 : _a.readyState) === "ended"
                ? 0
                : 5000;
            this._stopCameraTimeout = setTimeout(() => {
                localStream.getVideoTracks().forEach((track) => {
                    if (track.enabled === false) {
                        track.stop();
                        localStream.removeTrack(track);
                        this._emitToPWA(EVENTS.LOCAL_STREAM_TRACK_REMOVED, {
                            stream: localStream,
                            track,
                        });
                        this._handleStopOrResumeVideo({ enable, track });
                    }
                });
            }, stopCameraDelay);
        }
        else if (localStream.getVideoTracks().length === 0) {
            const constraints = this._webrtcProvider.getMediaConstraints().video;
            navigator.mediaDevices.getUserMedia({ video: constraints }).then((stream) => {
                const track = stream.getVideoTracks()[0];
                localStream.addTrack(track);
                this._monitorVideoTrack(track);
                this._emitToPWA(EVENTS.LOCAL_STREAM_TRACK_ADDED, {
                    streamId: localStream.id,
                    tracks: [track],
                    screenShare: false,
                });
                this._handleStopOrResumeVideo({ enable, track });
            });
        }
    }
    supportsScreenShareAudio() {
        return true;
    }
    acceptNewStream({ streamId, clientId }) {
        logger.info("acceptNewStream()", { streamId, clientId });
        const clientState = this._getOrCreateClientState(clientId);
        const isScreenShare = streamId !== clientId;
        if (isScreenShare) {
            clientState.hasAcceptedScreenStream = true;
            clientState.hasEmittedScreenStream = false;
        }
        else {
            clientState.hasAcceptedWebcamStream = true;
            clientState.hasEmittedWebcamStream = false;
        }
        this._syncIncomingStreamsWithPWA(clientId);
    }
    updateStreamResolution(streamId, _ignored, { width, height, }) {
        var _a, _b;
        logger.info("updateStreamResolution()", { streamId, width, height });
        const consumerId = this._streamIdToVideoConsumerId.get(streamId);
        const consumer = this._consumers.get(consumerId);
        if (!consumer) {
            this._streamIdToVideoResolution.set(streamId, { width, height });
            return;
        }
        const numberOfActiveVideos = getNumberOfActiveVideos(this._consumers);
        const numberOfTemporalLayers = getNumberOfTemporalLayers(consumer);
        const { spatialLayer, temporalLayer } = getLayers({ width, height }, {
            numberOfActiveVideos,
            numberOfTemporalLayers,
            uncappedSingleRemoteVideoOn: (_a = this._features) === null || _a === void 0 ? void 0 : _a.uncappedSingleRemoteVideoOn,
        });
        if (consumer.appData.spatialLayer !== spatialLayer || consumer.appData.temporalLayer !== temporalLayer) {
            consumer.appData.spatialLayer = spatialLayer;
            consumer.appData.temporalLayer = temporalLayer;
            (_b = this._vegaConnection) === null || _b === void 0 ? void 0 : _b.message("setConsumersPreferredLayers", {
                consumerIds: [consumerId],
                spatialLayer,
                temporalLayer,
            });
        }
    }
    disconnectAll() {
        var _a, _b, _c, _d;
        this._reconnect = false;
        if (this._reconnectTimeOut) {
            clearTimeout(this._reconnectTimeOut);
            this._reconnectTimeOut = null;
        }
        this._socketListenerDeregisterFunctions.forEach((func) => {
            func();
        });
        if (this._localStreamDeregisterFunction) {
            this._localStreamDeregisterFunction();
            this._localStreamDeregisterFunction = null;
        }
        this._socketListenerDeregisterFunctions = [];
        (_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.removeAllListeners();
        (_b = this._vegaConnection) === null || _b === void 0 ? void 0 : _b.close();
        (_c = this._sendTransport) === null || _c === void 0 ? void 0 : _c.close();
        (_d = this._receiveTransport) === null || _d === void 0 ? void 0 : _d.close();
        this._micTrack = null;
        this._webcamTrack = null;
        this._screenVideoTrack = null;
        this._screenAudioTrack = null;
        this._streamIdToVideoConsumerId.clear();
        this._mediasoupDevice = null;
    }
    sendAudioMutedStats(muted) {
        rtcStats.sendEvent("audio_muted", { muted });
    }
    sendVideoMutedStats(muted) {
        rtcStats.sendEvent("video_muted", { muted });
    }
    sendStatsCustomEvent(eventName, data) {
        rtcStats.sendEvent(eventName, data);
    }
    rtcStatsDisconnect() {
        rtcStats.server.close();
    }
    rtcStatsReconnect() {
        if (!rtcStats.server.connected) {
            rtcStats.server.connect();
        }
    }
    _monitorAudioTrack(track) {
        var _a, _b;
        if (((_a = this._audioTrackBeingMonitored) === null || _a === void 0 ? void 0 : _a.id) === track.id)
            return;
        (_b = this._audioTrackBeingMonitored) === null || _b === void 0 ? void 0 : _b.removeEventListener("ended", this._audioTrackOnEnded);
        track.addEventListener("ended", this._audioTrackOnEnded);
        this._audioTrackBeingMonitored = track;
    }
    _monitorVideoTrack(track) {
        var _a, _b;
        if (((_a = this._videoTrackBeingMonitored) === null || _a === void 0 ? void 0 : _a.id) === track.id)
            return;
        (_b = this._videoTrackBeingMonitored) === null || _b === void 0 ? void 0 : _b.removeEventListener("ended", this._videoTrackOnEnded);
        track.addEventListener("ended", this._videoTrackOnEnded);
        this._videoTrackBeingMonitored = track;
    }
    _onMessage(message) {
        return __awaiter(this, void 0, void 0, function* () {
            const { method, data } = message;
            return Promise.resolve()
                .then(() => {
                switch (method) {
                    case "consumerReady":
                        return this._onConsumerReady(data);
                    case "consumerClosed":
                        return this._onConsumerClosed(data);
                    case "consumerPaused":
                        return this._onConsumerPaused(data);
                    case "consumerResumed":
                        return this._onConsumerResumed(data);
                    case "dataConsumerReady":
                        return this._onDataConsumerReady(data);
                    case "dataConsumerClosed":
                        return this._onDataConsumerClosed(data);
                    case "dominantSpeaker":
                        return this._onDominantSpeaker(data);
                    case "consumerScore":
                        return this._onConsumerScore(data);
                    case "producerScore":
                        return this._onProducerScore(data);
                    default:
                        logger.info(`unknown message method "${method}"`);
                        return;
                }
            })
                .catch((error) => {
                logger.error('"message" failed [error:%o]', error);
            });
        });
    }
    _onConsumerReady(options) {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_onConsumerReady()", { id: options.id, producerId: options.producerId });
            const consumer = yield this._receiveTransport.consume(options);
            consumer.pause();
            consumer.appData.localPaused = true;
            consumer.appData.spatialLayer = 2;
            this._consumers.set(consumer.id, consumer);
            this._qualityMonitor.addConsumer(consumer.appData.sourceClientId, consumer.id);
            consumer.observer.once("close", () => {
                this._consumers.delete(consumer.id);
                this._qualityMonitor.removeConsumer(consumer.appData.sourceClientId, consumer.id);
                this._consumerClosedCleanup(consumer);
            });
            if (this._features.increaseIncomingMediaBufferOn && consumer.rtpReceiver) {
                try {
                    consumer.rtpReceiver.jitterBufferTarget = MEDIA_JITTER_BUFFER_TARGET;
                    consumer.rtpReceiver.playoutDelayHint = MEDIA_JITTER_BUFFER_TARGET / 1000;
                }
                catch (error) {
                    logger.error("Error during setting jitter buffer target:", error);
                }
            }
            const { sourceClientId: clientId, screenShare, streamId } = consumer.appData;
            const clientState = this._getOrCreateClientState(clientId);
            if (screenShare) {
                clientState.hasEmittedScreenStream = false;
                clientState.screenShareStreamId = streamId;
            }
            else {
                clientState.hasEmittedWebcamStream = false;
                clientState.camStreamId = streamId;
            }
            let stream = screenShare ? clientState.screenStream : clientState.webcamStream;
            if (!stream || stream.inboundId !== streamId) {
                stream = new MediaStream();
                stream.inboundId = streamId;
                screenShare ? (clientState.screenStream = stream) : (clientState.webcamStream = stream);
            }
            if (consumer.kind === "video") {
                this._streamIdToVideoConsumerId.set(stream.id, consumer.id);
            }
            stream.addTrack(consumer.track);
            this._syncIncomingStreamsWithPWA(clientId);
            const resolution = this._streamIdToVideoResolution.get(stream.id);
            if (resolution) {
                this.updateStreamResolution(stream.id, null, resolution);
                this._streamIdToVideoResolution.delete(stream.id);
            }
        });
    }
    _onConsumerClosed({ consumerId, reason }) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_onConsumerClosed()", { consumerId, reason });
            (_a = this._consumers.get(consumerId)) === null || _a === void 0 ? void 0 : _a.close();
        });
    }
    _onConsumerPaused({ consumerId }) {
        logger.info("_onConsumerPaused()", { consumerId });
        const consumer = this._consumers.get(consumerId);
        if (!consumer)
            return;
        consumer.appData.remotePaused = true;
        consumer.pause();
    }
    _onConsumerResumed({ consumerId }) {
        logger.info("_onConsumerResumed()", { consumerId });
        const consumer = this._consumers.get(consumerId);
        if (!consumer)
            return;
        consumer.appData.remotePaused = false;
        if (!consumer.appData.localPaused) {
            consumer.resume();
        }
    }
    _onConsumerScore({ consumerId, kind, score }) {
        logger.info("_onConsumerScore()", { consumerId, kind, score });
        const { appData: { sourceClientId }, } = this._consumers.get(consumerId) || { appData: {} };
        if (sourceClientId) {
            this._qualityMonitor.addConsumerScore(sourceClientId, consumerId, kind, score);
        }
    }
    _onProducerScore({ producerId, kind, score }) {
        logger.info("_onProducerScore()", { producerId, kind, score });
        [this._micProducer, this._webcamProducer, this._screenVideoProducer, this._screenAudioProducer].forEach((producer) => {
            if ((producer === null || producer === void 0 ? void 0 : producer.id) === producerId) {
                this._qualityMonitor.addProducerScore(this._selfId, producerId, kind, score);
            }
        });
    }
    _onDataConsumerReady(options) {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_onDataConsumerReady()", { id: options.id, producerId: options.producerId });
            const consumer = yield this._receiveTransport.consumeData(options);
            this._dataConsumers.set(consumer.id, consumer);
            consumer.once("close", () => {
                this._dataConsumers.delete(consumer.id);
            });
            const { clientId } = consumer.appData;
            consumer.on("message", (message) => {
                var _a;
                if (!this._micAnalyserDebugger)
                    return;
                if (typeof message !== "string")
                    return;
                const { score } = JSON.parse(message);
                if (score) {
                    (_a = this._micAnalyserDebugger) === null || _a === void 0 ? void 0 : _a.onConsumerScore(consumer.appData.clientId, score);
                }
            });
            this._syncIncomingStreamsWithPWA(clientId);
        });
    }
    _onDataConsumerClosed({ dataConsumerId, reason }) {
        return __awaiter(this, void 0, void 0, function* () {
            logger.info("_onDataConsumerClosed()", { dataConsumerId, reason });
            const consumer = this._dataConsumers.get(dataConsumerId);
            consumer === null || consumer === void 0 ? void 0 : consumer.close();
        });
    }
    _onDominantSpeaker({ consumerId }) {
        const consumer = this._consumers.get(consumerId);
        if (!consumer)
            return;
        const { sourceClientId: clientId } = consumer.appData;
        this._emitToPWA(rtcManagerEvents.DOMINANT_SPEAKER, { clientId });
    }
    _consumerClosedCleanup(consumer) {
        const { sourceClientId: clientId, screenShare } = consumer.appData;
        const clientState = this._getOrCreateClientState(clientId);
        const stream = screenShare ? clientState.screenStream : clientState.webcamStream;
        if (!stream)
            return;
        stream.removeTrack(consumer.track);
        if (stream.getTracks().length === 0) {
            this._streamIdToVideoConsumerId.delete(stream.id);
            this._streamIdToVideoResolution.delete(stream.id);
            if (screenShare) {
                clientState.screenStream = null;
                clientState.hasEmittedScreenStream = false;
                clientState.screenShareStreamId = null;
            }
            else {
                clientState.hasEmittedWebcamStream = false;
                clientState.camStreamId = null;
            }
        }
    }
    _syncIncomingStreamsWithPWA(clientId) {
        var _a, _b;
        const clientState = this._getOrCreateClientState(clientId);
        const { webcamStream, screenStream, hasEmittedWebcamStream, hasEmittedScreenStream, hasAcceptedWebcamStream, hasAcceptedScreenStream, screenShareStreamId, camStreamId, } = clientState;
        const toPauseConsumers = [];
        const toResumeConsumers = [];
        this._consumers.forEach((consumer) => {
            if (consumer.appData.sourceClientId !== clientId)
                return;
            const hasAccepted = consumer.appData.screenShare ? hasAcceptedScreenStream : hasAcceptedWebcamStream;
            if (!consumer.appData.localPaused !== hasAccepted) {
                if (hasAccepted) {
                    if (!consumer.appData.remotePaused) {
                        consumer.resume();
                    }
                    consumer.appData.localPaused = false;
                    toResumeConsumers.push(consumer.id);
                }
                else {
                    consumer.pause();
                    consumer.appData.localPaused = true;
                    toPauseConsumers.push(consumer.id);
                }
            }
        });
        if (toPauseConsumers.length > 0) {
            (_a = this._vegaConnection) === null || _a === void 0 ? void 0 : _a.message("pauseConsumers", {
                consumerIds: toPauseConsumers,
            });
        }
        if (toResumeConsumers.length > 0) {
            (_b = this._vegaConnection) === null || _b === void 0 ? void 0 : _b.message("resumeConsumers", {
                consumerIds: toResumeConsumers,
            });
        }
        if (webcamStream && !hasEmittedWebcamStream && hasAcceptedWebcamStream) {
            this._emitToPWA(EVENTS.STREAM_ADDED, {
                clientId,
                stream: webcamStream,
                streamId: camStreamId,
                streamType: "webcam",
            });
            clientState.hasEmittedWebcamStream = true;
        }
        if (screenStream && !hasEmittedScreenStream && hasAcceptedScreenStream) {
            this._emitToPWA(EVENTS.STREAM_ADDED, {
                clientId,
                stream: screenStream,
                streamId: screenShareStreamId,
                streamType: "screenshare",
            });
            clientState.hasEmittedScreenStream = true;
        }
    }
    _getOrCreateClientState(clientId) {
        let clientState = this._clientStates.get(clientId);
        if (!clientState) {
            clientState = {
                hasAcceptedWebcamStream: false,
                hasAcceptedScreenStream: false,
                hasEmittedWebcamStream: false,
                hasEmittedScreenStream: false,
                webcamStream: null,
                screenStream: null,
                screenShareStreamId: null,
                camStreamId: null,
            };
            this._clientStates.set(clientId, clientState);
        }
        return clientState;
    }
    _emitToPWA(eventName, data) {
        this._emitter.emit(eventName, data);
    }
    _emitToSignal(eventName, data, callback) {
        this._serverSocket.emit(eventName, data, callback);
    }
    shouldAcceptStreamsFromBothSides() {
        return true;
    }
    setMicAnalyserDebugger(analyserDebugger) {
        this._micAnalyserDebugger = analyserDebugger;
    }
    setMicAnalyserParams(params) {
        var _a;
        (_a = this._micAnalyser) === null || _a === void 0 ? void 0 : _a.setParams(params);
    }
    hasClient(clientId) {
        return this._clientStates.has(clientId);
    }
}

class RtcManagerDispatcher {
    constructor({ emitter, serverSocket, webrtcProvider, features, }) {
        this.emitter = emitter;
        this.currentManager = null;
        serverSocket.on(PROTOCOL_RESPONSES.ROOM_JOINED, ({ room, selfId, error, eventClaim }) => {
            if (error)
                return;
            const config = {
                selfId,
                room,
                emitter,
                serverSocket,
                webrtcProvider,
                features,
                eventClaim,
                deviceHandlerFactory: features === null || features === void 0 ? void 0 : features.deviceHandlerFactory,
            };
            const isSfu = !!room.sfuServer;
            if (this.currentManager) {
                if (this.currentManager.isInitializedWith({ selfId, roomName: room.name, isSfu })) {
                    if (this.currentManager.setEventClaim && eventClaim) {
                        this.currentManager.setEventClaim(eventClaim);
                    }
                    return;
                }
                this.currentManager.disconnectAll();
                emitter.emit(EVENTS.RTC_MANAGER_DESTROYED);
            }
            let rtcManager = null;
            if (isSfu) {
                rtcManager = new VegaRtcManager(config);
            }
            else {
                rtcManager = new P2pRtcManager(config);
            }
            rtcManager.rtcStatsReconnect();
            rtcManager.setupSocketListeners();
            emitter.emit(EVENTS.RTC_MANAGER_CREATED, { rtcManager });
            this.currentManager = rtcManager;
            serverSocket.setRtcManager(rtcManager);
        });
    }
    stopRtcManager() {
        if (this.currentManager) {
            this.currentManager.disconnectAll();
            this.emitter.emit(EVENTS.RTC_MANAGER_DESTROYED);
        }
    }
}

var RtcEventNames;
(function (RtcEventNames) {
    RtcEventNames["rtc_manager_created"] = "rtc_manager_created";
    RtcEventNames["stream_added"] = "stream_added";
})(RtcEventNames || (RtcEventNames = {}));

export { BandwidthTester, EVENTS, KNOCK_MESSAGES, KalmanFilter, Logger, MAXIMUM_TURN_BANDWIDTH, MAXIMUM_TURN_BANDWIDTH_UNLIMITED, MEDIA_JITTER_BUFFER_TARGET, NoDevicesError, P2pRtcManager, PROTOCOL_ERRORS, PROTOCOL_EVENTS, PROTOCOL_REQUESTS, PROTOCOL_RESPONSES, RELAY_MESSAGES, ReconnectManager, RtcEventNames, RtcManagerDispatcher, RtcStream, STREAM_TYPES, ServerSocket, Session, SfuV2Parser, TYPES, VegaConnection, VegaMediaQualityMonitor, VegaRtcManager, addAbsCaptureTimeExtMap, addExtMap, assert, buildDeviceList, calculateStd, captureAudioSsrcMetrics, captureCandidatePairInfoMetrics, captureCommonSsrcMetrics, captureSsrcInfo, captureVideoSsrcMetrics, changeMediaDirection, cleanSdp, compareLocalDevices, createACFCalculator, createMicAnalyser, createWorker, deprioritizeH264, detectMicrophoneNotWorking, enumerate, filterMidExtension, filterMsidSemantic, fromLocation, generateByteString, getConstraints, getCurrentPeerConnections, getDeviceData, getDisplayMedia, getHandler, getIssuesAndMetrics, getMediaConstraints, getMediaSettings, getOptimalBitrate, getPeerConnectionIndex, getStats, getStream, getStream2, getUpdatedDevices, getUpdatedStats, getUserMedia, hasGetDisplayMedia, ipRegex, isMobile, isRelayed, maybeRejectNoH264, maybeTurnOnly, modifyMediaCapabilities, removePeerConnection, replaceSSRCs, replaceTracksInStream, rtcManagerEvents, rtcStats, setClientProvider, setCodecPreferenceSDP, setPeerConnectionsForTests, setVideoBandwidthUsingSetParameters, standardDeviation, startPerformanceMonitor, stopStreamTracks, subscribeIssues, subscribeStats, variance };
